import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import Node, { getIdeaDomId } from './Node';
import Line from './Line';
import { ColorPickerWidget } from './ColorPickerWidget';
import { COLORS } from '../../config/colors';
import {
  DEFAULT_THEME_ID,
  getCanonicalPaletteColor,
  getMapTheme,
  getThemeCanvasBackground,
  getThemeCanvasForeground,
  getThemeCanvasSolidColor,
} from '../../config/themes';
import {
  DEFAULT_CONNECTION_STYLE_ID,
  normalizeConnectionStyleId,
} from '../../config/connectionStyles';
import EmojiPickerWidget from './EmojiPickerWidget';
import ReparentDropIndicator from './ReparentDropIndicator';
import { useNodeManagement } from '../../hooks/mindmap/useNodeManagement';
import { useMapNavigation } from '../../hooks/mindmap/useMapNavigation';
import { useKeyboardShortcuts } from '../../hooks/mindmap/useKeyboardShortcuts';
import { useUndoRedo } from '../../hooks/mindmap/useUndoRedo';
import { useCollisionDetection } from '../../hooks/mindmap/useCollisionDetection';
import { usePersistence } from '../../hooks/mindmap/usePersistence';
import { useVisibility } from '../../hooks/mindmap/useVisibility';
import { useYjsMap } from '../../hooks/mindmap/useYjsMap';
import { MindMapState } from '../../models/MindMapState';
import { EXPORT_FORMATS, FileExporter, FileImporter } from '../../utils/mindmap/serialization';
import Menu from './Menu';
import FocusNavigation from './FocusNavigation';
import ShareDialog from './ShareDialog';
import NodeDiscussionPanel from './NodeDiscussionPanel';
import NodeInfoPanel from './NodeInfoPanel';
import AttachmentViewerModal from './AttachmentViewerModal';
import AttachmentUploadStatus from './AttachmentUploadStatus';
import BranchMarkdownDialog from './BranchMarkdownDialog';
import OfflineRecoveryDialog from './OfflineRecoveryDialog';
import IdeaInbox, { INBOX_DRAG_TYPE } from './IdeaInbox';
import UpgradeDialog from '../ui/UpgradeDialog';
import {
  getHealth,
  fetchUrlPreview,
  createMindMap,
  updateMindMapDescription,
  updateMindMapTitle,
  getAuthToken,
  uploadMindMapThumbnail,
  uploadNodeAsset,
  deleteNodeAsset,
  fetchNodeAssetContent,
  updateMarkdownNodeAssetContent,
  getMindMapSharing,
} from '../../utils/mindmap/apiClient';
import NodeContextMenu from './NodeContextMenu';
import NodeActionCapsule from './NodeActionCapsule';
import { useNodeDiscussions } from '../../hooks/mindmap/useNodeDiscussions';
import { useNodeBranchModeration } from '../../hooks/mindmap/useNodeBranchModeration';
import { parseMindmappiBranchMarkdown } from '../../utils/mindmap/mindmappiBranchImport';
import {
  isNamedDiscussionCollaborator,
} from '../../utils/mindmap/nodeDiscussionEvents';
import {
  clampViewportOverlay,
  clientToViewport,
  createWorldViewportConstraint,
  minimumPanDeltaToReveal,
  screenToWorld,
  worldToViewport,
} from '../../utils/mindmap/viewport';
import {
  readNodeLayoutSize,
  resolveRouteLayoutSize,
} from '../../utils/mindmap/nodeMeasurements';
import {
  createDragPositionPreview,
  dragPreviewMatchesNodes,
  getRenderedNodePosition,
} from '../../utils/mindmap/dragPreview';
import {
  createLinkedMapPortal,
  getLinkedMapCreationFailure,
} from '../../utils/mindmap/linkedMapWorkflow';
import {
  BLANK_MARKDOWN_CONTENT,
  createStoredMarkdownIdea,
  DEFAULT_MARKDOWN_FILENAME,
} from '../../utils/mindmap/markdownAttachmentWorkflow';
import {
  planBranchMarkdown,
  replaceBranchWithStoredMarkdown,
} from '../../utils/mindmap/branchMarkdown';
import {
  buildLinkedMapTitlePatch,
  publishMapTitleUpdate,
  subscribeToMapTitleUpdates,
} from '../../utils/mindmap/mapTitleSync';
import {
  deriveVisibleNodeIds,
  getAncestorNodeIds,
  getBranchNodeIds,
  getNodePath,
} from '../../utils/mindmap/branchVisibility';
import {
  createFocusHistory,
  currentFocusId,
  pushFocusHistory,
  pruneFocusHistory,
  stepFocusHistory,
} from '../../utils/mindmap/focusHistory';
import {
  buildMoveSubtreeCommand,
  buildCreateChildCommand,
  buildReparentSubtreeCommand,
  buildCreateSiblingCommand,
  buildCreateTaskChildCommand,
  buildCreateLinkedMapCommand,
  buildCreateAttachmentCommand,
  buildDeleteSubtreeCommand,
  buildFinalizeCreatedNodeCommand,
  DEFAULT_NEW_NODE_SIZE,
  topLeftRectsOverlap,
} from '../../utils/mindmap/mapCommands';
import {
  attachmentDropParentId,
  attachmentFilesFromTransfer,
} from '../../utils/mindmap/attachmentDrop';
import { getNodeSearchLabel, searchNodes } from '../../utils/mindmap/nodeSearch';
import { buildMindMapRenderIndex } from '../../utils/mindmap/renderIndex';
import {
  getRootInfoProjection,
  projectionMatches,
  ROOT_INFO_PROJECTION_DEBOUNCE_MS,
  ROOT_INFO_PROJECTION_RETRY_DELAYS_MS,
} from '../../utils/mindmap/rootInfoProjection';
import {
  findAffectedEdgeKeys,
  findGeometryAffectedEdgeKeys,
  routeEdges,
} from '../../utils/mindmap/edgeRouting';
import { createReleaseSettlementController } from '../../utils/mindmap/releaseSettlementWorkerClient';
import { createReleaseSettlementWorker } from '../../utils/mindmap/createReleaseSettlementWorker';
import { createEdgeRoutingWorker } from '../../utils/mindmap/createEdgeRoutingWorker';
import {
  deriveViewportMountedNodeIds,
  deriveViewportMountedRelationships,
  VIEWPORT_CULLING_THRESHOLD,
} from '../../utils/mindmap/viewportCulling';
import { shouldUseSemanticZoom } from '../../utils/mindmap/semanticZoom';
import { createDirectionalNavigationIndex } from '../../utils/mindmap/spatialNavigation';
import { copyToClipboard } from '../../utils/common/helpers';
import {
  deriveNodeBranchModerationProjection,
  filterModeratedGraph,
} from '../../utils/mindmap/nodeBranchModeration';
import {
  canCompleteAssignedTask,
  normalizeTaskAssignees,
} from '../../utils/mindmap/nodeResponsibility';
import {
  buildAttachInboxBranchCommand,
  buildCaptureChildIdeaCommand,
  buildCreateInboxIdeaCommand,
  buildDeleteInboxBranchCommand,
  buildMoveBranchToInboxCommand,
  deriveIdeaInbox,
} from '../../utils/mindmap/ideaInbox';

// Minimal HTML entities decoder for titles coming from URL metadata.
// Covers the most common entities we see in practice.
const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

const buildMindMapShareUrl = (mindmapId) => {
  if (typeof window === 'undefined') return `?map=${encodeURIComponent(mindmapId)}`;
  const envBase = typeof process !== 'undefined' && process.env.REACT_APP_FRONTEND_BASE_URL
    ? process.env.REACT_APP_FRONTEND_BASE_URL
    : null;
  const baseUrl = (envBase || `${window.location.origin}${window.location.pathname}`).replace(/\/$/, '');
  return `${baseUrl}?map=${encodeURIComponent(mindmapId)}`;
};

const DEFAULT_CANVAS_SPAN = 2000;
const NODE_WIDTH_GUESS = 260;
const NODE_HEIGHT_GUESS = 110;
const CANVAS_PADDING = 600;
const COLLISION_SLIDE_MS = 180;
const IMAGE_ATTACHMENT_LAYOUT_SIZE = Object.freeze({ width: 150, height: 110 });
const NODE_DOUBLE_CLICK_GUARD_MS = 500;
const NODE_DOUBLE_CLICK_GUARD_DISTANCE = 24;
const MAP_SEARCH_INPUT_SELECTOR = '[data-map-search-input="true"]';
const VIEWPORT_OCCLUSION_SELECTOR = '[data-viewport-occlusion="true"]';
const CREATION_VIEWPORT_INSET = 12;
const CREATION_REVEAL_MS = 220;
const DIRECTIONAL_NAVIGATION_CORRECTION_MS = 360;
const CREATION_FINAL_RECHECK_MS = 320;
const NODE_ACTION_CAPSULE_EXIT_MS = 280;
const LARGE_MAP_ADAPTIVE_EDGE_ROUTING_MIN = 25;
const EMPTY_NODE_LIST = Object.freeze([]);

const NodesContainer = ({
  onBaseNodeChange,
  currentUserId,
  currentUserName = null,
  remoteMapData,
  onRegisterSaveHandler,
  onRegisterMapFileActions,
  onRegisterExitFocusedBranch,
  onMapInitialized,
  onDistractionFreeChange,
}) => {
  const containerRef = useRef(null);
  const captureRef = useRef(null);
  const nodeRefs = useRef({});
  const [canvasViewportSize, setCanvasViewportSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  }));
  const measuredNodeSizesRef = useRef(new Map());
  const liveEditingNodeSizesRef = useRef(new Map());
  const previousEdgeRoutesRef = useRef(new Map());
  const [showPicker, setShowPicker] = useState(false);
  const [colorPosOverride, setColorPosOverride] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiPos, setEmojiPos] = useState({ x: 0, y: 0 });
  const [isDistractionFree, setIsDistractionFree] = useState(false);
  const [areFullscreenMenusVisible, setAreFullscreenMenusVisible] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const stored = window.localStorage.getItem('mindMapTheme');
      if (stored === 'light') return false;
      if (stored === 'dark') return true;
      return true;
    } catch (e) {
      console.warn('Failed to read stored theme, defaulting to dark:', e);
      return true;
    }
  });
  // Map theme for local (signed-out) maps; remote maps read the theme from
  // the shared Yjs meta instead so every collaborator sees the same look.
  const [localThemeId, setLocalThemeId] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_THEME_ID;
    try {
      return window.localStorage.getItem('mindMapMapTheme') || DEFAULT_THEME_ID;
    } catch {
      return DEFAULT_THEME_ID;
    }
  });
  const [localConnectionStyleId, setLocalConnectionStyleId] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_CONNECTION_STYLE_ID;
    try {
      return normalizeConnectionStyleId(
        window.localStorage.getItem('mindMapConnectionStyle')
      );
    } catch {
      return DEFAULT_CONNECTION_STYLE_ID;
    }
  });
  const clickTimeoutRef = useRef(null);
  useEffect(() => () => {
    if (clickTimeoutRef.current !== null) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
  }, []);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [dragPositionPreview, setDragPositionPreview] = useState(() => new Map());
  const [layoutTransitionNodeIds, setLayoutTransitionNodeIds] = useState(() => new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const initializedAtRef = useRef(null);
  const [renderedCapsuleNodeId, setRenderedCapsuleNodeId] = useState(null);
  const [isRenderedCapsuleVisible, setIsRenderedCapsuleVisible] = useState(false);
  const renderedCapsuleNodeIdRef = useRef(null);
  const capsuleExitTimerRef = useRef(null);
  const capsuleEnterTimerRef = useRef(null);
  const didCallMapInitialized = useRef(false);
    // Call onMapInitialized after first full render when map is ready
    useEffect(() => {
      if (isInitialized && onMapInitialized && !didCallMapInitialized.current) {
        didCallMapInitialized.current = true;
        onMapInitialized();
      }
    }, [isInitialized, onMapInitialized]);
  const lastSavedStateRef = useRef(null);
  const lastPersistedStateRef = useRef(null);
  const lastSelectedIdRef = useRef(null);
  const urlNodeHandledRef = useRef(false);
  const [focusRootId, setFocusRootId] = useState(null);
  const [collapsedNodeIds, setCollapsedNodeIds] = useState(() => new Set());
  const [temporarilyExpandedNodeIds, setTemporarilyExpandedNodeIds] = useState(() => new Set());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActiveIndex, setSearchActiveIndex] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches
  ));
  const [mobileWorkspaceView, setMobileWorkspaceView] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches
      ? 'ideas'
      : 'map'
  ));
  const [ideaInboxMode, setIdeaInboxMode] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia?.('(max-width: 720px)').matches
      ? 'expanded'
      : 'collapsed'
  ));
  const [captureTargetId, setCaptureTargetId] = useState(null);
  const [isInboxDropActive, setIsInboxDropActive] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareNodeId, setShareNodeId] = useState(null);
  const [activeInfoNodeId, setActiveInfoNodeId] = useState(null);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [attachmentViewer, setAttachmentViewer] = useState(null);
  const [branchMarkdownRequest, setBranchMarkdownRequest] = useState(null);
  const [pendingAttachmentUploads, setPendingAttachmentUploads] = useState([]);
  const attachmentViewerRef = useRef(null);
  const branchMarkdownConversionRef = useRef(false);
  const [mapCreationUpgradeRequest, setMapCreationUpgradeRequest] = useState(null);
  const [focusHistory, setFocusHistory] = useState(() => createFocusHistory());
  const hasLoadedRef = useRef(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, nodeId: null, x: 0, y: 0 });
  const contextMenuOpenedAtRef = useRef(0);
  const lastSaveRequestRef = useRef(0);
  const hasUnsavedChangesRef = useRef(false);
  const centeredInitialViewportKeyRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [taskAssignees, setTaskAssignees] = useState([]);
  const [taskAssigneesMapId, setTaskAssigneesMapId] = useState(null);
  const [taskAssigneesLoading, setTaskAssigneesLoading] = useState(false);
  const [taskAssigneesError, setTaskAssigneesError] = useState(null);
  const taskAssigneesRequestRef = useRef(0);
  const rootPanDragRef = useRef(null);
  const dragGestureRef = useRef(null);
  const inboxHtmlDragNodeIdRef = useRef(null);
  const inboxHtmlDragPointRef = useRef(null);
  const dragFrameRef = useRef({ rafId: null, pendingCommand: null });
  const releaseSettlementRef = useRef({
    frameId: null,
    timerId: null,
    generation: 0,
    finalize: null,
  });
  const releaseSettlementPendingRef = useRef(false);
  const releaseSettlementTimingRef = useRef({
    generation: 0,
    requestId: 0,
    scheduledAt: null,
  });
  const largeMapHighWaterRef = useRef({
    mountedNodes: 0,
    mountedEdges: 0,
    routeDurationMs: 0,
    maxRouteNodeCount: 0,
    maxRouteEdgeCount: 0,
    denseEdgeRoutingDurationMs: 0,
  });
  const edgeRoutingWorkerRef = useRef({ worker: null, timerId: null, requestId: 0 });
  const denseEdgeRoutingInputRef = useRef(null);
  const releaseSettlementControllerRef = useRef(null);
  const graphRevisionRef = useRef(0);
  const graphIdentityRef = useRef({ nodes: null, relationships: null });
  const [releaseSettlementStatus, setReleaseSettlementStatus] = useState('idle');
  const [releaseSettlementSource, setReleaseSettlementSource] = useState('none');
  const [releaseSettlementMetrics, setReleaseSettlementMetrics] = useState({
    requestId: 0,
    completedRequestId: 0,
    feedbackMs: null,
    durationMs: null,
  });
  const [usablePaintAtMs, setUsablePaintAtMs] = useState(null);
  const [denseEdgeRouting, setDenseEdgeRouting] = useState({
    geometryKey: null,
    routes: null,
    status: 'idle',
    durationMs: null,
  });
  const layoutTransitionTimerRef = useRef(null);
  const reparentTargetRef = useRef(null);
  const linkedMapCreationRef = useRef(new Set());
  const latestGraphRef = useRef({ nodes: [], relationships: [] });
  const moderationProjectionRef = useRef({ fadedNodeIds: new Set() });
  const pendingAutoFocusNodeIdRef = useRef(null);
  const pendingCreationRevealRef = useRef(null);
  const creationRevealFrameRef = useRef(null);
  const creationPlacementFrameRef = useRef(null);
  const directionalNavigationFrameRef = useRef(null);
  const directionalNavigationCorrectionTimerRef = useRef(null);
  const creationViewportStateRef = useRef({
    scale: 1,
    offset: { x: 0, y: 0 },
  });
  const creationSetViewportRef = useRef(null);
  const recentNodePointerDownRef = useRef(null);
  const lastProjectedRootInfoRef = useRef({
    mindmapId: null,
    description: null,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(max-width: 720px)');
    const update = () => setIsMobileViewport(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof window === 'undefined') return undefined;

    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      const nextSize = { width: rect.width, height: rect.height };
      setCanvasViewportSize((current) => (
        current.width === nextSize.width && current.height === nextSize.height
          ? current
          : nextSize
      ));
    };

    updateSize();
    const observer = typeof window.ResizeObserver === 'function'
      ? new window.ResizeObserver(updateSize)
      : null;
    observer?.observe(container);
    window.addEventListener('resize', updateSize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const focusMapSearchInput = useCallback((select = false) => {
    if (typeof document === 'undefined') return;
    const input = document.querySelector(MAP_SEARCH_INPUT_SELECTOR);
    if (!input) return;
    input.focus();
    if (select && typeof input.select === 'function') input.select();
  }, []);

  const openMapSearch = useCallback((options = {}) => {
    setIsSearchOpen(true);
    if (options.focus === false || typeof window === 'undefined') return;
    window.requestAnimationFrame(() => focusMapSearchInput(options.select !== false));
  }, [focusMapSearchInput]);

  const closeMapSearch = useCallback((options = {}) => {
    setIsSearchOpen(false);
    if (options.blur !== true || typeof document === 'undefined') return;
    const input = document.querySelector(MAP_SEARCH_INPUT_SELECTOR);
    if (input && document.activeElement === input) input.blur();
  }, []);

  const updateMapSearchQuery = useCallback((query) => {
    setSearchQuery(typeof query === 'string' ? query : '');
    setSearchActiveIndex(0);
    setIsSearchOpen(true);
  }, []);

  const accessRole = remoteMapData?.accessRole
    || (remoteMapData?.ownerUserId === currentUserId ? 'owner' : null);
  const remoteMindmapId = remoteMapData?.mindmapId || null;
  // Remote maps always use the collaboration document, including anonymous
  // public/link visitors. The existing local flow remains available only when
  // there is no authorized remote map.
  const isLoggedIn = Boolean(remoteMapData?.mindmapId && accessRole);
  const canWriteMap = !isLoggedIn || accessRole === 'owner' || accessRole === 'editor';
  const canManageMapMetadata = !isLoggedIn || accessRole === 'owner';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = `mindMapIdeaInboxMode:${remoteMindmapId || 'guest-map'}`;
    try {
      const stored = window.localStorage.getItem(key);
      if (['expanded', 'collapsed', 'hidden'].includes(stored)) {
        setIdeaInboxMode(stored);
      } else {
        setIdeaInboxMode(isMobileViewport ? 'expanded' : 'collapsed');
      }
    } catch {
      setIdeaInboxMode(isMobileViewport ? 'expanded' : 'collapsed');
    }
    setMobileWorkspaceView(isMobileViewport ? 'ideas' : 'map');
    setCaptureTargetId(null);
  }, [isMobileViewport, remoteMindmapId]);

  const updateIdeaInboxMode = useCallback((mode) => {
    if (!['expanded', 'collapsed', 'hidden'].includes(mode)) return;
    setIdeaInboxMode(mode);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        `mindMapIdeaInboxMode:${remoteMindmapId || 'guest-map'}`,
        mode
      );
    } catch {
      // The current session can still use the selected state.
    }
  }, [remoteMindmapId]);

  const showNotification = useCallback((message, variant = 'info') => {
    if (!message) return;
    const id = Date.now() + Math.random();
    setNotifications((prev) => [...prev, { id, message, variant }]);

    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  }, []);

  const refreshTaskAssignees = useCallback(async () => {
    const requestId = taskAssigneesRequestRef.current + 1;
    taskAssigneesRequestRef.current = requestId;
    if (accessRole !== 'owner' || !remoteMindmapId) {
      setTaskAssignees([]);
      setTaskAssigneesMapId(null);
      setTaskAssigneesError(null);
      setTaskAssigneesLoading(false);
      return [];
    }

    setTaskAssigneesLoading(true);
    try {
      const settings = await getMindMapSharing(remoteMindmapId);
      const candidates = normalizeTaskAssignees(settings?.members);
      if (requestId !== taskAssigneesRequestRef.current) return null;
      setTaskAssignees(candidates);
      setTaskAssigneesMapId(remoteMindmapId);
      setTaskAssigneesError(null);
      return candidates;
    } catch (error) {
      if (requestId !== taskAssigneesRequestRef.current) return null;
      setTaskAssigneesError(error);
      return null;
    } finally {
      if (requestId === taskAssigneesRequestRef.current) {
        setTaskAssigneesLoading(false);
      }
    }
  }, [accessRole, remoteMindmapId]);

  useEffect(() => {
    taskAssigneesRequestRef.current += 1;
    setTaskAssignees([]);
    setTaskAssigneesMapId(null);
    setTaskAssigneesError(null);
    setTaskAssigneesLoading(false);
  }, [accessRole, remoteMindmapId]);

  useEffect(() => () => {
    taskAssigneesRequestRef.current += 1;
  }, []);

  const exitDistractionFree = useCallback(() => {
    setIsDistractionFree(false);
    setAreFullscreenMenusVisible(false);
    if (typeof document === 'undefined') return;
    const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
    const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen;
    if (fullscreenElement && typeof exitFullscreen === 'function') {
      Promise.resolve(exitFullscreen.call(document)).catch(() => {
        // The app-level distraction-free view has still been exited.
      });
    }
  }, []);

  const enterDistractionFree = useCallback(() => {
    setIsDistractionFree(true);
    setAreFullscreenMenusVisible(false);
    if (typeof document === 'undefined') return;
    const fullscreenTarget = document.documentElement;
    const requestFullscreen = fullscreenTarget?.requestFullscreen
      || fullscreenTarget?.webkitRequestFullscreen;
    if (typeof requestFullscreen === 'function') {
      Promise.resolve(requestFullscreen.call(fullscreenTarget)).catch(() => {
        // Browsers may deny fullscreen; the distraction-free layout still works.
      });
    }
  }, []);

  useEffect(() => {
    if (typeof onDistractionFreeChange === 'function') {
      onDistractionFreeChange({
        active: isDistractionFree,
        menusVisible: areFullscreenMenusVisible,
      });
    }
  }, [
    areFullscreenMenusVisible,
    isDistractionFree,
    onDistractionFreeChange,
  ]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement;
      if (!fullscreenElement) {
        setIsDistractionFree(false);
        setAreFullscreenMenusVisible(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (!isDistractionFree || typeof window === 'undefined') return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') exitDistractionFree();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [exitDistractionFree, isDistractionFree]);

  useEffect(() => () => {
    if (typeof onDistractionFreeChange === 'function') {
      onDistractionFreeChange({ active: false, menusVisible: false });
    }
  }, [onDistractionFreeChange]);

  // On initial mount, ping the backend health endpoint so we can
  // verify browser → API connectivity and CORS configuration.
  useEffect(() => {
    let cancelled = false;

    getHealth()
      .then((data) => {
        if (!cancelled) {
          // For now, just log the result; we can surface this in the UI later.
          console.log('Backend health:', data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Backend health check failed:', err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  // Persist theme selection so dark/light mode is restored on reload
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('mindMapTheme', isDarkMode ? 'dark' : 'light');
    } catch (e) {
      console.error('Failed to save theme preference:', e);
    }
  }, [isDarkMode]);


    // Reparenting candidate shown while a dragged subtree overlaps a valid parent.
    const [reparentCandidate, setReparentCandidate] = useState({
      visible: false,
      draggedNode: null,
      targetNode: null,
      position: { x: 0, y: 0 }
    });
    const [draggedNodeId, setDraggedNodeId] = useState(null);
    const [highlightedNodeId, setHighlightedNodeId] = useState(null);

    // Initialize collision detection
    const { resolveCollisions } = useCollisionDetection({
      nodeRefs,
      minDistance: 20,
      animationDuration: 300
    });

    // Initialize undo/redo for local maps. Remote maps rely on Yjs history.
    const {
      saveVersion,
      undo: localUndo,
      redo: localRedo,
      canUndo: localCanUndo,
      canRedo: localCanRedo,
    } = useUndoRedo({ maxHistorySize: 20 });

    // Initialize node management
    const nodeManagement = useNodeManagement({
      containerRef,
      saveVersion,
      resolveCollisions,
      currentUserId,
      currentUserName,
    });

    const {
      nodes,
      relationships,
      selectedId,
      root,
      setSelectedId,
      setNodes,
      getNode,
      getSelectedNode,
      getChildren,
      insertNodeWithRelationship,
      insertInboxIdea,
      applyMoveBranchToInboxCommand,
      applyAttachInboxBranchCommand,
      applyMoveNodesCommand,
      applyReparentSubtreeCommand,
      applyDeleteSubtreeCommand,
      updateNodeText,
      updateNodePosition,
      setNodeColor,
      toggleTaskFlag,
      toggleTaskDone,
      clearAutoFocus,
    } = nodeManagement;

  const ideaInbox = useMemo(
    () => deriveIdeaInbox(nodes, relationships),
    [nodes, relationships]
  );
  const captureTarget = captureTargetId && !ideaInbox.hiddenNodeIds.has(captureTargetId)
    ? nodes.find((node) => node.id === captureTargetId) || null
    : null;
  const canvasNodes = useMemo(
    () => nodes.filter((node) => !ideaInbox.hiddenNodeIds.has(node.id)),
    [ideaInbox.hiddenNodeIds, nodes]
  );
  const canvasRelationships = useMemo(() => relationships.filter((relationship) => (
    !ideaInbox.hiddenNodeIds.has(relationship.parentId)
    && !ideaInbox.hiddenNodeIds.has(relationship.childId)
  )), [ideaInbox.hiddenNodeIds, relationships]);

  useEffect(() => {
    if (!captureTargetId) return;
    if (
      ideaInbox.hiddenNodeIds.has(captureTargetId)
      || !nodes.some((node) => node.id === captureTargetId)
    ) {
      setCaptureTargetId(null);
    }
  }, [captureTargetId, ideaInbox.hiddenNodeIds, nodes]);

  if (
    graphIdentityRef.current.nodes !== nodes
    || graphIdentityRef.current.relationships !== relationships
  ) {
    graphRevisionRef.current += 1;
    graphIdentityRef.current = { nodes, relationships };
  }
  latestGraphRef.current = { nodes, relationships };

  // Initialize persistence (used by both guest and logged-in flows)
  const {
    saveMindMap,
    loadMindMap,
    loadMindMapFromData,
    projectMindMapFromData,
  } = usePersistence({
    nodeManagement,
    saveVersion
  });

  // Expose saveMindMap to parent (App) so it can force
  // a save on sign-out or other global events.
  useEffect(() => {
    if (typeof onRegisterSaveHandler === 'function') {
      onRegisterSaveHandler(saveMindMap);
    }
  }, [onRegisterSaveHandler, saveMindMap]);

  // --- Yjs / Hocuspocus integration for authorized remote maps ---

  const [mindmapIdForYjs, setMindmapIdForYjs] = useState(null);
  const prevMindmapIdRef = useRef(null);

  // Resolve the mindmap id for this view: prefer backend-provided
  // id, fall back to ?map= query param when present.
  useEffect(() => {
    let id = null;

    if (remoteMapData && remoteMapData.mindmapId) {
      id = remoteMapData.mindmapId;
    }

    if (!id && typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        const mapParam = url.searchParams.get('map');
        if (mapParam) id = mapParam;
      } catch {
        // ignore URL parse errors
      }
    }

    setMindmapIdForYjs(id || null);
  }, [remoteMapData]);

  // Viewport reset effect relocated below where setViewport is defined

  const [yInitialData, setYInitialData] = useState(null);

  // Provide initial snapshot only for the first load when we have
  // legacy JSON data from the backend; Yjs will seed from this
  // exactly once when the document is empty.
  useEffect(() => {
    if (!isLoggedIn) {
      setYInitialData(null);
      return;
    }

    if (remoteMapData && Array.isArray(remoteMapData.nodes)) {
      setYInitialData({
        nodes: remoteMapData.nodes,
        relationships: remoteMapData.relationships || [],
        root: remoteMapData.root || null,
        meta: {},
      });
    } else {
      setYInitialData(null);
    }
  }, [isLoggedIn, remoteMapData]);

  const authToken = isLoggedIn ? getAuthToken() : null;

  const {
    yState,
    actions: yActions,
    isSynced: yIsSynced,
    isLocallySynced: yIsLocallySynced,
    isNetworkSynced: yIsNetworkSynced,
    connectionStatus: yConnectionStatus,
    initialSyncMetrics: yInitialSyncMetrics,
    yProvider,
    awarenessStates: yAwarenessStates,
    recoveryNotice: yRecoveryNotice,
    clearRecoveryNotice: clearYRecoveryNotice,
    prepareRecoveryReload: prepareYRecoveryReload,
  } = useYjsMap({
    mindmapId: isLoggedIn && accessRole ? mindmapIdForYjs : null,
    initialData: isLoggedIn ? yInitialData : null,
    authToken,
    readOnly: !canWriteMap,
    currentUserId,
    currentUserName,
    selectedNodeId: selectedId,
    editingNodeId,
  }); //yProvider,

  const nodeBranchModeration = useNodeBranchModeration({
    mindmapId: mindmapIdForYjs,
    enabled: Boolean(mindmapIdForYjs && accessRole),
    provider: yProvider,
  });
  const hideModeratedBranch = nodeBranchModeration.hide;
  const restoreModeratedBranch = nodeBranchModeration.restore;
  const moderatedBranchByNodeId = nodeBranchModeration.branchByNodeId;
  const moderationRootKey = useMemo(() => (
    nodeBranchModeration.branches
      .map((branch) => branch.nodeId)
      .filter(Boolean)
      .sort()
      .join(',')
  ), [nodeBranchModeration.branches]);
  const moderationProjection = useMemo(() => (
    deriveNodeBranchModerationProjection({
      relationships,
      branches: nodeBranchModeration.branches,
      currentUserId,
      accessRole,
    })
  ), [
    accessRole,
    currentUserId,
    nodeBranchModeration.branches,
    relationships,
  ]);
  moderationProjectionRef.current = moderationProjection;
  const moderationExcludedNodeIds = useMemo(() => (
    nodeBranchModeration.isReady
      ? moderationProjection.excludedNodeIds
      : new Set(nodes.map((node) => node.id))
  ), [
    moderationProjection.excludedNodeIds,
    nodeBranchModeration.isReady,
    nodes,
  ]);
  const canvasExcludedNodeIds = useMemo(() => new Set([
    ...moderationExcludedNodeIds,
    ...ideaInbox.hiddenNodeIds,
  ]), [ideaInbox.hiddenNodeIds, moderationExcludedNodeIds]);
  const moderatedGraph = useMemo(() => filterModeratedGraph(
    canvasNodes,
    canvasRelationships,
    moderationExcludedNodeIds
  ), [canvasNodes, canvasRelationships, moderationExcludedNodeIds]);
  const searchResults = useMemo(
    () => searchNodes(moderatedGraph.nodes, searchQuery),
    [moderatedGraph.nodes, searchQuery]
  );

  useEffect(() => {
    setSearchActiveIndex((current) => (
      Math.min(current, Math.max(searchResults.length - 1, 0))
    ));
  }, [searchResults.length]);

  const moveMapSearchSelection = useCallback((delta) => {
    if (!searchResults.length) return;
    setSearchActiveIndex((current) => (
      (current + delta + searchResults.length) % searchResults.length
    ));
  }, [searchResults.length]);

  // Resolve the active map theme. Remote maps store it in the shared Yjs
  // meta (so it syncs live and persists with the map); local maps fall back
  // to the locally persisted choice. Unknown ids resolve to the default.
  const activeThemeId = isLoggedIn
    ? (yState?.meta?.theme || DEFAULT_THEME_ID)
    : localThemeId;
  const mapTheme = getMapTheme(activeThemeId);
  const activeConnectionStyleId = normalizeConnectionStyleId(
    isLoggedIn ? yState?.meta?.connectionStyle : localConnectionStyleId
  );

  const setMapTheme = useCallback((themeId) => {
    // UI-level enforcement: only the map owner may change a shared map's
    // theme. (Server-side enforcement is a known follow-up.)
    if (!canManageMapMetadata) return false;
    if (isLoggedIn) {
      return !!(yActions?.setTheme && yActions.setTheme(themeId));
    }
    const nextThemeId = themeId || DEFAULT_THEME_ID;
    setLocalThemeId(nextThemeId);
    try {
      window.localStorage.setItem('mindMapMapTheme', nextThemeId);
    } catch {
      // The theme still applies for this session.
    }
    return true;
  }, [canManageMapMetadata, isLoggedIn, yActions]);

  const setMapConnectionStyle = useCallback((connectionStyleId) => {
    if (!canManageMapMetadata) return false;
    const nextStyleId = normalizeConnectionStyleId(connectionStyleId);
    if (isLoggedIn) {
      return !!(
        yActions?.setConnectionStyle
        && yActions.setConnectionStyle(nextStyleId)
      );
    }
    setLocalConnectionStyleId(nextStyleId);
    try {
      window.localStorage.setItem('mindMapConnectionStyle', nextStyleId);
    } catch {
      // The style still applies for this session.
    }
    return true;
  }, [canManageMapMetadata, isLoggedIn, yActions]);

  const hasSeededYDocRef = useRef(false);
  const hasHydratedFromYjsRef = useRef(false);

  // Keep React node state as a projection of the Yjs document for
  // remote-map visitors. We deliberately do not restore selectedId from
  // Yjs – it remains a per-user UI concern.
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!yState) return;

    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.log('[YJS] yState snapshot:', yState);
    }

    let projected = yState;

    const hasNodes = Array.isArray(yState.nodes) && yState.nodes.length > 0;
    const hasRoot = !!yState.root;

    // For brand-new writable maps with an empty Yjs document,
    // create a default "New Idea" root. If we can write to Yjs,
    // seed the document first and wait for the next yState update.
    if (!hasNodes && !hasRoot && !hasSeededYDocRef.current) {
      // If the provider hasn't completed its initial sync yet,
      // we can't safely decide that the document is truly empty.
      // In this case, skip seeding for now but still project the
      // current (possibly empty) state so the UI can render while
      // we wait for remote content.
      if (!yIsSynced) {
        // Fall through to projection without seeding.
      } else {
      const id = crypto.randomUUID();

      let position = { x: 300, y: 300 };
      try {
        if (containerRef.current && containerRef.current.getBoundingClientRect) {
          const rect = containerRef.current.getBoundingClientRect();
          position = {
            x: rect.width / 2 - 100,
            y: rect.height / 2 - 30,
          };
        }
      } catch {
        // fall back to default position
      }

      const color = COLORS[0]?.gradient || null;

      const newNode = {
        id,
        text: 'New Idea',
        position,
        color,
        isRoot: true,
        collapsedChildren: null,
        embeddedMindmapId: null,
        linkUrl: null,
        linkMeta: null,
        isTask: false,
        taskDone: false,
        emoji: null,
        isEmbeddedMindmapLink: false,
      };

      if (yActions && yActions.addNode && yActions.setRoot) {
        hasSeededYDocRef.current = true;
        yActions.addNode(newNode);
        yActions.setRoot(id);
        // Wait for Yjs to apply the change and update yState
        return;
      }

      // Fallback: if we can't write to Yjs for some reason,
      // at least project a local default node so the UI isn't blank.
      projected = {
        ...yState,
        nodes: [newNode],
        relationships: [],
        root: id,
      };
      }
    }

    let data = {
      nodes: projected.nodes || [],
      relationships: projected.relationships || [],
      root: projected.root || null,
    };

    // On first hydration from Yjs, initialize selectedId to the root
    // so keyboard shortcuts and "Add child" work without requiring
    // a manual click. After that, selectedId stays purely local.
    const isFirstHydration = !hasHydratedFromYjsRef.current;
    if (isFirstHydration) {
      const rootId = data.root || (data.nodes.length > 0 ? data.nodes[0].id : null);
      data.selectedId = rootId;
    }

    const shouldPreserveSelection = !isFirstHydration;

    if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
      console.log('[YJS] Projected data into React:', data, 'preserveSelectedId:', shouldPreserveSelection);
    }

    projectMindMapFromData(data, { preserveSelectedId: shouldPreserveSelection });
    if (isFirstHydration) {
      hasHydratedFromYjsRef.current = true;
    }
    hasLoadedRef.current = true;
  }, [isLoggedIn, yState, yIsSynced, yActions, projectMindMapFromData]);

  // When a user signs in, tag any anonymous nodes with their userId
  useEffect(() => {
    if (!currentUserId) return;
    setNodes(prev => prev.map(node => (
      node.userId ? node : { ...node, userId: currentUserId }
    )));
  }, [currentUserId, setNodes]);

  const rootInfoProjection = useMemo(
    () => getRootInfoProjection(nodes, root),
    [nodes, root]
  );

  // Root Info remains collaborative Yjs content. The owner also projects it
  // into bounded PostgreSQL metadata so authorized map cards and public Open
  // Graph previews do not need to decode the complete CRDT document.
  useEffect(() => {
    if (
      !isLoggedIn
      || !canManageMapMetadata
      || !mindmapIdForYjs
      || !root
      || !yIsSynced
      || !yIsNetworkSynced
    ) {
      return undefined;
    }
    if (projectionMatches(
      lastProjectedRootInfoRef.current,
      mindmapIdForYjs,
      rootInfoProjection
    )) {
      return undefined;
    }

    let cancelled = false;
    let timerId = null;
    let retryIndex = 0;

    const project = async () => {
      try {
        const result = await updateMindMapDescription(
          mindmapIdForYjs,
          rootInfoProjection
        );
        if (!result) {
          throw new Error('Map description projection was not accepted');
        }
        if (!cancelled) {
          lastProjectedRootInfoRef.current = {
            mindmapId: mindmapIdForYjs,
            description: rootInfoProjection,
          };
        }
      } catch (error) {
        if (cancelled || retryIndex >= ROOT_INFO_PROJECTION_RETRY_DELAYS_MS.length) {
          if (!cancelled) {
            console.warn('Root Info preview projection is pending a later retry', {
              mindmapId: mindmapIdForYjs,
              error,
            });
          }
          return;
        }
        const retryDelay = ROOT_INFO_PROJECTION_RETRY_DELAYS_MS[retryIndex];
        retryIndex += 1;
        timerId = window.setTimeout(project, retryDelay);
      }
    };

    timerId = window.setTimeout(project, ROOT_INFO_PROJECTION_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [
    canManageMapMetadata,
    isLoggedIn,
    mindmapIdForYjs,
    root,
    rootInfoProjection,
    yIsNetworkSynced,
    yIsSynced,
  ]);

  const contentBounds = useMemo(() => {
    void layoutVersion; // ensures layoutVersion participates without altering math
    if (!Array.isArray(canvasNodes) || canvasNodes.length === 0) {
      const span = DEFAULT_CANVAS_SPAN / 2;
      return {
        minX: -span,
        maxX: span,
        minY: -span,
        maxY: span,
      };
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    canvasNodes.forEach((node) => {
      if (!node || !node.position) return;
      const left = node.position.x;
      const right = node.position.x + NODE_WIDTH_GUESS;
      const top = node.position.y;
      const bottom = node.position.y + NODE_HEIGHT_GUESS;

      minX = Math.min(minX, left);
      maxX = Math.max(maxX, right);
      minY = Math.min(minY, top);
      maxY = Math.max(maxY, bottom);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
      const span = DEFAULT_CANVAS_SPAN / 2;
      return {
        minX: -span,
        maxX: span,
        minY: -span,
        maxY: span,
      };
    }

    return {
      minX: minX - CANVAS_PADDING,
      maxX: maxX + CANVAS_PADDING,
      minY: minY - CANVAS_PADDING,
      maxY: maxY + CANVAS_PADDING,
    };
  }, [canvasNodes, layoutVersion]);

  const stableMindmapId = mindmapIdForYjs || (remoteMapData && remoteMapData.mindmapId) || null;
  const viewportStorageKey = stableMindmapId || (!isLoggedIn ? 'guest-map' : null);

  useEffect(() => {
    previousEdgeRoutesRef.current = new Map();
  }, [viewportStorageKey]);

  const canvasWidth = Math.max(
    DEFAULT_CANVAS_SPAN,
    contentBounds.maxX - contentBounds.minX
  );
  const canvasHeight = Math.max(
    DEFAULT_CANVAS_SPAN,
    contentBounds.maxY - contentBounds.minY
  );

  // Initialize map navigation. For logged-in users, panning/zooming
  // update positions through Yjs so the projection stays consistent
  // across tabs. Guest users use the local state helper.
  const moveNodeForNavigation = useCallback((nodeId, position, options) => {
    if (isLoggedIn && yActions && yActions.updateNode) {
      yActions.updateNode(nodeId, { position, lastEditedBy: currentUserId });
      return;
    }
    updateNodePosition(nodeId, position, options);
  }, [isLoggedIn, yActions, updateNodePosition, currentUserId]);

  const mapNavigation = useMapNavigation({
    containerRef,
    root,
    nodes: canvasNodes,
    moveNode: moveNodeForNavigation,
    contentBounds,
    viewportStorageKey,
  });

  const {
    scale,
    offset,
    didRestoreViewport,
    centerNode,
    focusNodeIfOutOfView,
    zoomIn,
    zoomOut,
    resetZoom,
    fitToBounds,
    panBy,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp: navHandleMouseUp,
    handlePointerDown: navHandlePointerDown,
    handlePointerMove: navHandlePointerMove,
    handlePointerUp: navHandlePointerUp,
    handlePointerCancel: navHandlePointerCancel,
    getViewport,
    setViewport
  } = mapNavigation;
  creationViewportStateRef.current = { scale, offset };
  creationSetViewportRef.current = setViewport;

  const readCreationViewportGeometry = useCallback(() => {
    const container = containerRef.current;
    const containerRect = container?.getBoundingClientRect?.();
    if (!containerRect || containerRect.width <= 0 || containerRect.height <= 0) return null;
    const currentViewport = creationViewportStateRef.current;

    const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null;
    const visualClientRect = visualViewport
      ? {
          left: visualViewport.offsetLeft,
          top: visualViewport.offsetTop,
          right: visualViewport.offsetLeft + visualViewport.width,
          bottom: visualViewport.offsetTop + visualViewport.height,
        }
      : {
          left: containerRect.left,
          top: containerRect.top,
          right: containerRect.right,
          bottom: containerRect.bottom,
        };
    const visibleClientRect = {
      left: Math.max(containerRect.left, visualClientRect.left),
      top: Math.max(containerRect.top, visualClientRect.top),
      right: Math.min(containerRect.right, visualClientRect.right),
      bottom: Math.min(containerRect.bottom, visualClientRect.bottom),
    };
    const viewportOrigin = {
      x: Math.max(0, visibleClientRect.left - containerRect.left),
      y: Math.max(0, visibleClientRect.top - containerRect.top),
    };
    const viewportSize = {
      width: Math.max(0, visibleClientRect.right - visibleClientRect.left),
      height: Math.max(0, visibleClientRect.bottom - visibleClientRect.top),
    };
    const obstacleRects = typeof document === 'undefined'
      ? []
      : [...document.querySelectorAll(VIEWPORT_OCCLUSION_SELECTOR)]
          .filter((element) => {
            const style = window.getComputedStyle(element);
            return (
              style.display !== 'none'
              && style.visibility !== 'hidden'
              && Number.parseFloat(style.opacity || '1') > 0.05
              && element.getClientRects().length > 0
            );
          })
          .map((element) => element.getBoundingClientRect())
          .filter((rect) => (
            rect.right > visibleClientRect.left
            && rect.left < visibleClientRect.right
            && rect.bottom > visibleClientRect.top
            && rect.top < visibleClientRect.bottom
          ))
          .map((rect) => ({
            x: rect.left - containerRect.left - CREATION_VIEWPORT_INSET,
            y: rect.top - containerRect.top - CREATION_VIEWPORT_INSET,
            width: rect.width + CREATION_VIEWPORT_INSET * 2,
            height: rect.height + CREATION_VIEWPORT_INSET * 2,
          }));
    const screenBounds = {
      x: viewportOrigin.x + CREATION_VIEWPORT_INSET,
      y: viewportOrigin.y + CREATION_VIEWPORT_INSET,
      width: Math.max(0, viewportSize.width - CREATION_VIEWPORT_INSET * 2),
      height: Math.max(0, viewportSize.height - CREATION_VIEWPORT_INSET * 2),
    };

    return {
      constraint: createWorldViewportConstraint({
        viewportSize,
        viewportOrigin,
        viewport: currentViewport,
        inset: CREATION_VIEWPORT_INSET,
        obstacles: obstacleRects,
      }),
      screenBounds,
      screenObstacles: obstacleRects,
      containerRect,
    };
  }, []);

  const cancelViewportPanAnimation = useCallback(() => {
    if (
      creationRevealFrameRef.current !== null
      && typeof window !== 'undefined'
    ) {
      window.cancelAnimationFrame(creationRevealFrameRef.current);
      creationRevealFrameRef.current = null;
    }
    if (
      directionalNavigationFrameRef.current !== null
      && typeof window !== 'undefined'
    ) {
      window.cancelAnimationFrame(directionalNavigationFrameRef.current);
      directionalNavigationFrameRef.current = null;
    }
    if (
      directionalNavigationCorrectionTimerRef.current !== null
      && typeof window !== 'undefined'
    ) {
      window.clearTimeout(directionalNavigationCorrectionTimerRef.current);
      directionalNavigationCorrectionTimerRef.current = null;
    }
  }, []);

  const animateViewportPan = useCallback((delta) => {
    if (
      !Number.isFinite(delta?.x)
      || !Number.isFinite(delta?.y)
      || (Math.abs(delta.x) < 0.5 && Math.abs(delta.y) < 0.5)
      || typeof window === 'undefined'
    ) {
      return;
    }
    cancelViewportPanAnimation();
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      const currentViewport = creationViewportStateRef.current;
      creationSetViewportRef.current?.({
        offset: {
          x: currentViewport.offset.x + delta.x,
          y: currentViewport.offset.y + delta.y,
        },
      });
      creationRevealFrameRef.current = null;
      return;
    }
    const startViewport = creationViewportStateRef.current;
    const startedAt = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / CREATION_REVEAL_MS);
      const eased = 1 - ((1 - progress) ** 3);
      creationSetViewportRef.current?.({
        offset: {
          x: startViewport.offset.x + delta.x * eased,
          y: startViewport.offset.y + delta.y * eased,
        },
      });
      if (progress < 1) {
        creationRevealFrameRef.current = window.requestAnimationFrame(step);
      } else {
        creationRevealFrameRef.current = null;
      }
    };
    creationRevealFrameRef.current = window.requestAnimationFrame(step);
  }, [cancelViewportPanAnimation]);

  const revealCreatedNode = useCallback((nodeId) => {
    const geometry = readCreationViewportGeometry();
    const nodeRect = nodeRefs.current[nodeId]?.getBoundingClientRect?.();
    if (!geometry || !nodeRect) return false;
    const relativeRect = {
      x: nodeRect.left - geometry.containerRect.left,
      y: nodeRect.top - geometry.containerRect.top,
      width: nodeRect.width,
      height: nodeRect.height,
    };
    const delta = minimumPanDeltaToReveal({
      rect: relativeRect,
      bounds: geometry.screenBounds,
      obstacles: geometry.screenObstacles,
    });
    animateViewportPan(delta);
    return Math.abs(delta.x) >= 0.5 || Math.abs(delta.y) >= 0.5;
  }, [animateViewportPan, readCreationViewportGeometry]);

  // Center a node using its visual center rather than top-left
  const centerNodePrecise = useCallback((nodeId, options = {}) => {
    cancelViewportPanAnimation();
    const node = nodes.find(n => n.id === nodeId);
    const containerRect = containerRef.current?.getBoundingClientRect();
    const nodeRef = nodeRefs.current[nodeId];
    const positionOverride = options && options.positionOverride ? options.positionOverride : null;

    if (!containerRect) {
      centerNode(nodeId);
      return;
    }

    let width = 200;
    let height = 60;
    const measuredSize = readNodeLayoutSize(nodeRef, scale);
    if (measuredSize) {
      width = measuredSize.width;
      height = measuredSize.height;
    }

    const targetPosition = positionOverride || (node ? node.position : null);
    if (!targetPosition) {
      centerNode(nodeId);
      return;
    }

    const viewportCenterX = containerRect.width / 2;
    const viewportCenterY = containerRect.height / 2;

    const nodeCenterX = (targetPosition.x + width / 2) * scale;
    const nodeCenterY = (targetPosition.y + height / 2) * scale;

    const newOffsetX = viewportCenterX - nodeCenterX;
    const newOffsetY = viewportCenterY - nodeCenterY;

    if (typeof setViewport === 'function') {
      setViewport({ offset: { x: newOffsetX, y: newOffsetY } });
    }
  }, [cancelViewportPanAnimation, nodes, containerRef, scale, centerNode, setViewport]);

  // Initialize visibility management
  const { getVisibleNodeIds } = useVisibility({
    nodes,
    relationships,
    focusRootId,
    collapsedNodeIds,
    temporarilyExpandedNodeIds,
    excludedNodeIds: canvasExcludedNodeIds,
  });

  const visibleNodeIds = useMemo(() => getVisibleNodeIds(), [getVisibleNodeIds]);
  const fullMapVisibleNodeIds = useMemo(() => deriveVisibleNodeIds({
    nodes,
    relationships,
    focusRootId: null,
    collapsedNodeIds,
    temporarilyExpandedNodeIds,
    excludedNodeIds: canvasExcludedNodeIds,
  }), [
    canvasExcludedNodeIds,
    collapsedNodeIds,
    nodes,
    relationships,
    temporarilyExpandedNodeIds,
  ]);
  const renderedNodeIds = useMemo(() => {
    if (!focusRootId) return visibleNodeIds;
    return new Set([...fullMapVisibleNodeIds, ...visibleNodeIds]);
  }, [focusRootId, fullMapVisibleNodeIds, visibleNodeIds]);
  const contextNodeIds = useMemo(() => {
    if (!focusRootId) return new Set();
    return new Set([...renderedNodeIds].filter((nodeId) => !visibleNodeIds.has(nodeId)));
  }, [focusRootId, renderedNodeIds, visibleNodeIds]);
  const directionalNavigationIndex = useMemo(() => {
    void layoutVersion; // measured sizes live in a ref; rebuild after geometry changes
    return createDirectionalNavigationIndex(nodes, {
        eligibleIds: renderedNodeIds,
        getRect: (node) => {
          const position = getRenderedNodePosition(node, dragPositionPreview);
          const size = measuredNodeSizesRef.current.get(node.id)
            || node.layoutSize
            || { width: NODE_WIDTH_GUESS, height: NODE_HEIGHT_GUESS };
          return { ...position, width: size.width, height: size.height };
        },
      });
  }, [dragPositionPreview, layoutVersion, nodes, renderedNodeIds]);
  const visibleDiscussionNodeIds = useMemo(
    () => [...visibleNodeIds],
    [visibleNodeIds]
  );
  const canUseNodeDiscussions = isNamedDiscussionCollaborator({
    currentUserId,
    isLoggedIn,
    accessRole,
    accessSource: remoteMapData?.accessSource,
  });
  const nodeDiscussions = useNodeDiscussions({
    mindmapId: mindmapIdForYjs,
    visibleNodeIds: visibleDiscussionNodeIds,
    enabled: canUseNodeDiscussions,
    provider: yProvider,
  });
  const activeDiscussionNodeId = nodeDiscussions.activeNodeId;
  const closeActiveDiscussion = nodeDiscussions.closeDiscussion;

  useEffect(() => {
    if (!viewportStorageKey || typeof window === 'undefined') {
      setCollapsedNodeIds(new Set());
      return;
    }
    try {
      const raw = window.localStorage.getItem(`mindMapCollapsed:${viewportStorageKey}`);
      const storedIds = raw ? JSON.parse(raw) : [];
      setCollapsedNodeIds(new Set(Array.isArray(storedIds) ? storedIds : []));
    } catch (error) {
      console.warn('Failed to restore collapsed branches:', error);
      setCollapsedNodeIds(new Set());
    }
  }, [viewportStorageKey]);

  useEffect(() => {
    if (!viewportStorageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        `mindMapCollapsed:${viewportStorageKey}`,
        JSON.stringify([...collapsedNodeIds])
      );
    } catch (error) {
      console.warn('Failed to persist collapsed branches:', error);
    }
  }, [collapsedNodeIds, viewportStorageKey]);

  useEffect(() => {
    const validIds = new Set(nodes.map((node) => node.id));
    setCollapsedNodeIds((previous) => {
      const next = new Set([...previous].filter((nodeId) => (
        validIds.has(nodeId) && getBranchNodeIds(nodeId, relationships).size > 1
      )));
      if (next.size === previous.size && [...next].every((nodeId) => previous.has(nodeId))) {
        return previous;
      }
      return next;
    });
  }, [nodes, relationships]);

  const collapseBranch = useCallback((nodeId) => {
    if (getBranchNodeIds(nodeId, relationships).size <= 1) return false;
    setTemporarilyExpandedNodeIds((previous) => {
      if (!previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.delete(nodeId);
      return next;
    });
    setCollapsedNodeIds((previous) => {
      if (previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.add(nodeId);
      return next;
    });
    return true;
  }, [relationships]);

  const expandBranch = useCallback((nodeId) => {
    setTemporarilyExpandedNodeIds((previous) => {
      if (!previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.delete(nodeId);
      return next;
    });
    setCollapsedNodeIds((previous) => {
      if (!previous.has(nodeId)) return previous;
      const next = new Set(previous);
      next.delete(nodeId);
      return next;
    });
  }, []);

  const revealNode = useCallback((nodeId) => {
    const ancestors = getAncestorNodeIds(nodeId, relationships);
    setCollapsedNodeIds((previous) => {
      const next = new Set([...previous].filter((collapsedId) => !ancestors.has(collapsedId)));
      return next.size === previous.size ? previous : next;
    });
  }, [relationships]);

  const getBoundsForNodeIds = useCallback((nodeIds) => {
    const requestedIds = nodeIds instanceof Set ? nodeIds : new Set(nodeIds || []);
    const requestedNodes = nodes.filter((node) => requestedIds.has(node.id) && node.position);
    if (!requestedNodes.length) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    requestedNodes.forEach((node) => {
      const ref = nodeRefs.current[node.id];
      const measuredSize = readNodeLayoutSize(ref, scale);
      const width = measuredSize?.width || node.layoutSize?.width || NODE_WIDTH_GUESS;
      const height = measuredSize?.height || node.layoutSize?.height || NODE_HEIGHT_GUESS;
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + width);
      maxY = Math.max(maxY, node.position.y + height);
    });
    return { minX, minY, maxX, maxY };
  }, [nodes, scale]);

  const fitNodeIds = useCallback((nodeIds, options = {}) => {
    const bounds = getBoundsForNodeIds(nodeIds);
    if (!bounds) return null;
    return fitToBounds(bounds, {
      padding: options.padding ?? 96,
      maxScale: options.maxScale ?? 1.25,
    });
  }, [fitToBounds, getBoundsForNodeIds]);

  const handleFitVisible = useCallback(() => {
    cancelViewportPanAnimation();
    fitNodeIds(visibleNodeIds);
  }, [cancelViewportPanAnimation, fitNodeIds, visibleNodeIds]);

  const handleFitSelection = useCallback(() => {
    cancelViewportPanAnimation();
    if (!selectedId) {
      handleFitVisible();
      return;
    }
    const branchIds = getBranchNodeIds(selectedId, relationships);
    const visibleBranchIds = new Set([...branchIds].filter((nodeId) => visibleNodeIds.has(nodeId)));
    fitNodeIds(visibleBranchIds.size ? visibleBranchIds : new Set([selectedId]));
  }, [
    cancelViewportPanAnimation,
    fitNodeIds,
    handleFitVisible,
    relationships,
    selectedId,
    visibleNodeIds,
  ]);

  const handleZoomIn = useCallback(() => {
    cancelViewportPanAnimation();
    zoomIn();
  }, [cancelViewportPanAnimation, zoomIn]);

  const handleZoomOut = useCallback(() => {
    cancelViewportPanAnimation();
    zoomOut();
  }, [cancelViewportPanAnimation, zoomOut]);

  const handleResetZoom = useCallback(() => {
    cancelViewportPanAnimation();
    resetZoom();
  }, [cancelViewportPanAnimation, resetZoom]);

  const updateFocusView = useCallback((requestedNodeId, options = {}) => {
    cancelViewportPanAnimation();
    const nextNode = requestedNodeId
      && !canvasExcludedNodeIds.has(requestedNodeId)
      ? getNode(requestedNodeId)
      : null;
    const nextFocusRootId = nextNode ? nextNode.id : null;
    const shouldRecord = options.record !== false;
    const shouldFit = options.fit !== false;

    setFocusRootId(nextFocusRootId);
    if (nextFocusRootId) {
      setSelectedId(nextFocusRootId);
    } else if (root) {
      setSelectedId(root);
    }
    if (shouldRecord) {
      setFocusHistory((previous) => pushFocusHistory(previous, nextFocusRootId));
    }
    if (onBaseNodeChange) {
      onBaseNodeChange(nextNode?.text || null);
    }

    if (typeof window !== 'undefined') {
      const current = new URL(window.location.href);
      if (nextFocusRootId) current.searchParams.set('node', nextFocusRootId);
      else current.searchParams.delete('node');
      window.history.replaceState({}, '', current.toString());
    }

    if (shouldFit && typeof window !== 'undefined') {
      const effectiveCollapsedIds = new Set(collapsedNodeIds);
      if (nextFocusRootId) effectiveCollapsedIds.delete(nextFocusRootId);
      const nextVisibleIds = deriveVisibleNodeIds({
        nodes,
        relationships,
        focusRootId: nextFocusRootId,
        collapsedNodeIds: effectiveCollapsedIds,
        temporarilyExpandedNodeIds,
        excludedNodeIds: canvasExcludedNodeIds,
      });
      window.requestAnimationFrame(() => fitNodeIds(nextVisibleIds));
    }
  }, [
    collapsedNodeIds,
    cancelViewportPanAnimation,
    fitNodeIds,
    getNode,
    canvasExcludedNodeIds,
    nodes,
    onBaseNodeChange,
    relationships,
    root,
    setSelectedId,
    temporarilyExpandedNodeIds,
  ]);

  useEffect(() => {
    if (
      nodeBranchModeration.isReady
      && focusRootId
      && canvasExcludedNodeIds.has(focusRootId)
    ) {
      updateFocusView(null, { record: false });
    }
  }, [
    focusRootId,
    canvasExcludedNodeIds,
    nodeBranchModeration.isReady,
    updateFocusView,
  ]);

  const handleDirectionalNavigation = useCallback((direction) => {
    const selectedNode = getSelectedNode();
    if (!selectedNode || typeof window === 'undefined') return false;
    if (clickTimeoutRef.current !== null) {
      window.clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    cancelViewportPanAnimation();
    const containerRect = containerRef.current?.getBoundingClientRect?.();
    const originRect = nodeRefs.current[selectedNode.id]?.getBoundingClientRect?.();
    if (!containerRect) return false;
    const currentViewport = getViewport();
    const liveOriginCenter = originRect
      ? {
          x: (
            originRect.left + originRect.width / 2
            - containerRect.left - currentViewport.offset.x
          ) / currentViewport.scale,
          y: (
            originRect.top + originRect.height / 2
            - containerRect.top - currentViewport.offset.y
          ) / currentViewport.scale,
        }
      : null;
    const result = directionalNavigationIndex.find(selectedNode.id, direction, {
      originCenter: liveOriginCenter,
    });
    if (!result) return false;
    const originScreenPoint = originRect
      ? {
          x: originRect.left + originRect.width / 2,
          y: originRect.top + originRect.height / 2,
        }
      : {
          x: containerRect.left
            + result.originCenter.x * currentViewport.scale
            + currentViewport.offset.x,
          y: containerRect.top
            + result.originCenter.y * currentViewport.scale
            + currentViewport.offset.y,
        };

    if (focusRootId && contextNodeIds.has(result.id)) {
      updateFocusView(null, { fit: false });
    }
    setSelectedId(result.id);
    setContextMenu((current) => (
      current.visible ? { visible: false, nodeId: null, x: 0, y: 0 } : current
    ));

    const positionDestination = (attempt = 0) => {
      const destinationRect = nodeRefs.current[result.id]?.getBoundingClientRect?.();
      if (!destinationRect && attempt < 5) {
        directionalNavigationFrameRef.current = window.requestAnimationFrame(
          () => positionDestination(attempt + 1)
        );
        return;
      }
      directionalNavigationFrameRef.current = null;
      if (!destinationRect) return;
      animateViewportPan({
        x: originScreenPoint.x - (destinationRect.left + destinationRect.width / 2),
        y: originScreenPoint.y - (destinationRect.top + destinationRect.height / 2),
      });
      directionalNavigationCorrectionTimerRef.current = window.setTimeout(() => {
        directionalNavigationCorrectionTimerRef.current = null;
        const settledDestination = nodeRefs.current[result.id]?.getBoundingClientRect?.();
        const destinationElement = document.getElementById(getIdeaDomId(result.id));
        if (
          !settledDestination
          || destinationElement?.dataset?.nodeSelected !== 'true'
        ) return;
        const correction = {
          x: originScreenPoint.x
            - (settledDestination.left + settledDestination.width / 2),
          y: originScreenPoint.y
            - (settledDestination.top + settledDestination.height / 2),
        };
        if (Math.abs(correction.x) < 0.5 && Math.abs(correction.y) < 0.5) return;
        const settledViewport = creationViewportStateRef.current;
        creationSetViewportRef.current?.({
          offset: {
            x: settledViewport.offset.x + correction.x,
            y: settledViewport.offset.y + correction.y,
          },
        });
      }, DIRECTIONAL_NAVIGATION_CORRECTION_MS);
      containerRef.current?.focus?.({ preventScroll: true });
    };
    directionalNavigationFrameRef.current = window.requestAnimationFrame(
      () => positionDestination()
    );
    return true;
  }, [
    animateViewportPan,
    cancelViewportPanAnimation,
    contextNodeIds,
    directionalNavigationIndex,
    focusRootId,
    getSelectedNode,
    getViewport,
    setSelectedId,
    updateFocusView,
  ]);

  const focusPath = useMemo(() => {
    if (!focusRootId) return [];
    const nodesById = new Map(nodes.map((node) => [node.id, node]));
    return getNodePath(focusRootId, relationships)
      .map((nodeId) => nodesById.get(nodeId))
      .filter((node) => node && !canvasExcludedNodeIds.has(node.id));
  }, [canvasExcludedNodeIds, focusRootId, nodes, relationships]);

  const navigateFocusHistory = useCallback((direction) => {
    const nextHistory = stepFocusHistory(focusHistory, direction);
    if (nextHistory === focusHistory) return;
    setFocusHistory(nextHistory);
    updateFocusView(currentFocusId(nextHistory), { record: false });
  }, [focusHistory, updateFocusView]);

  useEffect(() => {
    if (!onRegisterExitFocusedBranch) return undefined;
    onRegisterExitFocusedBranch(() => updateFocusView(null));
    return () => onRegisterExitFocusedBranch(null);
  }, [onRegisterExitFocusedBranch, updateFocusView]);

  const handleSearchResult = useCallback((nodeId) => {
    const node = getNode(nodeId);
    if (!node) return;
    cancelViewportPanAnimation();
    const ancestors = getAncestorNodeIds(nodeId, relationships);
    setTemporarilyExpandedNodeIds(new Set(
      [...ancestors].filter((ancestorId) => collapsedNodeIds.has(ancestorId))
    ));

    if (focusRootId && !getBranchNodeIds(focusRootId, relationships).has(nodeId)) {
      updateFocusView(null, { fit: false });
    }
    setSelectedId(nodeId);
    closeMapSearch({ blur: true });
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => centerNodePrecise(nodeId));
      });
    }
  }, [
    centerNodePrecise,
    cancelViewportPanAnimation,
    collapsedNodeIds,
    closeMapSearch,
    focusRootId,
    getNode,
    relationships,
    setSelectedId,
    updateFocusView,
  ]);

  const selectActiveMapSearchResult = useCallback(() => {
    const result = searchResults[searchActiveIndex];
    if (result) handleSearchResult(result.node.id);
  }, [handleSearchResult, searchActiveIndex, searchResults]);

  // Center the initial view on the root node once, for new sessions
  // where the viewport is still at its default (scale 1, offset {0,0}).
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!nodes.length) return;
    if (didRestoreViewport) return;

    const rootId = root || (nodes[0] && nodes[0].id);
    if (!rootId) return;
    const viewportRootKey = `${viewportStorageKey || 'anonymous'}:${rootId}`;
    if (centeredInitialViewportKeyRef.current === viewportRootKey) return;

    centerNodePrecise(rootId);
    centeredInitialViewportKeyRef.current = viewportRootKey;
  }, [nodes, root, centerNodePrecise, didRestoreViewport, viewportStorageKey]);

  const getCurrentHistoryState = useCallback(() => ({
    nodes,
    relationships,
    root,
    selectedId,
    viewport: typeof getViewport === 'function' ? getViewport() : null,
  }), [nodes, relationships, root, selectedId, getViewport]);

  // Helper function to save current state to undo stack
  const saveCurrentState = useCallback(() => {
    // When logged in, structural state is owned by Yjs and we use
    // its UndoManager. Skip local snapshots to avoid conflicts.
    if (isLoggedIn) return;

    const currentState = getCurrentHistoryState();
    const currentStateStr = JSON.stringify(currentState);

    if (lastSavedStateRef.current !== currentStateStr) {
      console.log('Saving current state to undo stack');
      saveVersion(currentState);
      lastSavedStateRef.current = currentStateStr;
    }
  }, [getCurrentHistoryState, saveVersion, isLoggedIn]);

  // Stable callback so node size-change notifications don't create
  // a render loop by changing on every render.
  const handleNodeSizeChange = useCallback((nodeId, size, options = {}) => {
    // Selection and editing deliberately expand long content above the map.
    // That overlay must not become canonical layout geometry or invalidate
    // routes/collision placement for surrounding nodes.
    if (options.transient) {
      if (options.editing && nodeId && size) {
        const previous = liveEditingNodeSizesRef.current.get(nodeId);
        liveEditingNodeSizesRef.current.set(nodeId, size);
        if (previous?.width !== size.width || previous?.height !== size.height) {
          setLayoutVersion((version) => version + 1);
        }
        revealCreatedNode(nodeId);
      }
      return;
    }
    if (nodeId && size) measuredNodeSizesRef.current.set(nodeId, size);
    setLayoutVersion(v => v + 1);
  }, [revealCreatedNode]);

  // When switching to a different mindmap, allow the initial centering effect
  // (or stored viewport) to run again for the new document.
  useEffect(() => {
    if (!mindmapIdForYjs) return;
    if (prevMindmapIdRef.current === mindmapIdForYjs) return;

    const isInitialMapBinding = prevMindmapIdRef.current === null;
    prevMindmapIdRef.current = mindmapIdForYjs;
    if (!isInitialMapBinding) {
      pendingAutoFocusNodeIdRef.current = null;
      pendingCreationRevealRef.current = null;
      if (
        creationPlacementFrameRef.current !== null
        && typeof window !== 'undefined'
      ) {
        window.cancelAnimationFrame(creationPlacementFrameRef.current);
        creationPlacementFrameRef.current = null;
      }
      if (
        creationRevealFrameRef.current !== null
        && typeof window !== 'undefined'
      ) {
        window.cancelAnimationFrame(creationRevealFrameRef.current);
        creationRevealFrameRef.current = null;
      }
      if (
        directionalNavigationFrameRef.current !== null
        && typeof window !== 'undefined'
      ) {
        window.cancelAnimationFrame(directionalNavigationFrameRef.current);
        directionalNavigationFrameRef.current = null;
      }
      if (
        directionalNavigationCorrectionTimerRef.current !== null
        && typeof window !== 'undefined'
      ) {
        window.clearTimeout(directionalNavigationCorrectionTimerRef.current);
        directionalNavigationCorrectionTimerRef.current = null;
      }
    }
    centeredInitialViewportKeyRef.current = null;
    urlNodeHandledRef.current = false;
    setFocusRootId(null);
    setFocusHistory(createFocusHistory());
    setTemporarilyExpandedNodeIds(new Set());
    setIsSearchOpen(false);
  }, [mindmapIdForYjs]);

  // Helper to perform an immediate persistence of the current state.
  // Optionally accepts an override `{ nodeId, text }` so we can
  // persist the exact text the user finished typing for a given
  // node, even if React state updates haven't fully flushed yet.
  const persistNow = useCallback((override) => {
    if (!hasLoadedRef.current) return;
    if (!nodes.length) return;

    let snapshotNodes = nodes;
    if (override && override.nodeId) {
      snapshotNodes = nodes.map((n) =>
        n.id === override.nodeId ? { ...n, text: override.text } : n
      );
    }

    const currentState = { nodes: snapshotNodes, relationships, root, selectedId };
    const currentStateStr = JSON.stringify(currentState);

    if (lastPersistedStateRef.current !== currentStateStr) {
      lastPersistedStateRef.current = currentStateStr;
      // Pass the exact snapshot we just computed so persistence
      // cannot lag behind what the user sees on screen.
      saveMindMap(currentState);
    }
  }, [nodes, relationships, root, selectedId, saveMindMap]);

  // Helper to request a save; actual persistence happens in an effect
  const saveIfChanged = useCallback(() => {
    const now = Date.now();
    // Debounce save requests so a burst of changes (e.g. during
    // login tag updates or fast edits) collapses into a single
    // persistence operation.
    if (now - lastSaveRequestRef.current < 500) {
      return;
    }
    lastSaveRequestRef.current = now;
    setSaveTrigger(t => t + 1);
  }, []);

  // Track whether there are changes in memory that haven't been
  // captured in the last persisted snapshot yet. This lets us
  // warn the user before closing the tab if a save is still
  // pending in memory.
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    const currentStateStr = JSON.stringify({ nodes, relationships, root, selectedId });
    hasUnsavedChangesRef.current = currentStateStr !== lastPersistedStateRef.current;
  }, [nodes, relationships, root, selectedId]);

  // Helper function to calculate distance between two points
  const getDistance = useCallback((pos1, pos2) => {
    if (!pos1 || !pos2) return Infinity;
    const x1 = typeof pos1.x === 'number' ? pos1.x : 0;
    const y1 = typeof pos1.y === 'number' ? pos1.y : 0;
    const x2 = typeof pos2.x === 'number' ? pos2.x : 0;
    const y2 = typeof pos2.y === 'number' ? pos2.y : 0;
    return Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
  }, []);

  // Helper function to get node center position
  const getNodeCenter = useCallback((node) => {
    if (!node) {
      return { x: 0, y: 0 };
    }

    const nodeRef = nodeRefs.current[node.id];
    let width = 200;
    let height = 60; // defaults

    const measuredSize = readNodeLayoutSize(nodeRef, scale);
    if (measuredSize) {
      width = measuredSize.width;
      height = measuredSize.height;
    }

    const baseX = node.position && typeof node.position.x === 'number' ? node.position.x : 0;
    const baseY = node.position && typeof node.position.y === 'number' ? node.position.y : 0;

    return {
      x: baseX + width / 2,
      y: baseY + height / 2,
    };
  }, [scale]);

  // Check if dragged node can be dropped on target (prevent cycles)
  const canReparent = useCallback((draggedId, targetId) => {
    if (draggedId === targetId) return false;

    const descendants = getChildren(draggedId);
    return !descendants.includes(targetId);
  }, [getChildren]);

  // Function to get current children positions for a node
  const getChildrenPositions = useCallback((nodeId) => {
    const childRelations = relationships.filter(rel => rel.parentId === nodeId);
    const children = childRelations.map(rel =>
      nodes.find(child => child.id === rel.childId)
    ).filter(Boolean);

    return children.map(child => ({
      id: child.id,
      x: child.position.x,
      y: child.position.y
    }));
  }, [relationships, nodes]);

  const handleExportImage = useCallback(async () => {
    if (!containerRef.current || typeof window === 'undefined') return;

    try {
      const rect = containerRef.current.getBoundingClientRect();

      const canvas = await html2canvas(document.body, {
        backgroundColor: getThemeCanvasSolidColor(mapTheme, isDarkMode),
        scale: window.devicePixelRatio || 2,
        useCORS: true,
        width: rect.width,
        height: rect.height,
        ignoreElements: (element) => {
          if (!element.dataset) return false;
          if (element.dataset.exportIgnore === 'true') return true;
          const { nodeId, edgeFrom, edgeTo } = element.dataset;
          return Boolean(
            (nodeId && moderationProjection.fadedNodeIds.has(nodeId))
            || (edgeFrom && moderationProjection.fadedNodeIds.has(edgeFrom))
            || (edgeTo && moderationProjection.fadedNodeIds.has(edgeTo))
          );
        },
      });

      const dataUrl = canvas.toDataURL('image/png');

      const titleNode =
        (focusRootId && nodes.find((n) => n.id === focusRootId)) ||
        nodes.find((n) => n.isRoot) ||
        (root && nodes.find((n) => n.id === root));

      const baseNameRaw =
        titleNode && typeof titleNode.text === 'string' && titleNode.text.trim()
          ? titleNode.text.trim()
          : 'mindmap';

      const safeBaseName =
        baseNameRaw
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-_]/g, '')
          .slice(0, 60) || 'mindmap';

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${safeBaseName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export mind map image:', err);
    }
  }, [
    focusRootId,
    isDarkMode,
    mapTheme,
    moderationProjection.fadedNodeIds,
    nodes,
    root,
  ]);

  // Capture current map view and upload as preview thumbnail
  const handleSaveThumbnail = useCallback(async (opts = {}) => {
    const silent = !!(opts && opts.silent);
    try {
      if (!isLoggedIn || !mindmapIdForYjs) {
        if (!silent) showNotification('Sign in to save a preview thumbnail.', 'error');
        return;
      }

      // Optional: only allow owner to upload
      const ownerUserId = remoteMapData && remoteMapData.ownerUserId ? remoteMapData.ownerUserId : null;
      if (ownerUserId && currentUserId && ownerUserId !== currentUserId) {
        if (!silent) showNotification('Only the map owner can update the preview.', 'error');
        return;
      }

      if (!containerRef.current) {
        if (!silent) showNotification('Map container not ready to capture.', 'error');
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();

      const canvas = await html2canvas(document.body, {
        backgroundColor: getThemeCanvasSolidColor(mapTheme, isDarkMode),
        scale: window.devicePixelRatio || 2,
        useCORS: true,
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
        ignoreElements: (element) => {
          if (!element.dataset) return false;
          if (element.dataset.exportIgnore === 'true') return true;
          const { nodeId, edgeFrom, edgeTo } = element.dataset;
          return Boolean(
            (nodeId && moderationProjection.fadedNodeIds.has(nodeId))
            || (edgeFrom && moderationProjection.fadedNodeIds.has(edgeFrom))
            || (edgeTo && moderationProjection.fadedNodeIds.has(edgeTo))
          );
        },
      });

      // Downscale to a max dimension of 512px, preserving aspect ratio
      const srcW = canvas.width;
      const srcH = canvas.height;
      const maxDim = 512;
      const scaleFactor = Math.min(1, maxDim / Math.max(srcW, srcH));
      const outW = Math.max(1, Math.round(srcW * scaleFactor));
      const outH = Math.max(1, Math.round(srcH * scaleFactor));

      const outCanvas = document.createElement('canvas');
      outCanvas.width = outW;
      outCanvas.height = outH;
      const ctx = outCanvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(canvas, 0, 0, srcW, srcH, 0, 0, outW, outH);

      // Encode as PNG to match backend expectations
      const dataUrl = outCanvas.toDataURL('image/png');

      await uploadMindMapThumbnail(mindmapIdForYjs, dataUrl);
      if (!silent) showNotification('Preview thumbnail saved.', 'success');
    } catch (err) {
      console.error('Failed to save preview thumbnail:', err);
      if (!silent) showNotification('Could not save thumbnail. Try again.', 'error');
    }
  }, [
    currentUserId,
    isDarkMode,
    isLoggedIn,
    mapTheme,
    mindmapIdForYjs,
    moderationProjection.fadedNodeIds,
    remoteMapData,
    showNotification,
  ]);

  // Auto-capture every 30s when owner is viewing, only if content changed
  const lastThumbnailSignatureRef = useRef(null);

  const computeThumbnailSignature = useCallback(() => {
    try {
      const normNodes = (nodes || [])
        .map((n) => ({
          id: n.id,
          text: typeof n.text === 'string' ? n.text : '',
          x: n.position && typeof n.position.x === 'number' ? n.position.x : 0,
          y: n.position && typeof n.position.y === 'number' ? n.position.y : 0,
          color: n.color || null,
          isRoot: !!n.isRoot,
        }))
        .sort((a, b) => String(a.id).localeCompare(String(b.id)));

      const normRels = (relationships || [])
        .map((r) => ({ parentId: r.parentId, childId: r.childId }))
        .sort((a, b) =>
          String(a.parentId).localeCompare(String(b.parentId)) ||
          String(a.childId).localeCompare(String(b.childId))
        );

      return JSON.stringify({
        nodes: normNodes,
        relationships: normRels,
        root: root || null,
        moderationRoots: moderationRootKey,
      });
    } catch {
      return null;
    }
  }, [moderationRootKey, nodes, relationships, root]);

  useEffect(() => {
    if (!isLoggedIn || !mindmapIdForYjs) return;
    // Only the owner auto-captures to prevent competing uploads
    const ownerUserId = remoteMapData && remoteMapData.ownerUserId ? remoteMapData.ownerUserId : null;
    if (ownerUserId && currentUserId && ownerUserId !== currentUserId) return;

    let timerId = null;
    const tick = async () => {
      try {
        if (typeof document !== 'undefined' && document.hidden) return;
        if (!isInitialized) return;

        const sig = computeThumbnailSignature();
        if (!sig) return;
        if (sig !== lastThumbnailSignatureRef.current) {
          lastThumbnailSignatureRef.current = sig;
          await handleSaveThumbnail({ silent: true });
        }
      } catch (e) {
        // swallow errors to keep interval alive
        console.warn('Auto-capture tick failed:', e);
      }
    };

    // Kick off after a short delay so initial render settles
    timerId = setInterval(tick, 30000);
    // Run one initial tick after initialization
    const initId = setTimeout(tick, 3000);
    return () => {
      clearInterval(timerId);
      clearTimeout(initId);
    };
  }, [isLoggedIn, mindmapIdForYjs, remoteMapData, currentUserId, isInitialized, computeThumbnailSignature, handleSaveThumbnail]);

  const handleExportData = useCallback(() => {
    if (
      !isLoggedIn
      || !nodeBranchModeration.isReady
      || !yActions
      || !yActions.exportSnapshot
    ) return;

    try {
      const snapshot = yActions.exportSnapshot();
      if (!snapshot || !Array.isArray(snapshot.nodes)) return;

      // Project the exact Yjs snapshot being exported. React's rendered graph can
      // legitimately trail a newly received relationship by one render.
      const snapshotModerationProjection = deriveNodeBranchModerationProjection({
        relationships: snapshot.relationships || [],
        branches: nodeBranchModeration.branches,
        currentUserId,
        accessRole,
      });
      const exportGraph = filterModeratedGraph(
        snapshot.nodes,
        snapshot.relationships || [],
        snapshotModerationProjection.excludedNodeIds
      );
      const exportRoot = exportGraph.nodes.some((node) => node.id === snapshot.root)
        ? snapshot.root
        : null;

      const state = new MindMapState({
        nodes: exportGraph.nodes,
        relationships: exportGraph.relationships,
        root: exportRoot,
        selectedId: exportRoot,
      });

      const titleNode =
        (focusRootId && nodes.find((n) => n.id === focusRootId)) ||
        nodes.find((n) => n.isRoot) ||
        (root && nodes.find((n) => n.id === root));

      const baseNameRaw =
        titleNode && typeof titleNode.text === 'string' && titleNode.text.trim()
          ? titleNode.text.trim()
          : 'mindmap';

      const safeBaseName =
        baseNameRaw
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-_]/g, '')
          .slice(0, 60) || 'mindmap';

      FileExporter.exportToFile(state, EXPORT_FORMATS.JSON, {
        filename: safeBaseName,
        includeMetadata: true,
        pretty: true,
      });
    } catch (err) {
      console.error('Failed to export mind map data:', err);
    }
  }, [
    accessRole,
    currentUserId,
    focusRootId,
    isLoggedIn,
    nodeBranchModeration.branches,
    nodeBranchModeration.isReady,
    nodes,
    root,
    yActions,
  ]);

  const handleImportData = useCallback(async () => {
    if (!isLoggedIn || !yActions) return;

    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,.xml,.csv,.md,.markdown,.txt';

      input.onchange = async () => {
        const file = input.files && input.files[0];
        if (!file) return;

        try {
          const validation = FileImporter.validateFile(file);
          if (!validation.isValid) {
            console.warn('Import file validation failed:', validation.errors);
            return;
          }

          const importedState = await FileImporter.importFromFile(file);
          if (!importedState) return;

          const plain = importedState.toJSON();
          const nodesArray = Array.isArray(plain.nodes) ? plain.nodes : [];
          const relsArray = Array.isArray(plain.relationships) ? plain.relationships : [];
          const rootId = plain.root || null;

          // Clear existing document and apply imported content
          if (yActions.replaceDocument) {
            yActions.replaceDocument({
              nodes: nodesArray,
              relationships: relsArray,
              root: rootId,
            });
          } else if (yActions.addNode && yActions.deleteNode && yActions.addRelationship && yActions.setRoot) {
            // Fallback: naive replace by deleting all current nodes then inserting imported ones
            if (Array.isArray(nodes)) {
              nodes.forEach((n) => {
                if (n && n.id) yActions.deleteNode(n.id);
              });
            }

            nodesArray.forEach((node) => {
              if (!node || !node.id) return;
              yActions.addNode(node);
            });

            relsArray.forEach((rel) => {
              if (!rel || !rel.parentId || !rel.childId) return;
              yActions.addRelationship(rel.parentId, rel.childId);
            });

            if (rootId) {
              yActions.setRoot(rootId);
            }
          }
        } catch (err) {
          console.error('Failed to import mind map data:', err);
        }
      };

      input.click();
    } catch (err) {
      console.error('Failed to start import:', err);
    }
  }, [isLoggedIn, yActions, nodes]);

  const exportDataHandlerRef = useRef(handleExportData);
  const importDataHandlerRef = useRef(handleImportData);
  const setMapThemeRef = useRef(setMapTheme);
  const setMapConnectionStyleRef = useRef(setMapConnectionStyle);
  exportDataHandlerRef.current = handleExportData;
  importDataHandlerRef.current = handleImportData;
  setMapThemeRef.current = setMapTheme;
  setMapConnectionStyleRef.current = setMapConnectionStyle;

  useEffect(() => {
    if (typeof onRegisterMapFileActions !== 'function') return undefined;

    onRegisterMapFileActions({
      exportCurrentMap: isLoggedIn
        ? () => exportDataHandlerRef.current()
        : null,
      importIntoCurrentMap: isLoggedIn && canWriteMap
        ? () => importDataHandlerRef.current()
        : null,
      mapTheme: {
        activeThemeId,
        canChangeTheme: canManageMapMetadata,
        setTheme: (themeId) => setMapThemeRef.current(themeId),
      },
      connectionStyle: {
        activeStyleId: activeConnectionStyleId,
        canChangeStyle: canManageMapMetadata,
        setStyle: (styleId) => setMapConnectionStyleRef.current(styleId),
      },
      appearance: {
        isDarkMode,
        toggleDarkMode,
      },
      ideaInbox: {
        count: ideaInbox.count,
        mode: ideaInboxMode,
        mobileView: mobileWorkspaceView,
        open: () => {
          updateIdeaInboxMode('expanded');
          if (isMobileViewport) setMobileWorkspaceView('ideas');
        },
        hide: () => updateIdeaInboxMode('hidden'),
      },
      openSearch: openMapSearch,
      search: {
        query: searchQuery,
        isOpen: isSearchOpen,
        results: searchResults,
        activeIndex: searchActiveIndex,
        open: openMapSearch,
        close: closeMapSearch,
        setQuery: updateMapSearchQuery,
        setActiveIndex: setSearchActiveIndex,
        moveActive: moveMapSearchSelection,
        selectActive: selectActiveMapSearchResult,
        selectResult: handleSearchResult,
      },
    });

    return () => onRegisterMapFileActions(null);
  }, [
    activeConnectionStyleId,
    activeThemeId,
    canManageMapMetadata,
    canWriteMap,
    closeMapSearch,
    handleSearchResult,
    isDarkMode,
    ideaInbox.count,
    ideaInboxMode,
    isMobileViewport,
    isLoggedIn,
    isSearchOpen,
    moveMapSearchSelection,
    onRegisterMapFileActions,
    openMapSearch,
    searchActiveIndex,
    searchQuery,
    searchResults,
    selectActiveMapSearchResult,
    toggleDarkMode,
    updateMapSearchQuery,
    updateIdeaInboxMode,
    mobileWorkspaceView,
  ]);

  const getRuntimeNodeSizes = useCallback(() => {
    const sizes = new Map(measuredNodeSizesRef.current);
    Object.entries(nodeRefs.current).forEach(([nodeId, element]) => {
      const size = readNodeLayoutSize(element, scale);
      if (size) sizes.set(nodeId, size);
    });
    return sizes;
  }, [scale]);

  const executeMoveNodesCommand = useCallback((command, options = {}) => {
    if (!command) return null;
    if (isLoggedIn && yActions?.moveNodes) {
      return yActions.moveNodes(command, {
        lastEditedBy: currentUserId,
        isolate: !!options.isolate,
      });
    }
    return applyMoveNodesCommand(command);
  }, [applyMoveNodesCommand, currentUserId, isLoggedIn, yActions]);

  const queueMoveNodesCommand = useCallback((command) => {
    const frameState = dragFrameRef.current;
    frameState.pendingCommand = command;
    if (frameState.rafId !== null) return;

    const requestFrame = typeof window !== 'undefined' && window.requestAnimationFrame
      ? window.requestAnimationFrame.bind(window)
      : (callback) => setTimeout(callback, 0);
    frameState.rafId = requestFrame(() => {
      frameState.rafId = null;
      const pending = frameState.pendingCommand;
      frameState.pendingCommand = null;
      if (pending) executeMoveNodesCommand(pending);
    });
  }, [executeMoveNodesCommand]);

  const clearQueuedMoveCommand = useCallback(() => {
    const frameState = dragFrameRef.current;
    if (frameState.rafId !== null) {
      if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(frameState.rafId);
      } else {
        clearTimeout(frameState.rafId);
      }
    }
    frameState.rafId = null;
    frameState.pendingCommand = null;
  }, []);

  useEffect(() => () => clearQueuedMoveCommand(), [clearQueuedMoveCommand]);

  const finishReleaseSettlement = useCallback((generation) => {
    const pending = releaseSettlementRef.current;
    if (generation !== pending.generation) return;
    const timing = releaseSettlementTimingRef.current;
    if (timing.generation === generation && timing.scheduledAt !== null) {
      const finishedAt = window.performance?.now?.() ?? Date.now();
      setReleaseSettlementMetrics((previous) => ({
        ...previous,
        completedRequestId: timing.requestId,
        durationMs: Math.max(0, finishedAt - timing.scheduledAt),
      }));
    }
    const finalize = pending.finalize;
    pending.finalize = null;
    releaseSettlementPendingRef.current = false;
    setReleaseSettlementStatus('idle');
    setDraggedNodeId(null);
    setHighlightedNodeId(null);
    finalize?.();
  }, []);

  const cancelReleaseSettlement = useCallback((reason = 'cancelled') => {
    const pending = releaseSettlementRef.current;
    pending.generation += 1;
    if (pending.frameId !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(pending.frameId);
    }
    if (pending.timerId !== null && typeof window !== 'undefined') {
      window.clearTimeout(pending.timerId);
    }
    pending.frameId = null;
    pending.timerId = null;
    releaseSettlementTimingRef.current = {
      ...releaseSettlementTimingRef.current,
      generation: pending.generation,
      scheduledAt: null,
    };
    releaseSettlementControllerRef.current?.cancel(reason);
    const finalize = pending.finalize;
    pending.finalize = null;
    releaseSettlementPendingRef.current = false;
    setReleaseSettlementStatus('idle');
    finalize?.();
  }, []);

  useEffect(() => {
    const activeDraggedNodeId = dragGestureRef.current?.nodeId || draggedNodeId;
    if (
      !activeDraggedNodeId
      || !canvasExcludedNodeIds.has(activeDraggedNodeId)
    ) return;

    clearQueuedMoveCommand();
    cancelReleaseSettlement('moderated-while-dragging');
    yActions?.endInteraction?.();
    dragGestureRef.current = null;
    rootPanDragRef.current = null;
    reparentTargetRef.current = null;
    setDraggedNodeId(null);
    setHighlightedNodeId(null);
    setDragPositionPreview(new Map());
    setReparentCandidate({
      visible: false,
      draggedNode: null,
      targetNode: null,
      position: { x: 0, y: 0 },
    });
  }, [
    cancelReleaseSettlement,
    clearQueuedMoveCommand,
    draggedNodeId,
    canvasExcludedNodeIds,
    yActions,
  ]);

  const settleReleaseInWorker = useCallback(async (command, options) => {
    const outcome = await releaseSettlementControllerRef.current?.settle({ command, options });
    if (!outcome) return null;
    if (outcome.status !== 'completed') {
      setReleaseSettlementSource(outcome.reason || 'cancelled');
      return null;
    }
    setReleaseSettlementSource(outcome.source);
    return outcome.result;
  }, []);

  const scheduleReleaseSettlement = useCallback((settle, finalize = null) => {
    cancelReleaseSettlement('superseded');
    const pending = releaseSettlementRef.current;
    pending.generation += 1;
    const generation = pending.generation;
    const scheduledAt = window.performance?.now?.() ?? Date.now();
    const requestId = releaseSettlementTimingRef.current.requestId + 1;
    releaseSettlementTimingRef.current = { generation, requestId, scheduledAt };
    setReleaseSettlementMetrics((previous) => ({
      requestId,
      completedRequestId: previous.completedRequestId,
      feedbackMs: null,
      durationMs: null,
    }));
    pending.finalize = finalize;
    releaseSettlementPendingRef.current = true;
    setReleaseSettlementStatus('scheduled');
    // The pointer gesture is complete. Keep the provisional node preview, but
    // remove drag affordances while the worker calculates the final geometry.
    setDraggedNodeId(null);
    setHighlightedNodeId(null);

    const run = async () => {
      if (generation !== releaseSettlementRef.current.generation) return;
      releaseSettlementRef.current.timerId = null;
      setReleaseSettlementStatus('running');
      try {
        await settle();
      } finally {
        finishReleaseSettlement(generation);
      }
    };

    if (typeof window === 'undefined' || !window.requestAnimationFrame) {
      run();
      return;
    }

    // Let the browser paint the released cursor and hide any drop indicator
    // before cloning the immutable geometry snapshot into the worker.
    releaseSettlementRef.current.frameId = window.requestAnimationFrame(() => {
      releaseSettlementRef.current.frameId = null;
      const paintedAt = window.performance?.now?.() ?? Date.now();
      setReleaseSettlementMetrics((previous) => ({
        ...previous,
        feedbackMs: Math.max(0, paintedAt - scheduledAt),
      }));
      releaseSettlementRef.current.timerId = window.setTimeout(run, 0);
    });
  }, [cancelReleaseSettlement, finishReleaseSettlement]);

  useEffect(() => {
    const pending = releaseSettlementRef.current;
    const controller = createReleaseSettlementController({
      createWorker: createReleaseSettlementWorker,
      getVersion: () => graphRevisionRef.current,
    });
    releaseSettlementControllerRef.current = controller;

    return () => {
      pending.generation += 1;
      if (pending.frameId !== null && typeof window !== 'undefined') {
        window.cancelAnimationFrame(pending.frameId);
      }
      if (pending.timerId !== null && typeof window !== 'undefined') {
        window.clearTimeout(pending.timerId);
      }
      pending.finalize = null;
      controller.dispose();
      if (releaseSettlementControllerRef.current === controller) {
        releaseSettlementControllerRef.current = null;
      }
      releaseSettlementPendingRef.current = false;
    };
  }, []);

  const stopCollisionSlide = useCallback(() => {
    if (layoutTransitionTimerRef.current !== null) {
      clearTimeout(layoutTransitionTimerRef.current);
      layoutTransitionTimerRef.current = null;
    }
    setLayoutTransitionNodeIds(new Set());
  }, []);

  const startCollisionSlide = useCallback((command) => {
    if (
      (!command?.collisionResolved && !command?.settlingResolved)
      || !Array.isArray(command.positions)
    ) {
      stopCollisionSlide();
      return false;
    }
    if (typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      stopCollisionSlide();
      return false;
    }

    if (layoutTransitionTimerRef.current !== null) {
      clearTimeout(layoutTransitionTimerRef.current);
    }
    setLayoutTransitionNodeIds(new Set(command.positions.map((entry) => entry.id)));
    layoutTransitionTimerRef.current = setTimeout(() => {
      layoutTransitionTimerRef.current = null;
      setLayoutTransitionNodeIds(new Set());
    }, COLLISION_SLIDE_MS + 40);
    return true;
  }, [stopCollisionSlide]);

  useEffect(() => () => {
    if (layoutTransitionTimerRef.current !== null) {
      clearTimeout(layoutTransitionTimerRef.current);
    }
  }, []);

  const executeReparentSubtreeCommand = useCallback((command) => {
    if (!command) return null;
    if (isLoggedIn && yActions?.reparentSubtree) {
      return yActions.reparentSubtree(command, { lastEditedBy: currentUserId });
    }
    return applyReparentSubtreeCommand(command);
  }, [applyReparentSubtreeCommand, currentUserId, isLoggedIn, yActions]);

  const screenPointToCanvas = useCallback((point) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    return screenToWorld(point, containerRect, { scale, offset });
  }, [offset, scale]);

  const isPointInsideInboxDropZone = useCallback((point) => {
    if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y) || typeof document === 'undefined') {
      return false;
    }
    return [...document.querySelectorAll('[data-idea-inbox-drop-zone="true"]')].some((element) => {
      if (element.getClientRects().length === 0) return false;
      const rect = element.getBoundingClientRect();
      return point.x >= rect.left && point.x <= rect.right
        && point.y >= rect.top && point.y <= rect.bottom;
    });
  }, []);

  // Handle node drag start
  const handleNodeDragStart = useCallback((nodeId, pointer = null) => {
    // A rerender during one physical gesture can cause a duplicate start
    // callback. Keep one history snapshot and one collaboration interaction
    // boundary for the complete drag/settle command.
    if (dragGestureRef.current?.nodeId === nodeId) return;

    cancelReleaseSettlement('new-drag');

    // Save state at the start of a drag so the whole drag
    // gesture can be undone in a single step
    saveCurrentState();
    stopCollisionSlide();
    setDragPositionPreview(new Map());

    setDraggedNodeId(nodeId);
    setHighlightedNodeId(null);
    setReparentCandidate(prev => ({ ...prev, visible: false }));
    reparentTargetRef.current = null;

    const draggedNode = nodes.find((node) => node.id === nodeId);
    dragGestureRef.current = {
      nodeId,
      hasMoved: false,
      startPosition: draggedNode?.position ? { ...draggedNode.position } : null,
      latestPosition: draggedNode?.position ? { ...draggedNode.position } : null,
      latestClientPoint: Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)
        ? { x: pointer.clientX, y: pointer.clientY }
        : null,
      nodes: nodes.map((node) => ({
        ...node,
        position: node.position ? { ...node.position } : { x: 0, y: 0 },
      })),
      relationships: relationships.map((relationship) => ({ ...relationship })),
    };
    yActions?.beginInteraction?.();

    if (nodeId && nodeId === root && !focusRootId) {
      rootPanDragRef.current = Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)
        ? { x: pointer.clientX, y: pointer.clientY }
        : null;
    }
  }, [
    cancelReleaseSettlement,
    saveCurrentState,
    stopCollisionSlide,
    root,
    focusRootId,
    nodes,
    relationships,
    yActions,
  ]);

  // Function to get parent node for a given node
  const getParentNodeData = useCallback((nodeId) => {
    const parentRelation = relationships.find(rel => rel.childId === nodeId);
    if (!parentRelation) return null;

    const parentNode = nodes.find(node => node.id === parentRelation.parentId);
    if (!parentNode) return null;

    const baseX = parentNode.position && typeof parentNode.position.x === 'number'
      ? parentNode.position.x
      : 0;
    const baseY = parentNode.position && typeof parentNode.position.y === 'number'
      ? parentNode.position.y
      : 0;

    return {
      id: parentNode.id,
      x: baseX,
      y: baseY,
      text: parentNode.text,
    };
  }, [relationships, nodes]);

  // Handle node drag move
  const handleNodeDragMove = useCallback((nodeId, position, pointer = null) => {
    if (!draggedNodeId || draggedNodeId !== nodeId) return;

    const draggedNode = getNode(draggedNodeId);
    if (!draggedNode) return;

    if (dragGestureRef.current?.nodeId === nodeId) {
      const gesture = dragGestureRef.current;
      if (
        gesture.startPosition
        && Math.hypot(
          position.x - gesture.startPosition.x,
          position.y - gesture.startPosition.y
        ) > 0.01
      ) {
        gesture.hasMoved = true;
      }
      gesture.latestPosition = { ...position };
      if (Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)) {
        gesture.latestClientPoint = { x: pointer.clientX, y: pointer.clientY };
      }
    }

    setIsInboxDropActive(
      nodeId !== root
      && isPointInsideInboxDropZone({ x: pointer?.clientX, y: pointer?.clientY })
    );

    if (nodeId === root && !focusRootId) {
      const lastPointer = rootPanDragRef.current;
      if (lastPointer && Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)) {
        panBy(pointer.clientX - lastPointer.x, pointer.clientY - lastPointer.y);
      }
      if (Number.isFinite(pointer?.clientX) && Number.isFinite(pointer?.clientY)) {
        rootPanDragRef.current = { x: pointer.clientX, y: pointer.clientY };
      }
      reparentTargetRef.current = null;
      setHighlightedNodeId(null);
      return;
    }

    if (focusRootId && nodeId === focusRootId) {
      reparentTargetRef.current = null;
      setHighlightedNodeId(null);
      return;
    }

    const runtimeSizes = getRuntimeNodeSizes();
    const draggedSize = runtimeSizes.get(draggedNodeId) || {
      width: NODE_WIDTH_GUESS,
      height: NODE_HEIGHT_GUESS,
    };
    const draggedRect = { ...position, ...draggedSize };
    let newHighlighted = null;
    let potentialTarget = null;
    let closestDistance = Infinity;

    // Select the closest valid node whose rendered rectangle overlaps the
    // dragged node. This remains accurate for large cards and zoom levels.
    for (const node of nodes) {
      if (node.id === draggedNodeId) continue;
      if (!visibleNodeIds.has(node.id)) continue;
      if (!canReparent(draggedNodeId, node.id)) continue;

      const targetSize = runtimeSizes.get(node.id) || {
        width: NODE_WIDTH_GUESS,
        height: NODE_HEIGHT_GUESS,
      };
      const targetRect = { ...node.position, ...targetSize };
      if (!topLeftRectsOverlap(draggedRect, targetRect, 0)) continue;
      const draggedCenter = {
        x: position.x + draggedSize.width / 2,
        y: position.y + draggedSize.height / 2,
      };
      const nodeCenter = getNodeCenter(node);
      const distance = getDistance(draggedCenter, nodeCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        newHighlighted = node.id;
        potentialTarget = node;
      }
    }

    setHighlightedNodeId(newHighlighted);
    reparentTargetRef.current = potentialTarget;

    // Show a compact hierarchy badge above the candidate parent. Releasing
    // commits the reparent; moving away clears the candidate.
    if (potentialTarget && (
      !reparentCandidate.visible || reparentCandidate.targetNode?.id !== potentialTarget.id
    )) {
      const targetSize = runtimeSizes.get(potentialTarget.id) || {
        width: NODE_WIDTH_GUESS,
        height: NODE_HEIGHT_GUESS,
      };
      const indicatorPosition = {
        x: potentialTarget.position.x + targetSize.width / 2,
        y: potentialTarget.position.y - 8,
      };

      setReparentCandidate({
        visible: true,
        draggedNode: draggedNode,
        targetNode: potentialTarget,
        position: indicatorPosition,
      });
    } else if (!potentialTarget && reparentCandidate.visible) {
      setReparentCandidate(prev => ({ ...prev, visible: false }));
    }
  }, [
    draggedNodeId,
    nodes,
    getNode,
    canReparent,
    getNodeCenter,
    getDistance,
    getRuntimeNodeSizes,
    visibleNodeIds,
    reparentCandidate.visible,
    reparentCandidate.targetNode?.id,
    root,
    focusRootId,
    panBy,
    isPointInsideInboxDropZone,
  ]);

  // Handle node drag end
  const handleNodeDragEnd = useCallback(() => {
    if (!releaseSettlementPendingRef.current) {
      setDraggedNodeId(null);
    }
    setHighlightedNodeId(null);
    rootPanDragRef.current = null;
    setIsInboxDropActive(false);
  }, []);

  const commitReparentDrop = useCallback(async (nodeId, newParentId) => {
    // Collapsed descendants remain in the map model at their last positions,
    // but they are not valid interaction targets while hidden.
    if (!visibleNodeIds.has(newParentId)) return false;

    try {
      const graph = latestGraphRef.current;
      const command = buildReparentSubtreeCommand({
        nodeId,
        newParentId,
        nodes: graph.nodes,
        relationships: graph.relationships,
        nodeSizes: getRuntimeNodeSizes(),
      });
      const settled = await settleReleaseInWorker(command, {
        nodes: graph.nodes,
        relationships: graph.relationships,
        nodeSizes: getRuntimeNodeSizes(),
        previousRoutes: previousEdgeRoutesRef.current,
        routedNodeIds: visibleNodeIds,
      });
      if (!settled) return false;
      startCollisionSlide(settled);
      setDragPositionPreview(createDragPositionPreview(settled));
      executeReparentSubtreeCommand(settled);
      return true;
    } catch (error) {
      console.error('Failed to reparent node:', error);
      setDragPositionPreview(new Map());
      showNotification(error.message || 'Could not reparent that idea.', 'error');
      return false;
    }
  }, [
    visibleNodeIds,
    getRuntimeNodeSizes,
    executeReparentSubtreeCommand,
    settleReleaseInWorker,
    startCollisionSlide,
    showNotification,
  ]);

  // Keep the last live position snapshot visible until the underlying local or
  // collaborative graph state reaches it. This avoids a one-frame snap-back at
  // pointer release without allowing stale previews to outlive the gesture.
  useEffect(() => {
    if (draggedNodeId || reparentCandidate.visible) return;
    setDragPositionPreview((current) => (
      current.size > 0 && dragPreviewMatchesNodes(current, nodes) ? new Map() : current
    ));
  }, [draggedNodeId, nodes, reparentCandidate.visible]);

  // Action handlers with undo state management
  const executeCreateNodeCommand = useCallback((command, options = {}) => {
    if (!command) return null;
    saveCurrentState();
    if (options.reveal !== false) {
      pendingCreationRevealRef.current = {
        nodeId: command.node.id,
        finalize: !command.node.autoFocus,
      };
    }

    if (isLoggedIn && yActions && typeof yActions.createNodeWithRelationship === 'function') {
      if (command.node.autoFocus) {
        pendingAutoFocusNodeIdRef.current = command.node.id;
      }
      yActions.createNodeWithRelationship(command);
    } else {
      insertNodeWithRelationship(command);
      saveIfChanged();
    }

    expandBranch(command.relationship.parentId);
    if (options.select !== false) setSelectedId(command.node.id);
    return command.node.id;
  }, [
    isLoggedIn,
    yActions,
    insertNodeWithRelationship,
    saveCurrentState,
    saveIfChanged,
    expandBranch,
    setSelectedId,
  ]);

  const handleAddChildNode = useCallback((parentIdOverride = null, preferredPosition = null) => {
    const explicitParentId = typeof parentIdOverride === 'string' ? parentIdOverride : null;
    const base = (explicitParentId && getNode(explicitParentId))
      || getSelectedNode()
      || (root && getNode(root))
      || nodes.find((node) => node.isRoot);
    if (!base) return null;

    const command = buildCreateChildCommand({
      id: crypto.randomUUID(),
      parentId: base.id,
      nodes,
      relationships,
      preferredPosition,
      size: DEFAULT_NEW_NODE_SIZE,
      nodeSizes: getRuntimeNodeSizes(),
      usableViewport: readCreationViewportGeometry()?.constraint || null,
      color: base.color || COLORS[0]?.gradient || null,
      userId: currentUserId || null,
      creatorName: currentUserName || null,
    });

    return executeCreateNodeCommand(command);
  }, [
    getSelectedNode,
    getNode,
    root,
    nodes,
    relationships,
    currentUserId,
    currentUserName,
    getRuntimeNodeSizes,
    readCreationViewportGeometry,
    executeCreateNodeCommand,
  ]);

  const handleCaptureIdea = useCallback(async (text, targetId = null) => {
    if (!canWriteMap) {
      showNotification('You have view-only access to this map.', 'error');
      return false;
    }
    try {
      if (targetId) {
        const target = getNode(targetId);
        if (!target || ideaInbox.hiddenNodeIds.has(targetId)) {
          throw new Error('The selected destination is no longer available');
        }
        const command = buildCaptureChildIdeaCommand({
          id: crypto.randomUUID(),
          parentId: targetId,
          text,
          nodes: canvasNodes,
          relationships: canvasRelationships,
          size: DEFAULT_NEW_NODE_SIZE,
          nodeSizes: getRuntimeNodeSizes(),
          usableViewport: readCreationViewportGeometry()?.constraint || null,
          color: target.color || COLORS[0]?.gradient || null,
          userId: currentUserId || null,
          creatorName: currentUserName || null,
        });
        executeCreateNodeCommand(command, { reveal: false, select: false });
        showNotification(`Idea added under ${target.text || 'the selected idea'}.`, 'success');
        return command.node.id;
      }

      const rootNode = getNode(root) || canvasNodes.find((node) => node.isRoot);
      const command = buildCreateInboxIdeaCommand({
        id: crypto.randomUUID(),
        text,
        color: rootNode?.color || COLORS[0]?.gradient || null,
        userId: currentUserId || null,
        creatorName: currentUserName || null,
      });
      saveCurrentState();
      if (isLoggedIn && yActions?.createInboxIdea) {
        yActions.createInboxIdea(command);
      } else {
        insertInboxIdea(command);
        saveIfChanged();
      }
      showNotification('Idea added to the inbox.', 'success');
      return command.node.id;
    } catch (error) {
      console.error('Failed to capture idea:', error);
      showNotification(error.message || 'Could not add that idea.', 'error');
      return false;
    }
  }, [
    canWriteMap,
    canvasNodes,
    canvasRelationships,
    currentUserId,
    currentUserName,
    executeCreateNodeCommand,
    getNode,
    getRuntimeNodeSizes,
    ideaInbox.hiddenNodeIds,
    insertInboxIdea,
    isLoggedIn,
    readCreationViewportGeometry,
    root,
    saveCurrentState,
    saveIfChanged,
    showNotification,
    yActions,
  ]);

  const handleMoveBranchToInbox = useCallback((nodeId) => {
    if (!canWriteMap) return false;
    try {
      const command = buildMoveBranchToInboxCommand({
        nodeId,
        nodes,
        relationships,
      });
      saveCurrentState();
      if (isLoggedIn && yActions?.moveBranchToInbox) {
        yActions.moveBranchToInbox(command);
      } else {
        applyMoveBranchToInboxCommand(command);
        saveIfChanged();
      }
      if (captureTargetId === nodeId) setCaptureTargetId(null);
      setSelectedId(null);
      showNotification('Branch moved to the idea inbox. Undo is available.', 'success');
      return true;
    } catch (error) {
      console.error('Failed to move branch to inbox:', error);
      showNotification(error.message || 'Could not move that branch to the inbox.', 'error');
      return false;
    }
  }, [
    applyMoveBranchToInboxCommand,
    canWriteMap,
    captureTargetId,
    isLoggedIn,
    nodes,
    relationships,
    saveCurrentState,
    saveIfChanged,
    setSelectedId,
    showNotification,
    yActions,
  ]);

  const handleAttachInboxIdea = useCallback((nodeId, parentId) => {
    if (!canWriteMap) return false;
    try {
      const command = buildAttachInboxBranchCommand({
        nodeId,
        parentId,
        nodes,
        relationships,
        nodeSizes: getRuntimeNodeSizes(),
        usableViewport: readCreationViewportGeometry()?.constraint || null,
      });
      saveCurrentState();
      if (isLoggedIn && yActions?.attachInboxBranch) {
        yActions.attachInboxBranch(command, { lastEditedBy: currentUserId });
      } else {
        applyAttachInboxBranchCommand(command);
        saveIfChanged();
      }
      setSelectedId(nodeId);
      setCaptureTargetId(parentId);
      showNotification('Idea placed in the map. Undo is available.', 'success');
      return true;
    } catch (error) {
      console.error('Failed to place inbox idea:', error);
      showNotification(error.message || 'Could not place that idea.', 'error');
      return false;
    }
  }, [
    applyAttachInboxBranchCommand,
    canWriteMap,
    currentUserId,
    getRuntimeNodeSizes,
    isLoggedIn,
    nodes,
    readCreationViewportGeometry,
    relationships,
    saveCurrentState,
    saveIfChanged,
    setSelectedId,
    showNotification,
    yActions,
  ]);

  const handleDeleteInboxIdea = useCallback((nodeId) => {
    try {
      if (!ideaInbox.hiddenNodeIds.has(nodeId)) return false;
      const command = buildDeleteInboxBranchCommand({
        nodeId,
        nodes,
        relationships,
        actorUserId: currentUserId || null,
      });
      saveCurrentState();
      if (isLoggedIn && yActions?.deleteSubtree) {
        yActions.deleteSubtree(command);
      } else {
        applyDeleteSubtreeCommand(command);
        saveIfChanged();
      }
      showNotification('Inbox idea deleted.', 'success');
      return true;
    } catch (error) {
      console.error('Failed to delete inbox idea:', error);
      showNotification(error.message || 'Could not delete that idea.', 'error');
      return false;
    }
  }, [
    applyDeleteSubtreeCommand,
    ideaInbox.hiddenNodeIds,
    isLoggedIn,
    currentUserId,
    nodes,
    relationships,
    saveCurrentState,
    saveIfChanged,
    showNotification,
    yActions,
  ]);

  const handleAttachmentDragOver = useCallback((event) => {
    if (!canWriteMap) return;
    if (
      inboxHtmlDragNodeIdRef.current
      || event.dataTransfer?.types?.includes(INBOX_DRAG_TYPE)
    ) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
        inboxHtmlDragPointRef.current = { x: event.clientX, y: event.clientY };
      }
      return;
    }
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [canWriteMap]);

  const nodeIdAtClientPoint = useCallback((clientX, clientY) => {
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
    const candidates = Object.entries(nodeRefs.current)
      .map(([nodeId, element]) => ({ nodeId, rect: element?.getBoundingClientRect?.() }))
      .filter(({ nodeId, rect }) => (
        rect
        && visibleNodeIds.has(nodeId)
        && clientX >= rect.left
        && clientX <= rect.right
        && clientY >= rect.top
        && clientY <= rect.bottom
      ))
      .sort((left, right) => (
        left.rect.width * left.rect.height - right.rect.width * right.rect.height
      ));
    return candidates[0]?.nodeId || null;
  }, [visibleNodeIds]);

  const handleAttachmentDrop = useCallback(async (event) => {
    const plainDragData = event.dataTransfer?.getData?.('text/plain') || '';
    const inboxNodeId = event.dataTransfer?.getData?.(INBOX_DRAG_TYPE)
      || inboxHtmlDragNodeIdRef.current
      || (plainDragData.startsWith('mindmappi-inbox:')
        ? plainDragData.slice('mindmappi-inbox:'.length)
        : '');
    if (inboxNodeId) {
      event.preventDefault();
      event.stopPropagation();
      inboxHtmlDragNodeIdRef.current = null;
      const dropTarget = event.target?.closest?.('[data-node-id], [data-node-drop-id]');
      const dropPoint = (
        Number.isFinite(event.clientX)
        && Number.isFinite(event.clientY)
        && (event.clientX !== 0 || event.clientY !== 0)
      ) ? { x: event.clientX, y: event.clientY } : inboxHtmlDragPointRef.current;
      const parentId = dropTarget?.dataset?.nodeId
        || dropTarget?.dataset?.nodeDropId
        || nodeIdAtClientPoint(dropPoint?.x, dropPoint?.y)
        || (selectedId && visibleNodeIds.has(selectedId) ? selectedId : null);
      inboxHtmlDragPointRef.current = null;
      if (!parentId) {
        showNotification('Drop the inbox idea onto an idea in the map.', 'error');
        return;
      }
      handleAttachInboxIdea(inboxNodeId, parentId);
      return;
    }
    if (!event.dataTransfer?.types?.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    const entries = attachmentFilesFromTransfer(event.dataTransfer);
    if (!entries.length) {
      showNotification('Drop a PNG, JPEG, WebP, SVG, or .md file.', 'error');
      return;
    }
    if (!canWriteMap || !mindmapIdForYjs || !currentUserId) {
      showNotification('Sign in with edit access to attach files.', 'error');
      return;
    }
    const dropTarget = event.target?.closest?.('[data-node-id], [data-node-drop-id]');
    const targetNodeId = dropTarget?.dataset?.nodeId
      || dropTarget?.dataset?.nodeDropId
      || nodeIdAtClientPoint(event.clientX, event.clientY);
    const parentId = attachmentDropParentId({
      targetNodeId,
      selectedNodeId: selectedId,
      rootNodeId: root,
    });
    const parent = getNode(parentId);
    if (!parent) {
      showNotification('Select an idea before attaching a file.', 'error');
      return;
    }
    const mapDropPosition = targetNodeId
      ? null
      : screenPointToCanvas({ x: event.clientX, y: event.clientY });
    let planningNodes = [...nodes];
    let planningRelationships = [...relationships];
    let completed = 0;
    let importedIdeas = 0;
    const pendingUploads = entries.map((entry) => ({
      id: crypto.randomUUID(),
      filename: entry.file.name,
    }));
    setPendingAttachmentUploads((current) => [...current, ...pendingUploads]);
    for (const [index, entry] of entries.entries()) {
      const pendingUpload = pendingUploads[index];
      try {
        const importedBranch = entry.kind === 'markdown'
          ? parseMindmappiBranchMarkdown(await entry.file.text())
          : null;
        if (importedBranch) {
          const importedNodeIds = [];
          importedBranch.forEach((idea, ideaIndex) => {
            const importedParentId = idea.parentIndex === null
              ? parentId
              : importedNodeIds[idea.parentIndex];
            const command = buildCreateChildCommand({
              id: crypto.randomUUID(),
              parentId: importedParentId,
              nodes: planningNodes,
              relationships: planningRelationships,
              preferredPosition: mapDropPosition && ideaIndex === 0 ? mapDropPosition : null,
              size: DEFAULT_NEW_NODE_SIZE,
              nodeSizes: getRuntimeNodeSizes(),
              usableViewport: readCreationViewportGeometry()?.constraint || null,
              color: parent.color || COLORS[0]?.gradient || null,
              userId: currentUserId,
              creatorName: currentUserName || null,
            });
            command.node.text = idea.text;
            command.node.autoFocus = false;
            command.node.isTask = idea.isTask;
            command.node.taskDone = idea.taskDone;
            executeCreateNodeCommand(command);
            importedNodeIds.push(command.node.id);
            planningNodes.push(command.node);
            planningRelationships.push(command.relationship);
          });
          importedIdeas += importedBranch.length;
          continue;
        }
        const asset = await uploadNodeAsset(mindmapIdForYjs, entry.file, entry.kind);
        const command = buildCreateAttachmentCommand({
          id: crypto.randomUUID(),
          parentId,
          nodes: planningNodes,
          relationships: planningRelationships,
          preferredPosition: mapDropPosition && index === 0 ? mapDropPosition : null,
          size: entry.kind === 'image' ? IMAGE_ATTACHMENT_LAYOUT_SIZE : DEFAULT_NEW_NODE_SIZE,
          nodeSizes: getRuntimeNodeSizes(),
          usableViewport: readCreationViewportGeometry()?.constraint || null,
          attachment: asset,
          color: parent.color || COLORS[0]?.gradient || null,
          userId: currentUserId,
          creatorName: currentUserName || null,
        });
        executeCreateNodeCommand(command);
        planningNodes.push(command.node);
        planningRelationships.push(command.relationship);
        completed += 1;
      } catch (error) {
        console.error('Failed to attach dropped file:', error);
        showNotification(error.message || `Could not attach ${entry.file.name}.`, 'error');
      } finally {
        setPendingAttachmentUploads((current) => (
          current.filter((upload) => upload.id !== pendingUpload.id)
        ));
      }
    }
    if (importedIdeas) {
      showNotification(
        `${importedIdeas} ${importedIdeas === 1 ? 'idea' : 'ideas'} imported. The source file was not stored.`,
        'success'
      );
    } else if (completed) {
      showNotification(
        completed === 1 ? 'Attachment added.' : `${completed} attachments added.`,
        'success'
      );
    }
  }, [
    canWriteMap,
    mindmapIdForYjs,
    currentUserId,
    selectedId,
    visibleNodeIds,
    root,
    getNode,
    screenPointToCanvas,
    nodes,
    relationships,
    getRuntimeNodeSizes,
    readCreationViewportGeometry,
    currentUserName,
    executeCreateNodeCommand,
    handleAttachInboxIdea,
    nodeIdAtClientPoint,
    showNotification,
  ]);

  const closeAttachmentViewer = useCallback(() => {
    const current = attachmentViewerRef.current;
    if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
    attachmentViewerRef.current = null;
    setAttachmentViewer(null);
  }, []);

  useEffect(() => {
    if (!nodeBranchModeration.isReady) return;
    const excluded = canvasExcludedNodeIds;
    if (selectedId && excluded.has(selectedId)) {
      setSelectedId(root && !excluded.has(root) ? root : null);
    }
    if (activeInfoNodeId && excluded.has(activeInfoNodeId)) setActiveInfoNodeId(null);
    if (editingNodeId && excluded.has(editingNodeId)) setEditingNodeId(null);
    if (attachmentViewer?.nodeId && excluded.has(attachmentViewer.nodeId)) {
      closeAttachmentViewer();
    }
    if (
      branchMarkdownRequest?.plan
      && (
        excluded.has(branchMarkdownRequest.plan.nodeId)
        || [...(branchMarkdownRequest.plan.branchNodeIds || [])]
          .some((nodeId) => excluded.has(nodeId))
      )
    ) {
      setBranchMarkdownRequest(null);
    }
    if (contextMenu.nodeId && excluded.has(contextMenu.nodeId)) {
      setContextMenu((previous) => ({ ...previous, visible: false }));
    }
    if (activeDiscussionNodeId && excluded.has(activeDiscussionNodeId)) {
      closeActiveDiscussion();
    }
  }, [
    activeInfoNodeId,
    attachmentViewer?.nodeId,
    branchMarkdownRequest?.plan,
    closeAttachmentViewer,
    contextMenu.nodeId,
    editingNodeId,
    canvasExcludedNodeIds,
    nodeBranchModeration.isReady,
    activeDiscussionNodeId,
    closeActiveDiscussion,
    root,
    selectedId,
    setSelectedId,
  ]);

  useEffect(() => () => {
    const current = attachmentViewerRef.current;
    if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
    attachmentViewerRef.current = null;
  }, []);

  const downloadAttachmentBlob = useCallback((blob, attachment) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = attachment?.filename || (attachment?.kind === 'markdown' ? 'attachment.md' : 'image.webp');
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }, []);

  const handleDownloadAttachment = useCallback(async (attachment) => {
    if (!mindmapIdForYjs || !attachment?.assetId) return;
    try {
      const cached = attachmentViewerRef.current;
      const blob = cached?.attachment?.assetId === attachment.assetId && cached.blob
        ? cached.blob
        : await fetchNodeAssetContent(mindmapIdForYjs, attachment.assetId, 'display');
      downloadAttachmentBlob(blob, attachment);
    } catch (error) {
      showNotification(error.message || 'Could not download this attachment.', 'error');
    }
  }, [downloadAttachmentBlob, mindmapIdForYjs, showNotification]);

  const handleOpenAttachment = useCallback(async (attachment, nodeId = null) => {
    if (!mindmapIdForYjs || !attachment?.assetId) return;
    closeAttachmentViewer();
    const loading = {
      attachment,
      nodeId,
      blob: null,
      content: '',
      objectUrl: null,
      isLoading: true,
      error: '',
    };
    attachmentViewerRef.current = loading;
    setAttachmentViewer(loading);
    try {
      const blob = await fetchNodeAssetContent(mindmapIdForYjs, attachment.assetId, 'display');
      if (attachmentViewerRef.current?.attachment?.assetId !== attachment.assetId) return;
      const content = attachment.kind === 'markdown' ? await blob.text() : '';
      if (attachmentViewerRef.current?.attachment?.assetId !== attachment.assetId) return;
      const loaded = {
        attachment,
        nodeId,
        blob,
        content,
        objectUrl: attachment.kind === 'image' ? URL.createObjectURL(blob) : null,
        isLoading: false,
        error: '',
      };
      attachmentViewerRef.current = loaded;
      setAttachmentViewer(loaded);
    } catch (error) {
      if (attachmentViewerRef.current?.attachment?.assetId !== attachment.assetId) return;
      const failed = {
        ...attachmentViewerRef.current,
        isLoading: false,
        error: error.message || 'Could not open this attachment.',
      };
      attachmentViewerRef.current = failed;
      setAttachmentViewer(failed);
    }
  }, [closeAttachmentViewer, mindmapIdForYjs]);

  const handleSaveMarkdownAttachment = useCallback(async (content, filename, description = '') => {
    const current = attachmentViewerRef.current;
    if (
      !current?.attachment?.assetId
      || current.attachment.kind !== 'markdown'
      || !mindmapIdForYjs
    ) {
      throw new Error('This Markdown attachment is no longer available.');
    }
    const targetNodeId = current.nodeId;
    const fileChanged = content !== current.content || filename !== current.attachment.filename;
    const updated = fileChanged
      ? await updateMarkdownNodeAssetContent(
          mindmapIdForYjs,
          current.attachment.assetId,
          content,
          filename
        )
      : null;
    const nextAttachment = {
      ...current.attachment,
      ...(updated
        ? {
            assetId: updated.id,
            kind: updated.kind,
            filename: updated.filename,
            contentType: updated.contentType,
            byteSize: updated.byteSize,
          }
        : {}),
      description: String(description || '').trim(),
    };
    // The private asset revision is authoritative for the filename and bytes.
    // Project the collaborative description (or filename fallback) even if the
    // viewer was closed while the request was in flight.
    if (targetNodeId) {
      if (isLoggedIn && yActions?.updateNode) {
        yActions.updateNode(targetNodeId, {
          text: nextAttachment.description || nextAttachment.filename,
          attachment: nextAttachment,
          lastEditedBy: currentUserId,
        });
      } else {
        setNodes((previous) => previous.map((node) => (
          node.id === targetNodeId
            ? {
                ...node,
                text: nextAttachment.description || nextAttachment.filename,
                attachment: nextAttachment,
              }
            : node
        )));
        saveIfChanged();
      }
    }

    if (attachmentViewerRef.current === current) {
      const nextBlob = fileChanged
        ? new Blob([content], { type: nextAttachment.contentType })
        : current.blob;
      const nextViewer = {
        ...current,
        attachment: nextAttachment,
        blob: nextBlob,
        content,
        error: '',
      };
      attachmentViewerRef.current = nextViewer;
      setAttachmentViewer(nextViewer);
    }
    return nextAttachment;
  }, [
    currentUserId,
    isLoggedIn,
    mindmapIdForYjs,
    saveIfChanged,
    setNodes,
    yActions,
  ]);

  const handleAddSiblingNode = useCallback((selectedNodeIdOverride = null) => {
    const explicitNodeId = typeof selectedNodeIdOverride === 'string' ? selectedNodeIdOverride : null;
    const selectedNode = (explicitNodeId && getNode(explicitNodeId)) || getSelectedNode();
    if (!selectedNode) return null;

    try {
      const command = buildCreateSiblingCommand({
        id: crypto.randomUUID(),
        selectedNodeId: selectedNode.id,
        nodes,
        relationships,
        size: DEFAULT_NEW_NODE_SIZE,
        nodeSizes: getRuntimeNodeSizes(),
        usableViewport: readCreationViewportGeometry()?.constraint || null,
        color: selectedNode.color || COLORS[0]?.gradient || null,
        userId: currentUserId || null,
        creatorName: currentUserName || null,
      });
      return executeCreateNodeCommand(command);
    } catch (error) {
      // Root nodes intentionally cannot have siblings.
      showNotification(error.message, 'error');
      return null;
    }
  }, [
    getSelectedNode,
    getNode,
    nodes,
    relationships,
    currentUserId,
    currentUserName,
    getRuntimeNodeSizes,
    readCreationViewportGeometry,
    executeCreateNodeCommand,
    showNotification,
  ]);

  const executeDeleteSubtreeCommand = useCallback((command) => {
    if (!command) return false;
    saveCurrentState();
    if (isLoggedIn) {
      if (!yActions?.deleteSubtree) throw new Error('The collaborative map is still syncing');
      yActions.deleteSubtree(command);
    } else {
      applyDeleteSubtreeCommand(command);
      saveIfChanged();
    }

    const deletingIds = new Set(command.nodeIds);
    setCollapsedNodeIds((previous) => new Set(
      [...previous].filter((nodeId) => !deletingIds.has(nodeId))
    ));
    setTemporarilyExpandedNodeIds((previous) => new Set(
      [...previous].filter((nodeId) => !deletingIds.has(nodeId))
    ));
    setFocusHistory((previous) => pruneFocusHistory(previous, deletingIds));
    if (focusRootId && deletingIds.has(focusRootId)) {
      updateFocusView(command.parentId && command.parentId !== root ? command.parentId : null, { fit: false });
    }
    setSelectedId(command.nextSelectedId || null);
    return true;
  }, [
    applyDeleteSubtreeCommand,
    focusRootId,
    isLoggedIn,
    root,
    saveCurrentState,
    saveIfChanged,
    setSelectedId,
    updateFocusView,
    yActions,
  ]);

  const handleRemoveNode = useCallback((nodeIdOverride = null) => {
    const explicitNodeId = typeof nodeIdOverride === 'string' ? nodeIdOverride : null;
    const selectedNode = (explicitNodeId && getNode(explicitNodeId)) || getSelectedNode();
    if (!selectedNode) return false;

    try {
      const command = buildDeleteSubtreeCommand({
        nodeId: selectedNode.id,
        rootId: root,
        nodes,
        relationships,
        actorUserId: currentUserId || null,
      });
      if (command.nodeIds.length > 1 && typeof window !== 'undefined') {
        const confirmed = window.confirm(
          `Delete this branch and its ${command.nodeIds.length - 1} descendant${command.nodeIds.length === 2 ? '' : 's'}?`
        );
        if (!confirmed) return false;
      }
      return executeDeleteSubtreeCommand(command);
    } catch (error) {
      showNotification(error.message || 'This branch could not be deleted.', 'error');
      return false;
    }
  }, [
    currentUserId,
    executeDeleteSubtreeCommand,
    getNode,
    getSelectedNode,
    nodes,
    relationships,
    root,
    showNotification,
  ]);

  const handleCollapseNode = useCallback(() => {
    const selectedNode = getSelectedNode();
    if (selectedNode) {
      collapseBranch(selectedNode.id);
    }
  }, [collapseBranch, getSelectedNode]);

  const handleExpandNode = useCallback(() => {
    const selectedNode = getSelectedNode();
    if (selectedNode) {
      expandBranch(selectedNode.id);
    }
  }, [expandBranch, getSelectedNode]);

  const restoreLocalHistoryState = useCallback((historyState, action) => {
    if (!historyState) return;
    cancelViewportPanAnimation();
    loadMindMapFromData(historyState);
    // Force the next real edit to record this restored state and clear any
    // obsolete forward branch. The hook itself retains duplicate protection.
    lastSavedStateRef.current = null;
    if (historyState.viewport && typeof setViewport === 'function') {
      setViewport(historyState.viewport);
    }
    console.log(`${action} complete, restored history state`);
  }, [cancelViewportPanAnimation, loadMindMapFromData, setViewport]);

  const handleUndo = useCallback(() => {
    // Logged-in (Yjs) path: delegate undo to the Yjs UndoManager so
    // history is consistent across collaborators.
    if (isLoggedIn && yActions && typeof yActions.undo === 'function') {
      yActions.undo();
      return;
    }

    // Guest / legacy path: restore from local snapshot history.
    const previousState = localUndo(getCurrentHistoryState());
    restoreLocalHistoryState(previousState, 'Undo');
  }, [isLoggedIn, yActions, localUndo, getCurrentHistoryState, restoreLocalHistoryState]);

  const handleRedo = useCallback(() => {
    if (isLoggedIn && yActions && typeof yActions.redo === 'function') {
      yActions.redo();
      return;
    }

    const nextState = localRedo(getCurrentHistoryState());
    restoreLocalHistoryState(nextState, 'Redo');
  }, [isLoggedIn, yActions, localRedo, getCurrentHistoryState, restoreLocalHistoryState]);

  // Combined canUndo that prefers Yjs history when logged in and
  // falls back to the local stack for guest mode.
  const canUndo = useCallback(() => {
    if (isLoggedIn && yActions && typeof yActions.canUndo === 'function') {
      return yActions.canUndo();
    }
    return localCanUndo();
  }, [isLoggedIn, yActions, localCanUndo]);

  const canRedo = useCallback(() => {
    if (isLoggedIn && yActions && typeof yActions.canRedo === 'function') {
      return yActions.canRedo();
    }
    return localCanRedo();
  }, [isLoggedIn, yActions, localCanRedo]);

  const openSelectedNodeMenu = useCallback(() => {
    const selectedNode = getSelectedNode();
    const containerRect = containerRef.current?.getBoundingClientRect();
    const nodeRect = selectedNode
      ? nodeRefs.current[selectedNode.id]?.getBoundingClientRect?.()
      : null;
    if (!selectedNode || !containerRect || !nodeRect) return;
    if (
      accessRole === 'owner'
      && selectedNode.isTask
      && taskAssigneesMapId !== remoteMindmapId
    ) {
      void refreshTaskAssignees();
    }
    const viewportPoint = clientToViewport(
      {
        x: nodeRect.left + Math.min(nodeRect.width, 28),
        y: nodeRect.bottom + 8,
      },
      containerRect
    );
    contextMenuOpenedAtRef.current = Date.now();
    setContextMenu({
      visible: true,
      nodeId: selectedNode.id,
      x: viewportPoint.x,
      y: viewportPoint.y,
    });
  }, [
    accessRole,
    getSelectedNode,
    refreshTaskAssignees,
    remoteMindmapId,
    taskAssigneesMapId,
  ]);

  const copyIdeaText = useCallback(async (nodeId) => {
    const node = latestGraphRef.current.nodes.find((candidate) => candidate.id === nodeId);
    if (!node) return false;
    const copied = await copyToClipboard(typeof node.text === 'string' ? node.text : '');
    showNotification(
      copied ? 'Idea text copied.' : 'Could not copy the idea text.',
      copied ? 'success' : 'error'
    );
    return copied;
  }, [showNotification]);

  // Initialize keyboard shortcuts
  const { handleKeyDown } = useKeyboardShortcuts({
    selectedNode: getSelectedNode(),
    onAddChild: canWriteMap ? handleAddChildNode : undefined,
    onAddSibling: canWriteMap ? handleAddSiblingNode : undefined,
    onNavigateDirection: handleDirectionalNavigation,
    onFocusNode: canWriteMap ? (nodeId) => {
      if (nodeId) {
        const nodeRef = nodeRefs.current[nodeId];
        if (nodeRef && nodeRef.startEditing) {
          nodeRef.startEditing();
        }
      }
    } : undefined,
    onUndo: canWriteMap ? handleUndo : undefined,
    onRedo: canWriteMap ? handleRedo : undefined,
    onRemoveNode: canWriteMap ? handleRemoveNode : undefined,
    onCollapseNode: handleCollapseNode,
    onExpandNode: handleExpandNode,
    onFitVisible: handleFitVisible,
    onFitSelection: handleFitSelection,
    onOpenSearch: () => openMapSearch({ select: true }),
    onOpenNodeMenu: openSelectedNodeMenu,
    onCopyNodeText: copyIdeaText,
    onFocusBack: focusRootId && focusHistory.index > 0
      ? () => navigateFocusHistory(-1)
      : undefined,
    onDeleteNode: canWriteMap ? () => {
      // Check if any node is currently being edited
      const editingNode = Object.keys(nodeRefs.current).find(nodeId => {
        const nodeRef = nodeRefs.current[nodeId];
        return nodeRef && nodeRef.isEditing && nodeRef.isEditing();
      });

      // Only delete if no node is being edited
      if (!editingNode) {
        handleRemoveNode();
      }
    } : undefined
  });

  // Handle auto-focusing new nodes
  useEffect(() => {
    const pendingNodeId = pendingAutoFocusNodeIdRef.current;
    const nodeToFocus = isLoggedIn
      ? nodes.find((node) => node.id === pendingNodeId)
      : nodes.find((node) => node.autoFocus);
    if (nodeToFocus) {
      const nodeRef = nodeRefs.current[nodeToFocus.id];
      if (nodeRef && nodeRef.startEditing) {
        nodeRef.startEditing();
        pendingAutoFocusNodeIdRef.current = null;
        if (!isLoggedIn) clearAutoFocus();
      }
    }
  }, [nodes, clearAutoFocus, isLoggedIn]);

  // Placement uses the expected node size. Once React has mounted the actual
  // content, recheck it against the usable viewport and pan only as far as
  // needed. Editing nodes stay pending so wrapping/growth is rechecked too.
  useEffect(() => {
    const pending = pendingCreationRevealRef.current;
    if (!pending || !nodes.some((node) => node.id === pending.nodeId)) return undefined;
    let finalRecheck = null;
    // Refs are committed before effects, so take an immediate measurement.
    // This cannot be starved by rapid size/text renders cancelling queued RAFs.
    revealCreatedNode(pending.nodeId);
    const settledFrame = window.requestAnimationFrame(() => {
      revealCreatedNode(pending.nodeId);
      if (!pending.finalize) return;

      // Initial centering, collision settling, and edit-size projection can
      // all update the viewport or node geometry just after this frame. Keep
      // ownership briefly and make one final measurement once they settle.
      finalRecheck = window.setTimeout(() => {
        revealCreatedNode(pending.nodeId);
        const initialRootId = root || nodes[0]?.id || null;
        const initialViewportKey = initialRootId
          ? `${viewportStorageKey || 'anonymous'}:${initialRootId}`
          : null;
        const isInitialCenterPending = Boolean(
          !didRestoreViewport
          && initialViewportKey
          && centeredInitialViewportKeyRef.current !== initialViewportKey
        );
        if (
          !isInitialCenterPending
          && pendingCreationRevealRef.current === pending
        ) {
          pendingCreationRevealRef.current = null;
        }
      }, CREATION_FINAL_RECHECK_MS);
    });
    return () => {
      window.cancelAnimationFrame(settledFrame);
      if (finalRecheck !== null) window.clearTimeout(finalRecheck);
    };
  }, [
    didRestoreViewport,
    layoutVersion,
    nodes,
    revealCreatedNode,
    root,
    viewportStorageKey,
  ]);

  // Clear auto-focus animation flags
  useEffect(() => {
    if (nodes.some(n => n.animate)) {
      const timeout = setTimeout(() => {
        clearAutoFocus();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [nodes, clearAutoFocus]);

  // Initialize with default data on mount for guest mode only.
  // Logged-in users load state from Yjs instead of legacy snapshots.
  useEffect(() => {
    if (isLoggedIn) return;

    if (remoteMapData && Array.isArray(remoteMapData.nodes) && remoteMapData.nodes.length > 0) {
      console.log('[DEBUG] Initializing map from remoteMapData');
      loadMindMapFromData(remoteMapData);
    } else {
      loadMindMap();
    }
    hasLoadedRef.current = true;
  }, [isLoggedIn, loadMindMap, loadMindMapFromData, remoteMapData]);

  // Once the mind map has loaded and nodes exist, wait for a
  // frame so scale/layout can settle, then reveal the map.
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    if (!nodes.length) return;

    const id = window.requestAnimationFrame(() => {
      if (initializedAtRef.current === null) {
        initializedAtRef.current = window.performance?.now?.() ?? Date.now();
      }
      setIsInitialized(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [nodes.length]);

  // Persist when a debounced save has been requested and state changed
  useEffect(() => {
    if (saveTrigger === 0) return;
    persistNow();
  }, [saveTrigger, persistNow]);

  // Removed beforeunload warning: Yjs sync persists changes continuously,
  // so we no longer block page unload with a confirmation dialog.

  // On initial load, if a `?node=` query param is present and matches
  // an existing node, focus on that node and its descendants.
  useEffect(() => {
    if (urlNodeHandledRef.current) return;
    if (!nodes.length) return;
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const urlNodeId = params.get('node');
    if (!urlNodeId) {
      urlNodeHandledRef.current = true;
      if (onBaseNodeChange) {
        onBaseNodeChange(null);
      }
      return;
    }

    const exists = nodes.some(n => n.id === urlNodeId);
    if (!exists) {
      console.warn('URL node id not found in current map:', urlNodeId);
      urlNodeHandledRef.current = true;
      if (onBaseNodeChange) {
        onBaseNodeChange(null);
      }
      return;
    }

    updateFocusView(urlNodeId);
    urlNodeHandledRef.current = true;
  }, [nodes, onBaseNodeChange, updateFocusView]);

  // Helper functions for UI
  const selectedNodePosition = () => {
    const node = getSelectedNode();
    if (!node) return { x: 0, y: 0 };

    // Get the actual node size from the DOM if available
    const nodeRef = nodeRefs.current[node.id];
    let nodeWidth = 200;

    const measuredSize = readNodeLayoutSize(nodeRef, scale);
    if (measuredSize) nodeWidth = measuredSize.width;

    return worldToViewport({
      // Position to the top-right of the node
      x: node.position.x + nodeWidth + 10, // 10px to the right
      y: node.position.y - 10 // 10px above
    }, { scale, offset });
  };

  const handleNodeSelect = (nodeId) => {
    setSelectedId(nodeId);
    setCaptureTargetId(nodeId);
    // Hide any open context menu when selection changes via click
    if (contextMenu.visible && contextMenu.nodeId !== nodeId) {
      setContextMenu(prev => ({ ...prev, visible: false }));
    }
  };

  const handleNodePositionChange = useCallback((nodeId, position) => {
    const isGlobalRoot = Boolean(root && nodeId === root);
    const isFocusedRootDrag = Boolean(focusRootId && nodeId === focusRootId);

    if (isGlobalRoot && !isFocusedRootDrag) {
      return;
    }

    const gesture = dragGestureRef.current?.nodeId === nodeId
      ? dragGestureRef.current
      : {
          nodeId,
          nodes,
          relationships,
        };

    let commandNodeId = nodeId;
    let targetPosition = position;
    if (isFocusedRootDrag) {
      const focused = gesture.nodes.find((node) => node.id === focusRootId);
      const rootNode = gesture.nodes.find((node) => node.id === root);
      if (focused && rootNode) {
        commandNodeId = root;
        targetPosition = {
          x: rootNode.position.x + position.x - focused.position.x,
          y: rootNode.position.y + position.y - focused.position.y,
        };
        centerNodePrecise(root, { positionOverride: targetPosition });
      }
    }

    if (dragGestureRef.current?.nodeId === nodeId) {
      dragGestureRef.current.finalNodeId = commandNodeId;
      dragGestureRef.current.finalTargetPosition = { ...targetPosition };
    }

    try {
      const command = buildMoveSubtreeCommand({
        nodeId: commandNodeId,
        targetPosition,
        nodes: gesture.nodes,
        relationships: gesture.relationships,
      });
      setDragPositionPreview(createDragPositionPreview(command));
      queueMoveNodesCommand(command);
    } catch (error) {
      console.error('Failed to move node:', error);
    }
  }, [
    root,
    focusRootId,
    nodes,
    relationships,
    centerNodePrecise,
    queueMoveNodesCommand,
  ]);

  const handleNodeDragComplete = useCallback(() => {
    clearQueuedMoveCommand();
    const gesture = dragGestureRef.current;
    const droppedInInbox = Boolean(
      gesture?.nodeId
      && gesture.nodeId !== root
      && isPointInsideInboxDropZone(gesture.latestClientPoint)
    );
    setIsInboxDropActive(false);
    const candidateTarget = reparentTargetRef.current;
    const reparentTarget = candidateTarget?.id && visibleNodeIds.has(candidateTarget.id)
      ? candidateTarget
      : null;
    reparentTargetRef.current = null;
    setHighlightedNodeId(null);
    setReparentCandidate({
      visible: false,
      draggedNode: null,
      targetNode: null,
      position: { x: 0, y: 0 },
    });

    // Desktop nodes enter a provisional drag state on mousedown so document
    // listeners are ready before the pointer moves. A plain click must not
    // run collision and complete-map route settling on pointer release.
    if (gesture && !gesture.hasMoved) {
      setDragPositionPreview(new Map());
      reparentTargetRef.current = null;
      yActions?.endInteraction?.();
      dragGestureRef.current = null;
      return;
    }

    // Dragging the global root outside focus mode pans the viewport and does
    // not mutate node positions.
    if (gesture?.nodeId === root && !focusRootId) {
      setDragPositionPreview(new Map());
      yActions?.endInteraction?.();
      dragGestureRef.current = null;
      return;
    }

    if (droppedInInbox) {
      setDragPositionPreview(new Map());
      dragGestureRef.current = null;
      handleMoveBranchToInbox(gesture.nodeId);
      yActions?.endInteraction?.();
      saveIfChanged();
      return;
    }

    if (gesture?.nodeId && reparentTarget?.id) {
      dragGestureRef.current = null;
      scheduleReleaseSettlement(
        () => commitReparentDrop(gesture.nodeId, reparentTarget.id),
        () => {
          yActions?.endInteraction?.();
          saveIfChanged();
        }
      );
      return;
    }

    const finalNodeId = gesture?.finalNodeId || gesture?.nodeId;
    const finalTargetPosition = gesture?.finalTargetPosition || gesture?.latestPosition;
    if (finalTargetPosition && !reparentTarget) {
      dragGestureRef.current = null;
      scheduleReleaseSettlement(async () => {
        try {
          const command = buildMoveSubtreeCommand({
            nodeId: finalNodeId,
            targetPosition: finalTargetPosition,
            nodes: gesture.nodes,
            relationships: gesture.relationships,
            pinRoot: !(focusRootId && gesture.nodeId === focusRootId),
          });
          let resolved = command;
          if (!(focusRootId && gesture.nodeId === focusRootId)) {
            const graph = latestGraphRef.current;
            resolved = await settleReleaseInWorker(command, {
              nodes: graph.nodes,
              relationships: graph.relationships,
              nodeSizes: getRuntimeNodeSizes(),
              previousRoutes: previousEdgeRoutesRef.current,
              routedNodeIds: visibleNodeIds,
            });
          }
          if (!resolved) return;
          startCollisionSlide(resolved);
          setDragPositionPreview(createDragPositionPreview(resolved));
          executeMoveNodesCommand(resolved);
        } catch (error) {
          console.error('Failed to finalize node movement:', error);
          setDragPositionPreview(new Map());
        }
      }, () => {
        yActions?.endInteraction?.();
        saveIfChanged();
      });
      return;
    }

    dragGestureRef.current = null;
    yActions?.endInteraction?.();
    saveIfChanged();
  }, [
    clearQueuedMoveCommand,
    root,
    focusRootId,
    visibleNodeIds,
    getRuntimeNodeSizes,
    executeMoveNodesCommand,
    commitReparentDrop,
    handleMoveBranchToInbox,
    isPointInsideInboxDropZone,
    scheduleReleaseSettlement,
    settleReleaseInWorker,
    startCollisionSlide,
    saveIfChanged,
    yActions,
  ]);

  const resolveCreatedNodeCollision = useCallback((nodeId) => {
    if (!nodeId) return;
    const graph = latestGraphRef.current;
    const node = graph.nodes.find((candidate) => candidate.id === nodeId);
    if (!node?.position) return;
    try {
      const runtimeNodeSizes = getRuntimeNodeSizes();
      const command = buildFinalizeCreatedNodeCommand({
        nodeId,
        nodes: graph.nodes,
        relationships: graph.relationships,
        // Re-plan against the bubble the user can actually see. The old
        // collision-ring correction also assumed a full-size card, but did
        // not rank nearby free gaps or protect the connecting route.
        size: runtimeNodeSizes.get(nodeId) || node.layoutSize || DEFAULT_NEW_NODE_SIZE,
        nodeSizes: runtimeNodeSizes,
        usableViewport: readCreationViewportGeometry()?.constraint || null,
      });
      if (Math.abs(command.delta.x) >= 0.5 || Math.abs(command.delta.y) >= 0.5) {
        startCollisionSlide(command);
        executeMoveNodesCommand(command);
        saveIfChanged();
      }
    } catch (error) {
      console.error('Failed to resolve created node collision:', error);
    }
  }, [
    executeMoveNodesCommand,
    getRuntimeNodeSizes,
    readCreationViewportGeometry,
    saveIfChanged,
    startCollisionSlide,
  ]);

  useEffect(() => () => {
    if (
      creationRevealFrameRef.current !== null
      && typeof window !== 'undefined'
    ) {
      window.cancelAnimationFrame(creationRevealFrameRef.current);
      creationRevealFrameRef.current = null;
    }
    if (
      creationPlacementFrameRef.current !== null
      && typeof window !== 'undefined'
    ) {
      window.cancelAnimationFrame(creationPlacementFrameRef.current);
      creationPlacementFrameRef.current = null;
    }
    if (
      directionalNavigationFrameRef.current !== null
      && typeof window !== 'undefined'
    ) {
      window.cancelAnimationFrame(directionalNavigationFrameRef.current);
      directionalNavigationFrameRef.current = null;
    }
    if (
      directionalNavigationCorrectionTimerRef.current !== null
      && typeof window !== 'undefined'
    ) {
      window.clearTimeout(directionalNavigationCorrectionTimerRef.current);
      directionalNavigationCorrectionTimerRef.current = null;
    }
  }, []);

  const handleNodeEditComplete = (finalText, nodeId) => {
    if (nodeId) liveEditingNodeSizesRef.current.delete(nodeId);
    setEditingNodeId((activeNodeId) => (
      !nodeId || activeNodeId === nodeId ? null : activeNodeId
    ));
    // Trigger persistence after editing; undo snapshot is taken
    // when editing starts via saveCurrentState
    if (onBaseNodeChange && focusRootId) {
      const node = getNode(focusRootId);
      onBaseNodeChange(node && node.text ? node.text : null);
    }
    // Save immediately when a node finishes editing so text
    // changes aren't lost even if there are no further actions.
    persistNow(nodeId && typeof finalText === 'string'
      ? { nodeId, text: finalText }
      : undefined
    );
    if (pendingCreationRevealRef.current?.nodeId === nodeId) {
      pendingCreationRevealRef.current.finalize = true;
      setLayoutVersion((version) => version + 1);
      if (creationPlacementFrameRef.current !== null) {
        window.cancelAnimationFrame(creationPlacementFrameRef.current);
      }
      // React applies the completed text and exits the editor in this event.
      // Measure on the next frame so free-space planning uses the final
      // rendered bubble rather than the provisional empty-idea dimensions.
      creationPlacementFrameRef.current = window.requestAnimationFrame(() => {
        creationPlacementFrameRef.current = null;
        if (pendingCreationRevealRef.current?.nodeId === nodeId) {
          resolveCreatedNodeCollision(nodeId);
        }
      });
    }

    // When the edited node is the global root, sync the map title
    // to the backend so shared links and previews stay up to date.
    try {
      if (isLoggedIn && canManageMapMetadata && mindmapIdForYjs && nodeId) {
        const edited = getNode(nodeId);
        const isRootNode = !!(edited && (edited.isRoot || (root && nodeId === root)));
        if (isRootNode) {
          const title = typeof finalText === 'string' ? finalText.trim() : '';
          // Fire-and-forget; persistence of Yjs state already happened above.
          updateMindMapTitle(mindmapIdForYjs, title)
            .then(() => publishMapTitleUpdate({ mapId: mindmapIdForYjs, title }))
            .catch((err) => {
              console.error('Failed to update map title from base node edit:', err);
            });
        }
      }
    } catch (err) {
      console.error('Error while attempting to sync map title:', err);
    }
  };

  const handleColorSelect = (color) => {
    const selectedNode = getSelectedNode();
    if (!selectedNode) {
      setShowPicker(false);
      return;
    }

    const isForeignOwner = !!selectedNode.userId && !!currentUserId && selectedNode.userId !== currentUserId;
    if (isForeignOwner) {
      setShowPicker(false);
      showNotification('You can only change the color of ideas you created.', 'error');
      return;
    }

    saveCurrentState(); // Save before color change
    const canonicalColor = getCanonicalPaletteColor(color);

    // Logged-in (Yjs) path: apply color via Yjs so it
    // syncs across collaborators and survives projection.
    if (isLoggedIn && yActions && yActions.updateNode) {
      const gradient = canonicalColor?.gradient || null;
      yActions.updateNode(selectedNode.id, { color: gradient, lastEditedBy: currentUserId });
    } else {
      // Guest / legacy path: update local React state.
      setNodeColor(selectedNode.id, canonicalColor);
      saveIfChanged();
    }

    setShowPicker(false);
  };

  const handleHome = useCallback(() => {
    updateFocusView(null);
  }, [updateFocusView]);

  const focusBranchInCurrentView = useCallback((nodeId) => {
    const node = getNode(nodeId);
    if (!node) return;
    revealNode(nodeId);
    expandBranch(nodeId);
    updateFocusView(nodeId);
  }, [expandBranch, getNode, revealNode, updateFocusView]);

  // Right-click on a node: open context menu near the click position
  const handleNodeContextMenu = useCallback((nodeId, event) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportPoint = clientToViewport(
      { x: event.clientX, y: event.clientY },
      rect
    );

    setSelectedId(nodeId);
    const node = latestGraphRef.current.nodes.find((candidate) => candidate.id === nodeId);
    if (
      accessRole === 'owner'
      && node?.isTask
      && taskAssigneesMapId !== remoteMindmapId
    ) {
      void refreshTaskAssignees();
    }
    contextMenuOpenedAtRef.current = Date.now();
    setContextMenu({
      visible: true,
      nodeId,
      x: viewportPoint.x,
      y: viewportPoint.y,
    });
  }, [
    accessRole,
    refreshTaskAssignees,
    remoteMindmapId,
    setSelectedId,
    taskAssigneesMapId,
  ]);

  const closeContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  const openNodeDiscussion = useCallback((nodeId) => {
    if (!canUseNodeDiscussions || !nodeId) return;
    setActiveInfoNodeId(null);
    setSelectedId(nodeId);
    nodeDiscussions.openDiscussion(nodeId);
    closeContextMenu();
  }, [
    canUseNodeDiscussions,
    closeContextMenu,
    nodeDiscussions,
    setSelectedId,
  ]);

  const handleContextOpenDiscussion = useCallback(() => {
    openNodeDiscussion(contextMenu.nodeId);
  }, [contextMenu.nodeId, openNodeDiscussion]);

  const openNodeInfo = useCallback((nodeId) => {
    if (!nodeId) return;
    nodeDiscussions.closeDiscussion();
    setSelectedId(nodeId);
    setActiveInfoNodeId(nodeId);
    closeContextMenu();
  }, [closeContextMenu, nodeDiscussions, setSelectedId]);

  const handleContextOpenInfo = useCallback(() => {
    openNodeInfo(contextMenu.nodeId);
  }, [contextMenu.nodeId, openNodeInfo]);

  const handleContextHideBranch = useCallback(async () => {
    const node = latestGraphRef.current.nodes.find(
      (candidate) => candidate.id === contextMenu.nodeId
    );
    closeContextMenu();
    if (
      accessRole !== 'owner'
      || !node?.userId
      || node.userId === currentUserId
      || node.id === root
      || node.isRoot
    ) {
      showNotification('Only collaborator-created branches can be hidden.', 'error');
      return;
    }
    const hidden = await hideModeratedBranch(node.id, node.userId);
    showNotification(
      hidden
        ? 'Branch hidden. You and its creator can still see the faded branch.'
        : 'Could not hide this branch. Check your connection and try again.',
      hidden ? 'success' : 'error'
    );
  }, [
    accessRole,
    closeContextMenu,
    contextMenu.nodeId,
    currentUserId,
    hideModeratedBranch,
    root,
    showNotification,
  ]);

  const handleContextRestoreBranch = useCallback(async () => {
    const nodeId = contextMenu.nodeId;
    closeContextMenu();
    if (accessRole !== 'owner' || !moderatedBranchByNodeId.has(nodeId)) {
      showNotification('Only the map owner can restore this branch.', 'error');
      return;
    }
    const restored = await restoreModeratedBranch(nodeId);
    showNotification(
      restored
        ? 'Branch restored for everyone.'
        : 'Could not restore this branch. Check your connection and try again.',
      restored ? 'success' : 'error'
    );
  }, [
    accessRole,
    closeContextMenu,
    contextMenu.nodeId,
    moderatedBranchByNodeId,
    restoreModeratedBranch,
    showNotification,
  ]);

  const saveNodeInfo = useCallback(async (nodeId, info) => {
    const node = latestGraphRef.current.nodes.find((candidate) => candidate.id === nodeId);
    if (!node) throw new Error('This idea no longer exists.');
    const isForeignOwner = !!node.userId && !!currentUserId && node.userId !== currentUserId;
    const isOwnerOnlyRoot = nodeId === root && !canManageMapMetadata;
    if (!canWriteMap || isForeignOwner || isOwnerOnlyRoot) {
      throw new Error(
        canWriteMap && !isOwnerOnlyRoot
          ? 'You can only edit info on ideas you created.'
          : isOwnerOnlyRoot
            ? 'Only the map owner can edit the main idea Info.'
            : 'This map is view only.'
      );
    }

    const value = typeof info === 'string' ? info : '';
    if (isLoggedIn && yActions?.updateNode) {
      yActions.updateNode(nodeId, {
        info: value,
        lastEditedBy: currentUserId,
      });
      return true;
    }

    setNodes((previous) => previous.map((candidate) => (
      candidate.id === nodeId ? { ...candidate, info: value } : candidate
    )));
    saveIfChanged();
    return true;
  }, [
    canWriteMap,
    canManageMapMetadata,
    currentUserId,
    isLoggedIn,
    saveIfChanged,
    setNodes,
    root,
    yActions,
  ]);

  const handleContextAddChild = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    handleAddChildNode(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, setSelectedId, handleAddChildNode, closeContextMenu]);

  const handleContextAddSibling = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    handleAddSiblingNode(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, setSelectedId, handleAddSiblingNode, closeContextMenu]);

  const addTaskChild = useCallback((parentId) => {
    if (!parentId) return false;
    const parentNode = getNode(parentId);
    if (!parentNode) return false;

    const command = buildCreateTaskChildCommand({
      id: crypto.randomUUID(),
      parentId,
      nodes,
      relationships,
      size: DEFAULT_NEW_NODE_SIZE,
      nodeSizes: getRuntimeNodeSizes(),
      usableViewport: readCreationViewportGeometry()?.constraint || null,
      color: parentNode.color || COLORS[0]?.gradient || null,
      userId: currentUserId || null,
      creatorName: currentUserName || null,
    });
    executeCreateNodeCommand(command);
    return true;
  }, [
    getNode,
    relationships,
    nodes,
    currentUserId,
    currentUserName,
    getRuntimeNodeSizes,
    readCreationViewportGeometry,
    executeCreateNodeCommand,
  ]);

  const handleContextAddTaskChild = useCallback(() => {
    if (!addTaskChild(contextMenu.nodeId)) return;
    closeContextMenu();
  }, [addTaskChild, closeContextMenu, contextMenu.nodeId]);

  const handleContextCreateMarkdown = useCallback(async () => {
    const parentId = contextMenu.nodeId;
    if (!parentId) return;
    if (!canWriteMap || !mindmapIdForYjs || !currentUserId) {
      showNotification('Sign in with edit access to create a Markdown file.', 'error');
      return;
    }

    const graph = latestGraphRef.current;
    const parent = graph.nodes.find((node) => node.id === parentId);
    if (!parent) {
      showNotification('The parent idea no longer exists.', 'error');
      return;
    }

    closeContextMenu();
    const filename = DEFAULT_MARKDOWN_FILENAME;
    const pendingUpload = { id: crypto.randomUUID(), filename };
    setPendingAttachmentUploads((current) => [...current, pendingUpload]);

    try {
      const created = await createStoredMarkdownIdea({
        // Plan before uploading so placement failures cannot leave a stored
        // file without a corresponding idea.
        plan: () => buildCreateAttachmentCommand({
          id: crypto.randomUUID(),
          parentId,
          nodes: graph.nodes,
          relationships: graph.relationships,
          size: DEFAULT_NEW_NODE_SIZE,
          nodeSizes: getRuntimeNodeSizes(),
          usableViewport: readCreationViewportGeometry()?.constraint || null,
          attachment: {
            id: `pending-${pendingUpload.id}`,
            kind: 'markdown',
            filename,
            contentType: 'text/markdown; charset=utf-8',
            byteSize: BLANK_MARKDOWN_CONTENT.length,
          },
          color: parent.color || COLORS[0]?.gradient || null,
          userId: currentUserId,
          creatorName: currentUserName || null,
        }),
        upload: () => uploadNodeAsset(
          mindmapIdForYjs,
          // A newline is visually blank Markdown while satisfying the
          // existing non-empty attachment invariant.
          new File([BLANK_MARKDOWN_CONTENT], filename, { type: 'text/markdown' }),
          'markdown'
        ),
        execute: executeCreateNodeCommand,
        cleanup: (asset) => deleteNodeAsset(mindmapIdForYjs, asset.id),
      });
      showNotification('Markdown file created.', 'success');
      handleOpenAttachment(created.command.node.attachment, created.nodeId);
    } catch (error) {
      console.error('Failed to create Markdown attachment:', error);
      if (error.cleanupError) {
        console.error('Failed to clean up unlinked Markdown attachment:', error.cleanupError);
      }
      showNotification(error.message || 'Could not create this Markdown file.', 'error');
    } finally {
      setPendingAttachmentUploads((current) => (
        current.filter((upload) => upload.id !== pendingUpload.id)
      ));
    }
  }, [
    canWriteMap,
    closeContextMenu,
    contextMenu.nodeId,
    currentUserId,
    currentUserName,
    executeCreateNodeCommand,
    getRuntimeNodeSizes,
    handleOpenAttachment,
    mindmapIdForYjs,
    readCreationViewportGeometry,
    showNotification,
  ]);

  const handleContextConvertBranchToMarkdown = useCallback(() => {
    const nodeId = contextMenu.nodeId;
    if (!nodeId) return;
    if (!canWriteMap || !mindmapIdForYjs || !currentUserId) {
      showNotification('Sign in with edit access to create a Markdown list.', 'error');
      return;
    }
    try {
      const latestGraph = latestGraphRef.current;
      const graph = filterModeratedGraph(
        latestGraph.nodes,
        latestGraph.relationships,
        moderationExcludedNodeIds
      );
      const plan = planBranchMarkdown({
        nodeId,
        nodes: graph.nodes,
        relationships: graph.relationships,
        actorUserId: currentUserId,
        rootId: root,
      });
      closeContextMenu();
      setBranchMarkdownRequest({ plan, isWorking: false });
    } catch (error) {
      closeContextMenu();
      showNotification(error.message || 'This branch cannot be converted.', 'error');
    }
  }, [
    canWriteMap,
    closeContextMenu,
    contextMenu.nodeId,
    currentUserId,
    mindmapIdForYjs,
    moderationExcludedNodeIds,
    root,
    showNotification,
  ]);

  const closeBranchMarkdownDialog = useCallback(() => {
    setBranchMarkdownRequest((current) => (current?.isWorking ? current : null));
  }, []);

  const executeBranchMarkdownChoice = useCallback(async (mode) => {
    const request = branchMarkdownRequest;
    const plan = request?.plan;
    if (!plan || request.isWorking) return;
    if (mode === 'replace' && !plan.replaceAllowed) return;
    if (!canWriteMap || !mindmapIdForYjs || !currentUserId) {
      setBranchMarkdownRequest(null);
      showNotification('Sign in with edit access to create a Markdown list.', 'error');
      return;
    }
    if (branchMarkdownConversionRef.current) return;
    branchMarkdownConversionRef.current = true;

    try {
      // Confirmation applies to a specific graph snapshot. Abort before the
      // upload if another collaborator changed the branch while the preview
      // was open.
      const latestGraph = latestGraphRef.current;
      const graph = filterModeratedGraph(
        latestGraph.nodes,
        latestGraph.relationships,
        moderationExcludedNodeIds
      );
      const currentPlan = planBranchMarkdown({
        nodeId: plan.nodeId,
        nodes: graph.nodes,
        relationships: graph.relationships,
        actorUserId: currentUserId,
        rootId: root,
      });
      const relationshipFingerprint = (candidate) => candidate.branchRelationships
        .map((relationship) => `${relationship.parentId}:${relationship.childId}`)
        .sort()
        .join('|');
      if (
        currentPlan.markdown !== plan.markdown
        || currentPlan.replaceAllowed !== plan.replaceAllowed
        || [...currentPlan.branchNodeIds].sort().join('|') !== [...plan.branchNodeIds].sort().join('|')
        || relationshipFingerprint(currentPlan) !== relationshipFingerprint(plan)
      ) {
        setBranchMarkdownRequest({ plan: currentPlan, isWorking: false });
        showNotification('The branch changed. The preview has been updated for review.', 'info');
        return;
      }

      const parent = graph.nodes.find((node) => node.id === plan.nodeId);
      if (!parent) throw new Error('The selected idea no longer exists.');
      if (mode === 'replace' && !yActions?.replaceBranchWithMarkdown) {
        throw new Error('The collaborative map is still syncing.');
      }

      setBranchMarkdownRequest({ plan, isWorking: true });
      const pendingUpload = { id: crypto.randomUUID(), filename: plan.filename };
      setPendingAttachmentUploads((current) => [...current, pendingUpload]);
      try {
        const upload = () => uploadNodeAsset(
          mindmapIdForYjs,
          new File([plan.markdown], plan.filename, { type: 'text/markdown' }),
          'markdown'
        );
        const cleanup = (asset) => deleteNodeAsset(mindmapIdForYjs, asset.id);
        let created;

        if (mode === 'keep') {
          created = await createStoredMarkdownIdea({
            plan: () => buildCreateAttachmentCommand({
              id: crypto.randomUUID(),
              parentId: plan.nodeId,
              nodes: graph.nodes,
              relationships: graph.relationships,
              size: DEFAULT_NEW_NODE_SIZE,
              nodeSizes: getRuntimeNodeSizes(),
              usableViewport: readCreationViewportGeometry()?.constraint || null,
              attachment: {
                id: `pending-${pendingUpload.id}`,
                kind: 'markdown',
                filename: plan.filename,
                contentType: 'text/markdown; charset=utf-8',
                byteSize: plan.markdownByteSize,
              },
              color: parent.color || COLORS[0]?.gradient || null,
              userId: currentUserId,
              creatorName: currentUserName || null,
            }),
            upload,
            execute: executeCreateNodeCommand,
            cleanup,
          });
        } else {
          created = await replaceBranchWithStoredMarkdown({
            plan,
            upload,
            execute: (command) => {
              saveCurrentState();
              return yActions.replaceBranchWithMarkdown(command);
            },
            cleanup,
            lastEditedBy: currentUserId,
          });
          const removedIds = new Set(plan.removedNodeIds);
          setCollapsedNodeIds((previous) => new Set(
            [...previous].filter((nodeId) => !removedIds.has(nodeId))
          ));
          setTemporarilyExpandedNodeIds((previous) => new Set(
            [...previous].filter((nodeId) => !removedIds.has(nodeId))
          ));
          setFocusHistory((previous) => pruneFocusHistory(previous, removedIds));
          if (focusRootId && removedIds.has(focusRootId)) {
            updateFocusView(plan.nodeId !== root ? plan.nodeId : null, { fit: false });
          }
          setSelectedId(plan.nodeId);
        }

        setBranchMarkdownRequest(null);
        showNotification(
          mode === 'keep'
            ? 'Markdown list created and ideas kept.'
            : 'Branch replaced with a Markdown list.',
          'success'
        );
        handleOpenAttachment(created.command.node?.attachment || created.command.nodePatch.attachment, created.nodeId);
      } finally {
        setPendingAttachmentUploads((current) => (
          current.filter((upload) => upload.id !== pendingUpload.id)
        ));
      }
    } catch (error) {
      console.error('Failed to create Markdown list:', error);
      if (error.cleanupError) {
        console.error('Failed to clean up unlinked Markdown list:', error.cleanupError);
      }
      setBranchMarkdownRequest((current) => (
        current ? { ...current, isWorking: false } : current
      ));
      showNotification(error.message || 'Could not create this Markdown list.', 'error');
    } finally {
      branchMarkdownConversionRef.current = false;
    }
  }, [
    branchMarkdownRequest,
    canWriteMap,
    currentUserId,
    currentUserName,
    executeCreateNodeCommand,
    focusRootId,
    getRuntimeNodeSizes,
    handleOpenAttachment,
    mindmapIdForYjs,
    moderationExcludedNodeIds,
    readCreationViewportGeometry,
    root,
    saveCurrentState,
    setSelectedId,
    showNotification,
    updateFocusView,
    yActions,
  ]);

  const handleCapsuleAddChild = useCallback((nodeId) => {
    if (!nodeId) return;
    setSelectedId(nodeId);
    handleAddChildNode(nodeId);
  }, [handleAddChildNode, setSelectedId]);

  const handleCapsuleChangeColor = useCallback((nodeId) => {
    if (!nodeId) return;
    setSelectedId(nodeId);
    setColorPosOverride(null);
    setShowPicker(true);
  }, [setSelectedId]);

  const handleCapsuleOpenMenu = useCallback((nodeId) => {
    const nodeRect = nodeRefs.current[nodeId]?.getBoundingClientRect?.();
    if (!nodeId || !nodeRect) return;
    handleNodeContextMenu(nodeId, {
      clientX: nodeRect.right + 8,
      clientY: nodeRect.top + Math.min(nodeRect.height / 2, 24),
    });
  }, [handleNodeContextMenu]);

  const returnFocusToCanvas = useCallback(() => {
    containerRef.current?.focus?.({ preventScroll: true });
  }, []);

  const handleContextDeleteNode = useCallback(() => {
    if (!contextMenu.nodeId) return;
    handleRemoveNode(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, handleRemoveNode, closeContextMenu]);

  const handleContextChangeColor = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    // Offset the color picker a bit from the click to avoid overlap
    setColorPosOverride({ x: contextMenu.x + 8, y: contextMenu.y + 8 });
    setShowPicker(true);
    closeContextMenu();
  }, [contextMenu.nodeId, setSelectedId, setShowPicker, closeContextMenu, contextMenu.x, contextMenu.y]);

  const handleContextChangeEmoji = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setSelectedId(contextMenu.nodeId);
    setShowEmojiPicker(true);
    setEmojiPos({ x: contextMenu.x, y: contextMenu.y });
    closeContextMenu();
  }, [contextMenu.nodeId, contextMenu.x, contextMenu.y, setSelectedId, closeContextMenu]);

  const handleContextCreateMindmapLink = useCallback(async (parentIdOverride = null) => {
    const requestedParentId = typeof parentIdOverride === 'string'
      ? parentIdOverride
      : contextMenu.nodeId;
    if (!requestedParentId) return;
    if (!currentUserId) {
      showNotification('Sign in to create a linked map.', 'error');
      return;
    }
    if (!isLoggedIn || !yActions?.createNodeWithRelationship) {
      showNotification('The map is still syncing. Try again in a moment.', 'error');
      return;
    }

    const parentId = requestedParentId;
    if (linkedMapCreationRef.current.has(parentId)) return;
    linkedMapCreationRef.current.add(parentId);
    closeContextMenu();

    try {
      const parentNode = getNode(parentId);
      if (!parentNode) throw new Error('The parent idea no longer exists');
      const NEW_MAP_TITLE = 'New Map';
      await createLinkedMapPortal({
        title: NEW_MAP_TITLE,
        createMindMap,
        buildCommand: (created) => {
          const latestGraph = latestGraphRef.current;
          const latestParent = latestGraph.nodes.find((node) => node.id === parentId);
          return buildCreateLinkedMapCommand({
            id: crypto.randomUUID(),
            parentId,
            nodes: latestGraph.nodes,
            relationships: latestGraph.relationships,
            size: DEFAULT_NEW_NODE_SIZE,
            nodeSizes: getRuntimeNodeSizes(),
            usableViewport: readCreationViewportGeometry()?.constraint || null,
            linkedMindmapId: created.id,
            linkedMindmapUrl: buildMindMapShareUrl(created.id),
            title: NEW_MAP_TITLE,
            color: latestParent?.color || COLORS[0]?.gradient || null,
            userId: currentUserId,
            creatorName: currentUserName || null,
          });
        },
        executeCommand: executeCreateNodeCommand,
      });
      showNotification('Linked map created.', 'success');
    } catch (err) {
      console.error('Failed to create linked mind map:', err);
      const failure = getLinkedMapCreationFailure(err);
      showNotification(failure.message, 'error');
      if (failure.upgradeRequired) {
        setMapCreationUpgradeRequest({
          capabilityKey: failure.capabilityKey,
          parentId,
        });
      }
    } finally {
      linkedMapCreationRef.current.delete(parentId);
    }
  }, [
    contextMenu.nodeId,
    currentUserId,
    isLoggedIn,
    yActions,
    closeContextMenu,
    getNode,
    currentUserName,
    getRuntimeNodeSizes,
    readCreationViewportGeometry,
    executeCreateNodeCommand,
    showNotification,
  ]);

  const handleMapLinkTitleCommit = useCallback(async (mapId, title) => {
    if (!mapId) return;
    try {
      await updateMindMapTitle(mapId, title || '');
    } catch (err) {
      console.error('Failed to sync map title from link node edit:', err);
    }
  }, []);

  useEffect(() => subscribeToMapTitleUpdates(({ mapId, title }) => {
    const linkedNodes = latestGraphRef.current.nodes.filter(
      (node) => node.embeddedMindmapId === mapId
    );
    if (linkedNodes.length === 0) return;

    if (isLoggedIn && canWriteMap && yActions?.updateNode) {
      linkedNodes.forEach((node) => {
        const patch = buildLinkedMapTitlePatch(node, {
          mapId,
          title,
          currentUserId,
        });
        if (patch) yActions.updateNode(node.id, patch);
      });
      return;
    }

    setNodes((previous) => previous.map((node) => {
      const patch = buildLinkedMapTitlePatch(node, {
        mapId,
        title,
        currentUserId,
      });
      return patch ? { ...node, ...patch } : node;
    }));
    saveIfChanged();
  }), [canWriteMap, currentUserId, isLoggedIn, saveIfChanged, setNodes, yActions]);

  const handleContextFocusBranch = useCallback(() => {
    if (!contextMenu.nodeId) return;
    focusBranchInCurrentView(contextMenu.nodeId);
    closeContextMenu();
  }, [contextMenu.nodeId, focusBranchInCurrentView, closeContextMenu]);

  const handleContextOpenBranch = useCallback(() => {
    if (!contextMenu.nodeId) return;
    const nodeId = contextMenu.nodeId;
    let fullUrl = `${window.location.origin}${window.location.pathname}`;
    try {
      const current = new URL(window.location.href);
      const params = new URLSearchParams(current.search);
      params.set('node', nodeId);
      const query = params.toString();
      fullUrl = `${window.location.origin}${current.pathname}${query ? `?${query}` : ''}`;
    } catch {
      // Fallback to basic node-only link
      fullUrl = `${window.location.origin}${window.location.pathname}?node=${encodeURIComponent(nodeId)}`;
    }

    window.open(fullUrl, '_blank', 'noopener,noreferrer');
    closeContextMenu();
  }, [contextMenu.nodeId, closeContextMenu]);

  const handleContextCopyLink = useCallback(() => {
    if (!contextMenu.nodeId) return;
    const nodeId = contextMenu.nodeId;
    let fullUrl = `${window.location.origin}${window.location.pathname}`;
    try {
      const current = new URL(window.location.href);
      const params = new URLSearchParams(current.search);
      params.set('node', nodeId);
      const query = params.toString();
      fullUrl = `${window.location.origin}${current.pathname}${query ? `?${query}` : ''}`;
    } catch {
      // Fallback to basic node-only link
      fullUrl = `${window.location.origin}${window.location.pathname}?node=${encodeURIComponent(nodeId)}`;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullUrl).then(() => {
        console.log('Link copied to clipboard:', fullUrl);
      }).catch((err) => {
        console.error('Failed to copy link, showing alert instead:', err);
        // Fallback
        alert(fullUrl);
      });
    } else {
      alert(fullUrl);
    }

    closeContextMenu();
  }, [contextMenu.nodeId, closeContextMenu]);

  const handleContextShareIdea = useCallback(() => {
    if (!contextMenu.nodeId) return;
    setShareNodeId(contextMenu.nodeId);
    setIsShareOpen(true);
    closeContextMenu();
  }, [closeContextMenu, contextMenu.nodeId]);

  const handleContextCopyText = useCallback(() => {
    if (!contextMenu.nodeId) return;
    copyIdeaText(contextMenu.nodeId);
    closeContextMenu();
  }, [closeContextMenu, contextMenu.nodeId, copyIdeaText]);

  const handleContextToggleTask = useCallback(() => {
    if (!contextMenu.nodeId) return;
    const nodeId = contextMenu.nodeId;
    const existing = getNode(nodeId);
    const isForeignIdea = Boolean(
      existing?.userId
      && currentUserId
      && existing.userId !== currentUserId
    );
    if (!canWriteMap || isForeignIdea) {
      showNotification(
        canWriteMap
          ? 'You can only change task status on ideas you created.'
          : 'This map is view only.',
        'error'
      );
      closeContextMenu();
      return;
    }
    saveCurrentState();

    // Logged-in (Yjs) path: flip task flags via Yjs using the
    // current projected node so it syncs across collaborators.
    if (isLoggedIn && yActions && yActions.updateNode) {
      const currentlyTask = !!(existing && existing.isTask);
      const nextIsTask = !currentlyTask;
      const nextTaskDone = nextIsTask ? false : false; // turning off clears done

      yActions.updateNode(nodeId, {
        isTask: nextIsTask,
        taskDone: nextTaskDone,
        taskCompletedAt: null,
        taskCompletedByUserId: null,
        lastEditedBy: currentUserId,
      });
      closeContextMenu();
      return;
    }

    // Guest / legacy path: use local helper.
    toggleTaskFlag(nodeId);
    saveIfChanged();
    closeContextMenu();
  }, [
    contextMenu.nodeId,
    canWriteMap,
    currentUserId,
    isLoggedIn,
    yActions,
    getNode,
    toggleTaskFlag,
    saveCurrentState,
    saveIfChanged,
    closeContextMenu,
    showNotification,
  ]);

  const handleContextAssignTask = useCallback(async (assignee) => {
    const node = latestGraphRef.current.nodes.find(
      (candidate) => candidate.id === contextMenu.nodeId
    );
    if (
      accessRole !== 'owner'
      || !currentUserId
      || !node?.isTask
      || !yActions?.assignTaskResponsibility
    ) {
      showNotification('Only the map owner can assign this task.', 'error');
      return;
    }
    let verifiedAssignee = assignee;
    if (assignee) {
      const freshAssignees = await refreshTaskAssignees();
      if (!freshAssignees) {
        showNotification(
          'Connect to the internet so we can verify this collaborator is still an editor.',
          'error'
        );
        return;
      }
      verifiedAssignee = freshAssignees.find(
        (candidate) => candidate.userId === assignee.userId
      ) || null;
      if (!verifiedAssignee) {
        showNotification('That collaborator is no longer an editor on this map.', 'error');
        return;
      }
      if (moderationProjectionRef.current.fadedNodeIds.has(node.id)) {
        showNotification('Restore this hidden branch before assigning its task.', 'error');
        return;
      }
    }

    saveCurrentState();
    try {
      yActions.assignTaskResponsibility(node.id, verifiedAssignee, currentUserId);
    } catch (error) {
      showNotification(error?.message || 'Could not update this task assignment.', 'error');
      return;
    }
    closeContextMenu();
    showNotification(
      verifiedAssignee
        ? `Task assigned to ${verifiedAssignee.displayName}.`
        : 'Task assignment removed.',
      'success'
    );
  }, [
    accessRole,
    closeContextMenu,
    contextMenu.nodeId,
    currentUserId,
    refreshTaskAssignees,
    saveCurrentState,
    showNotification,
    yActions,
  ]);

  // Wrap map navigation mouse-up to persist the new center position
  // after a pan gesture so the map center is saved and undoable.
  const handleMouseUp = useCallback((e) => {
    navHandleMouseUp(e);
    // A node owns its full drag/settle history boundary. Saving again while
    // its mouse-up bubbles through the canvas would make the live drop
    // position a second undo step.
    if (e?.target?.closest?.('.node') || dragGestureRef.current) return;
    saveCurrentState();
    saveIfChanged();
  }, [navHandleMouseUp, saveCurrentState, saveIfChanged]);

  const cancelNodeTouchGestures = useCallback(() => {
    Object.values(nodeRefs.current).forEach((nodeRef) => {
      nodeRef?.cancelTouchGesture?.();
    });
  }, []);

  const handlePointerDownCapture = useCallback((event) => {
    cancelViewportPanAnimation();
    if (isSearchOpen) {
      closeMapSearch({ blur: true });
    }
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      return;
    }
    const nodeElement = event?.target?.closest?.('[data-node-id]');
    if (nodeElement && event.button === 0 && event.isPrimary !== false) {
      recentNodePointerDownRef.current = {
        at: Date.now(),
        x: event.clientX,
        y: event.clientY,
      };
    }
    const result = navHandlePointerDown(event);
    if (result?.startedMultiTouch) {
      cancelNodeTouchGestures();
    }
  }, [
    cancelNodeTouchGestures,
    cancelViewportPanAnimation,
    closeMapSearch,
    isSearchOpen,
    navHandlePointerDown,
  ]);

  const handleDoubleClickCapture = useCallback((event) => {
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event?.target?.closest?.('[data-node-id]')) {
      recentNodePointerDownRef.current = null;
    }
  }, []);

  const handlePointerUpCapture = useCallback((event) => {
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      return;
    }
    navHandlePointerUp(event);
  }, [navHandlePointerUp]);

  const handlePointerCancelCapture = useCallback((event) => {
    navHandlePointerCancel(event);
  }, [navHandlePointerCancel]);

  const handlePointerUp = useCallback((event) => {
    if (event?.target?.closest?.('.node')) return;
    saveCurrentState();
    saveIfChanged();
  }, [saveCurrentState, saveIfChanged]);

  const handlePointerCancel = useCallback((event) => {
    if (event?.target?.closest?.('.node')) return;
    saveCurrentState();
    saveIfChanged();
  }, [saveCurrentState, saveIfChanged]);

  // Automatically enrich nodes whose text is a bare URL with metadata,
  // so the preview is ready the first time the user clicks on them.
  const pendingUrlMetaRef = useRef(new Set());
  const attemptedMindmapThumbnailRef = useRef(new Set());

  useEffect(() => {
    const urlOnlyRegex = /^https?:\/\/[^\s]+$/i;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    const getEmbeddedMapId = (candidate) => {
      if (!candidate) return null;
      try {
        const urlObj = new URL(candidate);
        const mapId = urlObj.searchParams.get('map');
        if (!mapId || !uuidRegex.test(mapId)) {
          return null;
        }

        const host = urlObj.hostname.toLowerCase();
        const normalizedHost = host.replace(/^www\./, '');
        const currentHost = (typeof window !== 'undefined' && window.location && window.location.hostname)
          ? window.location.hostname.toLowerCase()
          : null;
        const isLocalHost = normalizedHost === 'localhost' || normalizedHost === '127.0.0.1';
        const matchesCurrentHost = currentHost ? normalizedHost === currentHost : false;
        const isMindmappiHost =
          normalizedHost === 'mindmappi.com'
          || normalizedHost.endsWith('.mindmappi.com');

        if (isLocalHost || matchesCurrentHost || isMindmappiHost) {
          return mapId;
        }

        return null;
      } catch {
        return null;
      }
    };

    nodes.forEach((node) => {
      const rawText = typeof node.text === 'string' ? node.text.trim() : '';
      const linkUrlCandidate =
        typeof node.linkUrl === 'string' && node.linkUrl.trim().length
          ? node.linkUrl.trim()
          : '';

      let urlToUse = null;
      if (linkUrlCandidate) {
        urlToUse = linkUrlCandidate;
      } else if (rawText) {
        if (urlOnlyRegex.test(rawText)) {
          urlToUse = rawText;
        }
      }

      if (!urlToUse) {
        return;
      }

      const mindmapId = getEmbeddedMapId(urlToUse);
      const missingLinkUrl = !linkUrlCandidate;
      const missingLinkMeta = !node.linkMeta;
      const missingMindmapThumbnail = mindmapId && (!node.linkMeta || !node.linkMeta.image);

      const shouldFetchBasicMeta = missingLinkUrl || missingLinkMeta;
      const shouldRefreshMindmapThumbnail = missingMindmapThumbnail;

      if (!shouldFetchBasicMeta && !shouldRefreshMindmapThumbnail) {
        return;
      }

      if (pendingUrlMetaRef.current.has(node.id)) {
        return;
      }

      if (shouldRefreshMindmapThumbnail && attemptedMindmapThumbnailRef.current.has(node.id)) {
        return;
      }

      pendingUrlMetaRef.current.add(node.id);

      (async () => {
        try {
          const meta = await fetchUrlPreview(urlToUse);
          const cleanedTitle = meta && meta.title
            ? decodeHtmlEntities(meta.title).trim()
            : null;
          const enrichedMeta = {
            ...meta,
            title: cleanedTitle || meta.title,
          };

          if (shouldRefreshMindmapThumbnail && mindmapId) {
            attemptedMindmapThumbnailRef.current.add(node.id);
          }

          if (isLoggedIn && yActions && yActions.updateNode) {
            // Logged-in (Yjs) path: write link metadata through Yjs
            // so the document remains the source of truth.
            const currentText = rawText;
            const patch = {
              linkUrl: urlToUse,
              linkMeta: enrichedMeta,
              lastEditedBy: currentUserId,
            };
            if (urlOnlyRegex.test(currentText) && enrichedMeta.title && currentText === urlToUse) {
              patch.text = enrichedMeta.title;
            }
            yActions.updateNode(node.id, patch);
          } else {
            // Guest / legacy path: mutate local React state and persist
            setNodes((prev) => prev.map((n) => {
              if (n.id !== node.id) return n;
              const currentText = (n.text || '').trim();

              const shouldOverrideText =
                urlOnlyRegex.test(currentText) && enrichedMeta.title && currentText === urlToUse;

              if (shouldOverrideText) {
                return {
                  ...n,
                  linkUrl: urlToUse,
                  linkMeta: enrichedMeta,
                  text: enrichedMeta.title,
                };
              }

              return {
                ...n,
                linkUrl: urlToUse,
                linkMeta: enrichedMeta,
              };
            }));
            saveIfChanged();
          }
        } catch (err) {
          console.error('Failed to auto-fetch URL metadata', err);
        } finally {
          pendingUrlMetaRef.current.delete(node.id);
        }
      })();
    });
  }, [nodes, setNodes, saveIfChanged, isLoggedIn, yActions, currentUserId]);

  const handleContainerClick = (event) => {
    if (isSearchOpen) {
      closeMapSearch({ blur: true });
    }
    // Toolbar, menu, picker, and navigation controls live inside the canvas
    // DOM tree but are not canvas background. Their clicks must not schedule
    // a delayed selection clear after the control action has completed.
    if (event?.target?.closest?.('[data-export-ignore="true"]')) {
      return;
    }
    // Mobile browsers may emit a compatibility click immediately after the
    // touch release that completed a long press. Do not let that synthetic
    // background click dismiss the menu that gesture just opened.
    if (Date.now() - contextMenuOpenedAtRef.current < 750) {
      return;
    }
      /*
      console.log('click')
      if (showPicker) {
        setShowPicker(false);
      } else {
        if(selectedId)lastSelectedIdRef.current = selectedId; // Store last selected ID
        setSelectedId(null);
      }
        */
      console.log('click')

    // Hide context menu on background click
    if (contextMenu.visible) {
      setContextMenu(prev => ({ ...prev, visible: false }));
    }

    // Hide emoji picker on background click
    if (showEmojiPicker) {
      setShowEmojiPicker(false);
    }

    // Clear any existing timeout
    if (clickTimeoutRef.current !== null) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      return; // This was a double-click, don't process single click
    }

    // Set a timeout for single click processing
    const timeout = setTimeout(() => {
      if (showPicker) {
        setShowPicker(false);
        setColorPosOverride(null);
      } else {
        if(selectedId) lastSelectedIdRef.current = selectedId;
        setSelectedId(null);
        setCaptureTargetId(null);
      }
      clickTimeoutRef.current = null;
    }, 300); // 300ms delay to detect double-clicks

    clickTimeoutRef.current = timeout;
  };

  const handleEmojiSelect = useCallback((emoji) => {
    if (!selectedId) {
      setShowEmojiPicker(false);
      return;
    }

    const node = getNode(selectedId);
    const isForeignOwner = !!(node && node.userId) && !!currentUserId && node.userId !== currentUserId;
    if (isForeignOwner) {
      setShowEmojiPicker(false);
      showNotification('You can only change the emoji of ideas you created.', 'error');
      return;
    }

    saveCurrentState();

    // Logged-in (Yjs) path: store emoji in Yjs so it syncs.
    if (isLoggedIn && yActions && yActions.updateNode) {
      yActions.updateNode(selectedId, { emoji, lastEditedBy: currentUserId });
    } else {
      // Guest / legacy path: local React state.
      setNodes(prev => prev.map(n => (
        n.id === selectedId ? { ...n, emoji } : n
      )));
      // Persist immediately so emoji changes are not lost if
      // there are no further actions before closing the tab.
      persistNow();
    }

    setShowEmojiPicker(false);
  }, [selectedId, isLoggedIn, yActions, saveCurrentState, setNodes, persistNow, getNode, currentUserId, showNotification]);

  const handleDoubleClick = (e) => {
    if (e?.target?.closest?.('[data-export-ignore="true"]')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (clickTimeoutRef.current !== null) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    // Prevent default double-click behavior
    e.preventDefault();
    e.stopPropagation();

    // A node can move between the two presses because of collaboration or
    // collision layout. In that case the browser targets the resulting
    // double-click at the canvas; keep the gesture owned by the node instead
    // of interpreting it as background child creation.
    const recentNodePointerDown = recentNodePointerDownRef.current;
    if (recentNodePointerDown) {
      const elapsed = Date.now() - recentNodePointerDown.at;
      const distance = Math.hypot(
        e.clientX - recentNodePointerDown.x,
        e.clientY - recentNodePointerDown.y
      );
      recentNodePointerDownRef.current = null;
      if (
        elapsed >= 0
        && elapsed <= NODE_DOUBLE_CLICK_GUARD_MS
        && distance <= NODE_DOUBLE_CLICK_GUARD_DISTANCE
      ) {
        return;
      }
    }

    const selectedNode = getSelectedNode();
    const base = selectedNode
      || (lastSelectedIdRef.current && getNode(lastSelectedIdRef.current))
      || (root && getNode(root))
      || nodes.find((node) => node.isRoot);
    if (!base) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const worldPoint = screenToWorld(
      { x: e.clientX, y: e.clientY },
      containerRect,
      { scale, offset }
    );

    handleAddChildNode(base.id, worldPoint);
  };


  const colorPosFallback = selectedNodePosition();
  const colorPos = colorPosOverride || colorPosFallback;
  const reparentViewportPosition = worldToViewport(
    reparentCandidate.position,
    { scale, offset }
  );
  const contextMenuViewportSize = {
    width: containerRef.current?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 0),
    height: containerRef.current?.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 0),
  };
  const contextMenuPosition = clampViewportOverlay(
    contextMenu,
    contextMenuViewportSize,
    {
      width: 220,
      // Include the writable action set plus the menu's own border/padding.
      // Underestimating this height lets a bottom-opened menu clip after a
      // new command is added, even though its scroll container is bounded.
      height: Math.min(664, Math.max(0, contextMenuViewportSize.height - 16)),
    },
    8
  );
  const activeDiscussionNode = nodeDiscussions.activeNodeId
    ? nodes.find((node) => node.id === nodeDiscussions.activeNodeId)
    : null;
  const activeDiscussionNodeRect = activeDiscussionNode
    ? nodeRefs.current[activeDiscussionNode.id]?.getBoundingClientRect?.()
    : null;
  const discussionAnchorRect = activeDiscussionNodeRect
    ? {
        left: activeDiscussionNodeRect.left,
        right: activeDiscussionNodeRect.right,
        top: activeDiscussionNodeRect.top,
        bottom: activeDiscussionNodeRect.bottom,
        width: activeDiscussionNodeRect.width,
        height: activeDiscussionNodeRect.height,
      }
    : null;
  const activeInfoNode = activeInfoNodeId
    ? nodes.find((node) => node.id === activeInfoNodeId)
    : null;
  const attachmentViewerNode = attachmentViewer?.nodeId
    ? nodes.find((node) => node.id === attachmentViewer.nodeId)
    : null;
  const canEditOpenMarkdownAttachment = Boolean(
    canWriteMap
    && currentUserId
    && attachmentViewerNode
    && (!attachmentViewerNode.userId || attachmentViewerNode.userId === currentUserId)
  );
  const activeInfoNodeRect = activeInfoNode
    ? nodeRefs.current[activeInfoNode.id]?.getBoundingClientRect?.()
    : null;
  const infoAnchorRect = activeInfoNodeRect
    ? {
        left: activeInfoNodeRect.left,
        right: activeInfoNodeRect.right,
        top: activeInfoNodeRect.top,
        bottom: activeInfoNodeRect.bottom,
        width: activeInfoNodeRect.width,
        height: activeInfoNodeRect.height,
      }
    : null;
  const selectedCapsuleNode = selectedId && visibleNodeIds.has(selectedId)
    ? nodes.find((node) => node.id === selectedId)
    : null;
  const selectedCapsuleAnchor = selectedCapsuleNode
    ? nodeRefs.current[selectedCapsuleNode.id]
    : null;
  const showNodeActionCapsule = Boolean(
    selectedCapsuleNode
    && selectedCapsuleAnchor
    && !draggedNodeId
    && editingNodeId !== selectedCapsuleNode.id
    && !contextMenu.visible
    && !showPicker
    && !showEmojiPicker
    && !activeInfoNodeId
    && !nodeDiscussions.activeNodeId
  );
  const requestedCapsuleNodeId = showNodeActionCapsule ? selectedCapsuleNode.id : null;

  useEffect(() => {
    clearTimeout(capsuleExitTimerRef.current);
    clearTimeout(capsuleEnterTimerRef.current);

    const currentNodeId = renderedCapsuleNodeIdRef.current;
    const showRenderedCapsule = () => {
      capsuleEnterTimerRef.current = setTimeout(() => {
        setIsRenderedCapsuleVisible(true);
      }, 16);
    };

    if (currentNodeId === requestedCapsuleNodeId) {
      if (requestedCapsuleNodeId) showRenderedCapsule();
      else setIsRenderedCapsuleVisible(false);
    } else if (currentNodeId) {
      setIsRenderedCapsuleVisible(false);
      capsuleExitTimerRef.current = setTimeout(() => {
        renderedCapsuleNodeIdRef.current = requestedCapsuleNodeId;
        setRenderedCapsuleNodeId(requestedCapsuleNodeId);
        if (requestedCapsuleNodeId) showRenderedCapsule();
      }, NODE_ACTION_CAPSULE_EXIT_MS);
    } else if (requestedCapsuleNodeId) {
      renderedCapsuleNodeIdRef.current = requestedCapsuleNodeId;
      setRenderedCapsuleNodeId(requestedCapsuleNodeId);
      setIsRenderedCapsuleVisible(false);
      showRenderedCapsule();
    }

    return () => {
      clearTimeout(capsuleExitTimerRef.current);
      clearTimeout(capsuleEnterTimerRef.current);
    };
  }, [requestedCapsuleNodeId]);

  const renderedCapsuleNode = renderedCapsuleNodeId && visibleNodeIds.has(renderedCapsuleNodeId)
    ? nodes.find((node) => node.id === renderedCapsuleNodeId)
    : null;
  const renderedCapsuleAnchor = renderedCapsuleNode
    ? nodeRefs.current[renderedCapsuleNode.id]
    : null;
  const renderedCapsuleNodeIsForeign = Boolean(
    renderedCapsuleNode?.userId
    && currentUserId
    && renderedCapsuleNode.userId !== currentUserId
  );
  const capsulePositioningKey = [
    renderedCapsuleNode?.id || '',
    scale,
    offset.x,
    offset.y,
    layoutVersion,
    renderedCapsuleNode?.position?.x,
    renderedCapsuleNode?.position?.y,
  ].join(':');
  const buttonStyle = {
    background: 'transparent',
    border: 'none',
    padding: 2,
    margin: 2,
    cursor: 'pointer',
    outline: 'none',
    color: getThemeCanvasForeground(mapTheme, isDarkMode),
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
  const renderIndex = useMemo(
    () => buildMindMapRenderIndex(nodes, relationships),
    [nodes, relationships]
  );

  const retainedViewportNodeIds = useMemo(() => {
    const retained = new Set([
      selectedId,
      editingNodeId,
      draggedNodeId,
      highlightedNodeId,
      renderedCapsuleNodeId,
      contextMenu.nodeId,
      activeInfoNodeId,
      nodeDiscussions.activeNodeId,
      attachmentViewer?.nodeId,
      focusRootId,
      reparentCandidate.draggedNode?.id,
      reparentCandidate.targetNode?.id,
      ...dragPositionPreview.keys(),
      ...layoutTransitionNodeIds,
    ].filter(Boolean));
    for (const presence of yAwarenessStates || []) {
      if (presence.selectedNodeId) retained.add(presence.selectedNodeId);
      if (presence.editingNodeId) retained.add(presence.editingNodeId);
    }
    return retained;
  }, [
    activeInfoNodeId,
    attachmentViewer?.nodeId,
    contextMenu.nodeId,
    dragPositionPreview,
    draggedNodeId,
    editingNodeId,
    focusRootId,
    highlightedNodeId,
    layoutTransitionNodeIds,
    nodeDiscussions.activeNodeId,
    renderedCapsuleNodeId,
    reparentCandidate.draggedNode?.id,
    reparentCandidate.targetNode?.id,
    selectedId,
    yAwarenessStates,
  ]);

  const fullDetailNodeIds = useMemo(() => {
    const fullDetail = new Set([
      selectedId,
      editingNodeId,
      renderedCapsuleNodeId,
      contextMenu.nodeId,
      activeInfoNodeId,
      nodeDiscussions.activeNodeId,
      attachmentViewer?.nodeId,
    ].filter(Boolean));
    for (const presence of yAwarenessStates || []) {
      if (presence.selectedNodeId) fullDetail.add(presence.selectedNodeId);
      if (presence.editingNodeId) fullDetail.add(presence.editingNodeId);
    }
    return fullDetail;
  }, [
    activeInfoNodeId,
    attachmentViewer?.nodeId,
    contextMenu.nodeId,
    editingNodeId,
    nodeDiscussions.activeNodeId,
    renderedCapsuleNodeId,
    selectedId,
    yAwarenessStates,
  ]);

  const viewportNodeSpatialIndex = directionalNavigationIndex.spatialIndex;

  const mountedNodeIds = useMemo(() => deriveViewportMountedNodeIds({
    nodes,
    branchVisibleNodeIds: renderedNodeIds,
    retainedNodeIds: retainedViewportNodeIds,
    viewport: { scale, offset },
    viewportSize: canvasViewportSize,
    spatialIndex: viewportNodeSpatialIndex,
    getNodeRect: (node) => {
      const position = getRenderedNodePosition(node, dragPositionPreview);
      const size = measuredNodeSizesRef.current.get(node.id)
        || node.layoutSize
        || { width: NODE_WIDTH_GUESS, height: NODE_HEIGHT_GUESS };
      return { ...position, width: size.width, height: size.height };
    },
  }), [
    canvasViewportSize,
    dragPositionPreview,
    nodes,
    offset,
    renderedNodeIds,
    retainedViewportNodeIds,
    scale,
    viewportNodeSpatialIndex,
  ]);
  const semanticZoomActive = shouldUseSemanticZoom({
    scale,
    branchVisibleNodeCount: renderedNodeIds.size,
  });

  const handleShare = useCallback(() => {
    setShareNodeId(null);
    setIsShareOpen(true);
  }, []);

  const edgeRoutingNodes = nodes
    .filter((node) => mountedNodeIds.has(node.id))
    .map((node) => {
      const position = getRenderedNodePosition(node, dragPositionPreview);
      const canonicalSize = measuredNodeSizesRef.current.get(node.id)
        || node.layoutSize;
      // Selected previews stay out of route geometry so opening a card or a
      // long idea does not disturb nearby edges. The active editor is the one
      // exception: its own connected edge needs the live width/height so a
      // newly created idea on the left remains boundary-attached as it grows.
      const measuredSize = resolveRouteLayoutSize({
        nodeId: node.id,
        selectedNodeId: selectedId,
        editingNodeId,
        canonicalSize,
        liveSize: liveEditingNodeSizesRef.current.get(node.id)
          || readNodeLayoutSize(nodeRefs.current[node.id], scale),
      });
      return {
        id: node.id,
        rect: {
          x: position.x,
          y: position.y,
          width: measuredSize?.width || node.layoutSize?.width || NODE_WIDTH_GUESS,
          height: measuredSize?.height || node.layoutSize?.height || NODE_HEIGHT_GUESS,
        },
      };
    });
  const edgeRoutingRelationships = deriveViewportMountedRelationships(
    relationships,
    mountedNodeIds
  );
  const liveMovedNodeIds = new Set([
    ...dragPositionPreview.keys(),
    ...layoutTransitionNodeIds,
  ]);
  if (draggedNodeId && dragGestureRef.current?.hasMoved) {
    liveMovedNodeIds.add(draggedNodeId);
  }
  const affectedEdgeKeys = liveMovedNodeIds.size > 0
    ? findAffectedEdgeKeys({
        nodes: edgeRoutingNodes,
        edges: edgeRoutingRelationships,
        movedNodeIds: liveMovedNodeIds,
        previousRoutes: previousEdgeRoutesRef.current,
      })
    : findGeometryAffectedEdgeKeys({
        nodes: edgeRoutingNodes,
        edges: edgeRoutingRelationships,
        previousRoutes: previousEdgeRoutesRef.current,
      });
  const edgeRoutingGeometryKey = JSON.stringify({
    nodes: edgeRoutingNodes.map(({ id, rect }) => [id, rect.x, rect.y, rect.width, rect.height]),
    relationships: edgeRoutingRelationships.map(
      ({ parentId, childId }) => [parentId, childId]
    ),
    affectedEdges: affectedEdgeKeys ? [...affectedEdgeKeys].sort() : null,
    adaptiveEdgeRouting: (
      nodes.length > VIEWPORT_CULLING_THRESHOLD
      && edgeRoutingNodes.length >= LARGE_MAP_ADAPTIVE_EDGE_ROUTING_MIN
    ),
    connectionStyle: activeConnectionStyleId,
  });
  const routedVisibleEdgeResult = useMemo(() => {
    const startedAt = window.performance?.now?.() ?? Date.now();
    const useAdaptiveEdgeRouting = (
      nodes.length > VIEWPORT_CULLING_THRESHOLD
      && edgeRoutingNodes.length >= LARGE_MAP_ADAPTIVE_EDGE_ROUTING_MIN
    );
    const routes = routeEdges({
      nodes: edgeRoutingNodes,
      edges: edgeRoutingRelationships,
      previousRoutes: previousEdgeRoutesRef.current,
      affectedEdgeKeys,
      avoidObstacles: true,
      avoidEdgeObstacles: !useAdaptiveEdgeRouting,
      style: activeConnectionStyleId,
    });
    const finishedAt = window.performance?.now?.() ?? Date.now();
    return {
      routes,
      durationMs: Math.max(0, finishedAt - startedAt),
    };
    // The serialized key contains every geometry field consumed above. Node
    // text, selection, and other presentation changes must not rerun routing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    edgeRoutingGeometryKey,
  ]);
  denseEdgeRoutingInputRef.current = {
    useAdaptiveEdgeRouting: (
      nodes.length > VIEWPORT_CULLING_THRESHOLD
      && edgeRoutingNodes.length >= LARGE_MAP_ADAPTIVE_EDGE_ROUTING_MIN
    ),
    geometryKey: edgeRoutingGeometryKey,
    nodes: edgeRoutingNodes,
    edges: edgeRoutingRelationships,
    previousRoutes: [...routedVisibleEdgeResult.routes.entries()],
    affectedEdgeKeys: affectedEdgeKeys ? [...affectedEdgeKeys] : null,
    style: activeConnectionStyleId,
  };
  useEffect(() => {
    const pending = edgeRoutingWorkerRef.current;
    const routingInput = denseEdgeRoutingInputRef.current;
    if (pending.timerId !== null) window.clearTimeout(pending.timerId);
    pending.timerId = null;
    pending.worker?.terminate?.();
    pending.worker = null;
    pending.requestId += 1;
    const requestId = pending.requestId;

    if (!routingInput?.useAdaptiveEdgeRouting) {
      setDenseEdgeRouting((previous) => (
        previous.status === 'idle' && previous.geometryKey === null
          ? previous
          : { geometryKey: null, routes: null, status: 'idle', durationMs: null }
      ));
      return undefined;
    }

    setDenseEdgeRouting({
      geometryKey: null,
      routes: null,
      status: 'scheduled',
      durationMs: null,
    });
    pending.timerId = window.setTimeout(() => {
      pending.timerId = null;
      let worker;
      try {
        worker = createEdgeRoutingWorker();
      } catch {
        setDenseEdgeRouting({
          geometryKey: routingInput.geometryKey,
          routes: null,
          status: 'error',
          durationMs: null,
        });
        return;
      }
      pending.worker = worker;
      setDenseEdgeRouting((previous) => ({ ...previous, status: 'running' }));
      worker.onmessage = (event) => {
        if (edgeRoutingWorkerRef.current.requestId !== requestId) return;
        const message = event?.data;
        if (message?.requestId !== requestId || message.geometryKey !== routingInput.geometryKey) return;
        worker.terminate?.();
        if (edgeRoutingWorkerRef.current.worker === worker) {
          edgeRoutingWorkerRef.current.worker = null;
        }
        if (message.type === 'result') {
          largeMapHighWaterRef.current.denseEdgeRoutingDurationMs = Math.max(
            largeMapHighWaterRef.current.denseEdgeRoutingDurationMs,
            Number(message.durationMs) || 0
          );
          setDenseEdgeRouting({
            geometryKey: routingInput.geometryKey,
            routes: new Map(message.routes || []),
            status: 'completed',
            durationMs: largeMapHighWaterRef.current.denseEdgeRoutingDurationMs,
          });
        } else {
          setDenseEdgeRouting({
            geometryKey: routingInput.geometryKey,
            routes: null,
            status: 'error',
            durationMs: null,
          });
        }
      };
      worker.onerror = (error) => {
        if (edgeRoutingWorkerRef.current.requestId !== requestId) return;
        error?.preventDefault?.();
        worker.terminate?.();
        if (edgeRoutingWorkerRef.current.worker === worker) {
          edgeRoutingWorkerRef.current.worker = null;
        }
        setDenseEdgeRouting({
          geometryKey: routingInput.geometryKey,
          routes: null,
          status: 'error',
          durationMs: null,
        });
      };
      try {
        worker.postMessage({
          type: 'route-dense-edges',
          requestId,
          geometryKey: routingInput.geometryKey,
          nodes: routingInput.nodes,
          edges: routingInput.edges,
          previousRoutes: routingInput.previousRoutes,
          affectedEdgeKeys: routingInput.affectedEdgeKeys,
          style: routingInput.style,
        });
      } catch {
        worker.terminate?.();
        if (edgeRoutingWorkerRef.current.worker === worker) {
          edgeRoutingWorkerRef.current.worker = null;
        }
        setDenseEdgeRouting({
          geometryKey: routingInput.geometryKey,
          routes: null,
          status: 'error',
          durationMs: null,
        });
      }
    }, 80);

    return () => {
      if (pending.timerId !== null) window.clearTimeout(pending.timerId);
      pending.timerId = null;
      if (pending.worker) pending.worker.terminate?.();
      pending.worker = null;
    };
  }, [edgeRoutingGeometryKey]);
  largeMapHighWaterRef.current.mountedNodes = Math.max(
    largeMapHighWaterRef.current.mountedNodes,
    mountedNodeIds.size
  );
  largeMapHighWaterRef.current.mountedEdges = Math.max(
    largeMapHighWaterRef.current.mountedEdges,
    edgeRoutingRelationships.length
  );
  if (routedVisibleEdgeResult.durationMs > largeMapHighWaterRef.current.routeDurationMs) {
    largeMapHighWaterRef.current.routeDurationMs = routedVisibleEdgeResult.durationMs;
    largeMapHighWaterRef.current.maxRouteNodeCount = edgeRoutingNodes.length;
    largeMapHighWaterRef.current.maxRouteEdgeCount = edgeRoutingRelationships.length;
  }

  useEffect(() => {
    if (!isInitialized || mountedNodeIds.size === 0 || usablePaintAtMs !== null) return undefined;
    let secondFrameId = null;
    const firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        setUsablePaintAtMs(window.performance?.now?.() ?? Date.now());
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrameId);
      if (secondFrameId !== null) window.cancelAnimationFrame(secondFrameId);
    };
  }, [isInitialized, mountedNodeIds.size, usablePaintAtMs]);
  const routedVisibleEdges = (
    denseEdgeRouting.geometryKey === edgeRoutingGeometryKey
    && denseEdgeRouting.routes instanceof Map
  )
    ? denseEdgeRouting.routes
    : routedVisibleEdgeResult.routes;
  useEffect(() => {
    previousEdgeRoutesRef.current = routedVisibleEdges;
  }, [routedVisibleEdges]);

  return (
    <div
      data-layout-version={layoutVersion}
      style={{
        background: getThemeCanvasBackground(mapTheme, isDarkMode),
        width: '100%',
        position: 'absolute',
        height: '100%',
        left: 0,
        top: 0,
        overflow: 'clip',
        right: 0,
        bottom: 0,
        opacity: isInitialized ? 1 : 0,
        transition: 'opacity 150ms ease-out',

      }}
    >
      {/* Loading overlay shown until the mind map is initialized */}
      {!isInitialized && (
        <div className="mind-map-loading">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="mind-map-loading__spinner" />
            <div className="mind-map-loading__text">Loading your mind map</div>
          </div>
        </div>
      )}

      {/* Main container */}
      <div
        ref={containerRef}
        tabIndex={0}
        role="tree"
        aria-label="Mind map canvas"
        aria-activedescendant={selectedId ? getIdeaDomId(selectedId) : undefined}
        data-testid="mind-map-canvas"
        data-canvas-initialized={isInitialized ? 'true' : 'false'}
        data-canvas-initialized-at-ms={
          initializedAtRef.current === null
            ? undefined
            : initializedAtRef.current.toFixed(2)
        }
        data-usable-paint-at-ms={
          usablePaintAtMs === null ? undefined : usablePaintAtMs.toFixed(2)
        }
        data-total-node-count={nodes.length}
        data-total-relationship-count={relationships.length}
        data-branch-visible-node-count={renderedNodeIds.size}
        data-mounted-node-count={mountedNodeIds.size}
        data-mounted-edge-count={edgeRoutingRelationships.length}
        data-mounted-node-high-water={largeMapHighWaterRef.current.mountedNodes}
        data-mounted-edge-high-water={largeMapHighWaterRef.current.mountedEdges}
        data-viewport-culling={
          renderedNodeIds.size > VIEWPORT_CULLING_THRESHOLD ? 'active' : 'inactive'
        }
        data-semantic-zoom={semanticZoomActive ? 'simplified' : 'full'}
        data-last-route-duration-ms={routedVisibleEdgeResult.durationMs.toFixed(2)}
        data-max-route-duration-ms={largeMapHighWaterRef.current.routeDurationMs.toFixed(2)}
        data-max-route-node-count={largeMapHighWaterRef.current.maxRouteNodeCount}
        data-max-route-edge-count={largeMapHighWaterRef.current.maxRouteEdgeCount}
        data-dense-edge-routing-status={denseEdgeRouting.status}
        data-dense-edge-routing-duration-ms={
          denseEdgeRouting.durationMs === null
            ? undefined
            : denseEdgeRouting.durationMs.toFixed(2)
        }
        data-release-settlement-status={releaseSettlementStatus}
        data-release-settlement-source={releaseSettlementSource}
        data-release-request-id={releaseSettlementMetrics.requestId}
        data-release-completed-request-id={releaseSettlementMetrics.completedRequestId}
        data-release-feedback-ms={
          releaseSettlementMetrics.feedbackMs === null
            ? undefined
            : releaseSettlementMetrics.feedbackMs.toFixed(2)
        }
        data-release-settlement-duration-ms={
          releaseSettlementMetrics.durationMs === null
            ? undefined
            : releaseSettlementMetrics.durationMs.toFixed(2)
        }
        data-yjs-projection-mode={yState?.projectionMetrics?.mode || 'none'}
        data-yjs-projection-duration-ms={
          Number(yState?.projectionMetrics?.durationMs || 0).toFixed(2)
        }
        data-yjs-update-bytes={yState?.projectionMetrics?.updateBytes || 0}
        data-yjs-initial-sync-status={
          yInitialSyncMetrics?.completed
            ? 'completed'
            : (isLoggedIn && accessRole ? 'pending' : 'none')
        }
        data-yjs-initial-sync-duration-ms={
          yInitialSyncMetrics?.durationMs === undefined
            ? undefined
            : yInitialSyncMetrics.durationMs.toFixed(2)
        }
        data-yjs-initial-sync-network-received-bytes={
          yInitialSyncMetrics?.networkReceivedBytes
        }
        data-yjs-initial-sync-network-update-bytes={
          yInitialSyncMetrics?.networkAppliedUpdateBytes
        }
        data-yjs-initial-sync-local-update-bytes={
          yInitialSyncMetrics?.localAppliedUpdateBytes
        }
        data-yjs-initial-sync-projection-total-ms={
          yInitialSyncMetrics?.projectionTotalDurationMs === undefined
            ? undefined
            : yInitialSyncMetrics.projectionTotalDurationMs.toFixed(2)
        }
        data-yjs-initial-sync-projection-max-ms={
          yInitialSyncMetrics?.projectionMaxDurationMs === undefined
            ? undefined
            : yInitialSyncMetrics.projectionMaxDurationMs.toFixed(2)
        }
        data-yjs-initial-sync-projection-flushes={
          yInitialSyncMetrics?.projectionFlushCount
        }
        data-yjs-initial-sync-node-count={yInitialSyncMetrics?.nodeCount}
        data-yjs-initial-sync-relationship-count={
          yInitialSyncMetrics?.relationshipCount
        }
        data-map-access-role={accessRole || (isLoggedIn ? 'pending' : 'guest')}
        data-collaboration-status={
          !isLoggedIn
            ? 'guest'
            : (
              yIsNetworkSynced && yConnectionStatus === 'connected'
                ? 'synced'
                : yConnectionStatus
            )
        }
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onPointerDownCapture={handlePointerDownCapture}
        onDoubleClickCapture={handleDoubleClickCapture}
        onPointerMove={navHandlePointerMove}
        onPointerUpCapture={handlePointerUpCapture}
        onPointerUp={handlePointerUp}
        onPointerCancelCapture={handlePointerCancelCapture}
        onPointerCancel={handlePointerCancel}
        onWheelCapture={cancelViewportPanAnimation}
        onClick={handleContainerClick}
        onDoubleClick={handleDoubleClick}
        onDragOver={handleAttachmentDragOver}
        onDrop={handleAttachmentDrop}
        onKeyDown={handleKeyDown}
        style={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          overflow: 'clip',
          outline: 'none',
          touchAction: 'none'
        }}
      >
        {!nodeBranchModeration.isReady && (
          <div
            role="status"
            data-testid="branch-moderation-loading"
            data-export-ignore="true"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 130,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              background: getThemeCanvasBackground(mapTheme, isDarkMode),
              color: getThemeCanvasForeground(mapTheme, isDarkMode),
              textAlign: 'center',
            }}
          >
            <div>
              <div>
                {nodeBranchModeration.error
                  ? 'This map\u2019s visibility could not be loaded. Check your connection.'
                  : 'Loading map visibility\u2026'}
              </div>
              {nodeBranchModeration.error && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    void nodeBranchModeration.refresh();
                  }}
                  style={{ marginTop: 12, padding: '7px 12px', cursor: 'pointer' }}
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        )}
        <FocusNavigation
          path={focusPath}
          isDarkMode={isDarkMode}
          topOffset={!isDistractionFree || areFullscreenMenusVisible ? 60 : 16}
          canGoBack={focusHistory.index > 0}
          canGoForward={focusHistory.index < focusHistory.entries.length - 1}
          onBack={() => navigateFocusHistory(-1)}
          onForward={() => navigateFocusHistory(1)}
          onNavigate={(nodeId) => updateFocusView(nodeId)}
        />

        {/* Scaled content container (export target: nodes + lines only) */}
        <div
          style={{
              transform: `translate(${offset.x}px, ${offset.y}px)`,
              width: `${canvasWidth}px`,
              height: `${canvasHeight}px`,
              top: 0,
              left: 0,
              position: 'relative',
          }}
        >
            <div
              ref={captureRef}
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
                width: `${100 / scale}%`,
                height: `${100 / scale}%`,
                position: 'relative',
              }}
            >
          {/* SVG for connections */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 0,
              overflow: 'visible'
            }}
            overflow="visible"
          >
            <g>
              {edgeRoutingRelationships.map(({ parentId, childId }) => {
                const parentNode = renderIndex.nodesById.get(parentId);
                const childNode = renderIndex.nodesById.get(childId);

                if (!parentNode || !childNode) return null;

                const resolvedRoute = routedVisibleEdges.get(`${parentId}::${childId}`);
                const parentRect = resolvedRoute?.sourceRect;
                const childRect = resolvedRoute?.targetRect;
                const isContextEdge = (
                  contextNodeIds.has(parentId) || contextNodeIds.has(childId)
                );

                return (
                  <Line
                    key={`${parentId}-${childId}`}
                    fromRect={parentRect}
                    toRect={childRect}
                    resolvedRoute={resolvedRoute}
                    opacity={Math.min(
                      isContextEdge ? 0.12 : 1,
                      moderationProjection.fadedNodeIds.has(parentId)
                        || moderationProjection.fadedNodeIds.has(childId)
                        ? 0.2
                        : 1
                    )}
                    theme={mapTheme}
                    isDarkMode={isDarkMode}
                    isSelected={selectedId === parentId || selectedId === childId}
                    isMuted={Boolean(selectedId && selectedId !== parentId && selectedId !== childId)}
                    fromId={parentId}
                    toId={childId}
                    isLayoutAnimating={
                      layoutTransitionNodeIds.has(parentId)
                      || layoutTransitionNodeIds.has(childId)
                    }
                  />
                );
              })}
            </g>
          </svg>

          {/* Nodes */}
          {nodes.map(node => {
            if (!mountedNodeIds.has(node.id)) return null;

            const isContextOnly = contextNodeIds.has(node.id);
            const isHighlighted = highlightedNodeId === node.id;
            const safePosition = getRenderedNodePosition(node, dragPositionPreview);
            const semanticZoomSize = measuredNodeSizesRef.current.get(node.id)
              || node.layoutSize
              || { width: NODE_WIDTH_GUESS, height: NODE_HEIGHT_GUESS };

            // Check if this node has visible children
            // Get the current children's positions for smart button placement.
            // The shared index keeps this linear across the whole render.
            const currentChildren = (
              renderIndex.childrenByParentId.get(node.id) || EMPTY_NODE_LIST
            ).filter((child) => visibleNodeIds.has(child.id));
            const hasVisibleChildren = currentChildren.length > 0;
            const canCompleteTask = canCompleteAssignedTask({
              node,
              canWriteMap,
              currentUserId,
              allowUnattributed: !isLoggedIn,
            });

            let collapsedChildren = null;
            if (!isContextOnly && collapsedNodeIds.has(node.id)) {
              const branchIds = getBranchNodeIds(node.id, relationships);
              const hiddenNodes = nodes.filter((candidate) => (
                candidate.id !== node.id
                && branchIds.has(candidate.id)
                && !visibleNodeIds.has(candidate.id)
                && !moderationExcludedNodeIds.has(candidate.id)
              ));
              collapsedChildren = { nodes: hiddenNodes, count: hiddenNodes.length };
            }

            return (
              <Node
                key={node.id}
                id={node.id}
                ref={el => nodeRefs.current[node.id] = el}
                initialText={node.text}
                initialX={safePosition.x}
                initialY={safePosition.y}
                userId={node.userId}
                creatorName={node.creatorName}
                currentUserId={currentUserId}
                onTextChange={text => {
                  if (isLoggedIn && yActions && yActions.updateNode) {
                    yActions.updateNode(node.id, { text, lastEditedBy: currentUserId });
                  } else {
                    updateNodeText(node.id, text);
                  }
                }}
                onPositionChange={pos => handleNodePositionChange(node.id, pos)}
                screenToCanvasPosition={screenPointToCanvas}
                focusNodeIfOutOfView={focusNodeIfOutOfView}
                isSelected={selectedId === node.id}
                onSelect={handleNodeSelect}
                x={safePosition.x}
                y={safePosition.y}
                animate={node.animate}
                opacity={Math.min(
                  isContextOnly ? 0.16 : 1,
                  moderationProjection.fadedNodeIds.has(node.id) ? 0.24 : 1
                )}
                isContextOnly={isContextOnly}
                onKeyDown={handleKeyDown}
                color={node.color}
                theme={mapTheme}
                isRoot={node.isRoot}
                isTask={!!node.isTask}
                taskDone={!!node.taskDone}
                responsiblePrincipal={node.responsiblePrincipal || null}
                canCompleteTask={canCompleteTask}
                linkUrl={node.linkUrl}
                linkMeta={node.linkMeta}
                attachment={node.attachment}
                mindmapId={mindmapIdForYjs}
                onOpenAttachment={(attachment) => handleOpenAttachment(attachment, node.id)}
                onDownloadAttachment={handleDownloadAttachment}
                emoji={node.emoji}
                embeddedMindmapId={node.embeddedMindmapId}
                isEmbeddedMindmapLink={!!node.isEmbeddedMindmapLink}
                canEdit={
                  canWriteMap
                  && !(node.userId && currentUserId && node.userId !== currentUserId)
                }
                canMove={
                  canWriteMap
                  && (
                    accessRole === 'owner'
                    || !(node.userId && currentUserId && node.userId !== currentUserId)
                  )
                }
                moderationNotice={
                  moderationProjection.warningRootIds.has(node.id)
                    ? accessRole === 'owner'
                      ? 'This branch is hidden by you. Only you can restore it.'
                      : 'This branch was hidden by the map owner. Only you and the owner can see it.'
                    : null
                }
                discussionSummary={nodeDiscussions.summaries[node.id] || null}
                onOpenDiscussion={
                  canUseNodeDiscussions ? openNodeDiscussion : undefined
                }
                info={node.info}
                onOpenInfo={openNodeInfo}
                onDisallowedEdit={() => {
                  showNotification(
                    canWriteMap
                      ? 'You can only edit ideas you created.'
                      : 'This map is view only.',
                    'error'
                  );
                }}
                lockPositionToState={
                  (node.id === root && !focusRootId)
                  || dragPositionPreview.has(node.id)
                }
                isPositionAnimating={layoutTransitionNodeIds.has(node.id)}
                onMapLinkTitleCommit={node.embeddedMindmapId ? (newTitle) => handleMapLinkTitleCommit(node.embeddedMindmapId, newTitle) : undefined}
                onDragComplete={handleNodeDragComplete}
                editComplete={handleNodeEditComplete}
                onDragStart={(pointer) => handleNodeDragStart(node.id, pointer)}
                onDragMove={(pos, pointer) => handleNodeDragMove(node.id, pos, pointer)}
                onDragEnd={handleNodeDragEnd}
                isHighlighted={isHighlighted}
                semanticZoom={
                  semanticZoomActive && !fullDetailNodeIds.has(node.id)
                }
                semanticZoomSize={semanticZoomSize}
                collapsedChildren={collapsedChildren}
                hasChildren={hasVisibleChildren}
                currentChildren={currentChildren}
                onEditStart={() => {
                  setEditingNodeId(node.id);
                  saveCurrentState();
                }}
                onExpandNode={(nodeId) => {
                  expandBranch(nodeId);
                }}
                onCollapseNode={(nodeId) => {
                  collapseBranch(nodeId);
                }}
                getChildrenPositions={getChildrenPositions}
                getParentNodeData={getParentNodeData}
                onSizeChange={handleNodeSizeChange}
                onToggleTaskDone={() => {
                  if (!canCompleteTask) {
                    showNotification(
                      'Only the assignee or idea creator can complete this task.',
                      'error'
                    );
                    return;
                  }

                  saveCurrentState();
                  if (isLoggedIn && yActions?.completeTask) {
                    const currentlyDone = !!node.taskDone;
                    try {
                      yActions.completeTask(node.id, !currentlyDone, currentUserId);
                    } catch (error) {
                      showNotification(error?.message || 'Could not update this task.', 'error');
                    }
                  } else {
                    toggleTaskDone(node.id);
                    saveIfChanged();
                  }
                }}
                onClearLinkMeta={() => {
                  saveCurrentState();
                  setNodes(prev => prev.map(n => {
                    if (n.id !== node.id) return n;
                    const updated = { ...n };
                    delete updated.linkUrl;
                    delete updated.linkMeta;
                    return updated;
                  }));
                  saveIfChanged();
                }}
                onOpenPreview={async (nodeId, url) => {
                  try {
                    const meta = await fetchUrlPreview(url);
                    const cleanedTitle = meta && meta.title
                      ? decodeHtmlEntities(meta.title).trim()
                      : null;
                    const enrichedMeta = {
                      ...meta,
                      title: cleanedTitle || meta.title,
                    };
                    // Persist URL + meta; only overwrite text with
                    // the fetched title if the current text is still
                    // the raw URL (user hasn't customised it yet).
                    if (isLoggedIn && yActions && yActions.updateNode) {
                      const currentText = (node.text || '').trim();
                      const patch = {
                        linkUrl: url,
                        linkMeta: enrichedMeta,
                        lastEditedBy: currentUserId,
                      };
                      if (enrichedMeta.title && currentText === url) {
                        patch.text = enrichedMeta.title;
                      }
                      yActions.updateNode(nodeId, patch);
                    } else {
                      setNodes(prev => prev.map(n => {
                        if (n.id !== nodeId) return n;
                        const currentText = (n.text || '').trim();
                        if (enrichedMeta.title && currentText === url) {
                          return {
                            ...n,
                            linkUrl: url,
                            linkMeta: enrichedMeta,
                            text: enrichedMeta.title,
                          };
                        }
                        return {
                          ...n,
                          linkUrl: url,
                          linkMeta: enrichedMeta,
                        };
                      }));
                      saveIfChanged();
                    }
                  } catch (err) {
                    console.error('Failed to fetch URL metadata', err);
                  }
                }}
                onNavigateIdeaReference={(nodeId) => {
                  if (getNode(nodeId)) updateFocusView(nodeId);
                  else showNotification('That linked idea is no longer available.', 'error');
                }}
                onContextMenu={handleNodeContextMenu}
              />
            );
          })}
          </div>
        </div>

        <ReparentDropIndicator
          isVisible={reparentCandidate.visible}
          draggedNode={reparentCandidate.draggedNode}
          targetNode={reparentCandidate.targetNode}
          position={reparentViewportPosition}
        />

        <IdeaInbox
          mode={ideaInboxMode}
          isMobile={isMobileViewport}
          mobileView={mobileWorkspaceView}
          isDarkMode={isDarkMode}
          inbox={ideaInbox}
          captureTarget={captureTarget}
          canWrite={canWriteMap}
          isDropActive={isInboxDropActive}
          onModeChange={updateIdeaInboxMode}
          onMobileViewChange={setMobileWorkspaceView}
          onAddIdea={handleCaptureIdea}
          onDeleteIdea={handleDeleteInboxIdea}
          onInboxDragStart={(nodeId) => {
            inboxHtmlDragNodeIdRef.current = nodeId;
            inboxHtmlDragPointRef.current = null;
          }}
          onInboxDragEnd={() => {
            inboxHtmlDragNodeIdRef.current = null;
            inboxHtmlDragPointRef.current = null;
          }}
        />

        {renderedCapsuleNode && renderedCapsuleAnchor && (
          <NodeActionCapsule
            nodeId={renderedCapsuleNode.id}
            nodeLabel={renderedCapsuleNode.text}
            anchor={renderedCapsuleAnchor}
            container={containerRef.current}
            isDarkMode={isDarkMode}
            isVisible={isRenderedCapsuleVisible}
            positioningKey={capsulePositioningKey}
            canWrite={canWriteMap}
            canChangeColor={canWriteMap && !renderedCapsuleNodeIsForeign}
            onAddChild={handleCapsuleAddChild}
            onChangeColor={handleCapsuleChangeColor}
            onFocusBranch={focusBranchInCurrentView}
            onOpenMenu={handleCapsuleOpenMenu}
            onReturnFocus={returnFocusToCanvas}
            getUsableViewportGeometry={readCreationViewportGeometry}
          />
        )}

        {/* Color picker overlay */}
        {showPicker && (
          <div
            style={{
              position: 'absolute',
              left: `${colorPos.x}px`,
              top: `${colorPos.y}px`,
              zIndex: 100
            }}
            data-export-ignore="true"
          >
            <ColorPickerWidget
              x={0}
              y={0}
              colors={mapTheme.palette}
              onColorSelect={handleColorSelect}
            />
          </div>
        )}

        {/* Emoji picker overlay */}
        {showEmojiPicker && (
          <div
            style={{
              position: 'absolute',
              left: `${emojiPos.x}px`,
              top: `${emojiPos.y}px`,
              zIndex: 110,
            }}
            data-export-ignore="true"
          >
            <EmojiPickerWidget
              x={0}
              y={0}
              onEmojiSelect={handleEmojiSelect}
            />
          </div>
        )}

        {/* Node context menu */}
        {contextMenu.visible && (
          <div
            data-testid="node-context-menu"
            style={{
              position: 'absolute',
              left: `${contextMenuPosition.x}px`,
              top: `${contextMenuPosition.y}px`,
              zIndex: 120,
              maxHeight: `${Math.max(
                80,
                Math.min(664, contextMenuViewportSize.height - 16)
              )}px`,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
            data-export-ignore="true"
          >
            {(() => {
              const node = nodes.find(n => n.id === contextMenu.nodeId);
              const isTask = !!(node && node.isTask);
              const moderatedRoot = nodeBranchModeration.branchByNodeId.has(node?.id);
              const insideModeratedBranch = Boolean(
                node && moderationProjection.fadedNodeIds.has(node.id)
              );
              const canModerate = Boolean(
                accessRole === 'owner'
                && node
                && (
                  moderatedRoot
                  || (
                    !insideModeratedBranch
                    && !node.isRoot
                    && node.id !== root
                    && node.userId
                    && node.userId !== currentUserId
                  )
                )
              );
              return (
                <NodeContextMenu
                  isDarkMode={isDarkMode}
                  onAddChild={handleContextAddChild}
                  onAddSibling={handleContextAddSibling}
                  onAddTaskChild={handleContextAddTaskChild}
                  onDelete={handleContextDeleteNode}
                  onChangeColor={handleContextChangeColor}
                  onChangeEmoji={handleContextChangeEmoji}
                  onFocusBranch={handleContextFocusBranch}
                  onShareIdea={handleContextShareIdea}
                  onOpenBranch={handleContextOpenBranch}
                  onCopyText={handleContextCopyText}
                  onCopyLink={handleContextCopyLink}
                  isTask={isTask}
                  onToggleTask={handleContextToggleTask}
                  onCreateMindmapLink={handleContextCreateMindmapLink}
                  onCreateMarkdown={handleContextCreateMarkdown}
                  onConvertBranchToMarkdown={handleContextConvertBranchToMarkdown}
                  canConvertBranchToMarkdown={!!node && !['image', 'markdown'].includes(node.attachment?.kind)}
                  canCreateMindmapLink={!!currentUserId}
                  canAddSibling={!!node && !node.isRoot}
                  canWrite={canWriteMap}
                  canDiscuss={canUseNodeDiscussions}
                  onOpenDiscussion={handleContextOpenDiscussion}
                  canOpenInfo
                  onOpenInfo={handleContextOpenInfo}
                  canAssignTask={accessRole === 'owner' && isTask && !insideModeratedBranch}
                  taskAssignees={taskAssignees}
                  assignedPrincipal={node?.responsiblePrincipal || null}
                  assignmentLoading={taskAssigneesLoading}
                  assignmentError={taskAssigneesError}
                  onAssignTask={handleContextAssignTask}
                  canModerate={canModerate}
                  isModerated={moderatedRoot}
                  onHideBranch={handleContextHideBranch}
                  onRestoreBranch={handleContextRestoreBranch}
                />
              );
            })()}
          </div>
        )}

        {canUseNodeDiscussions && activeDiscussionNode && (
          <NodeDiscussionPanel
            node={activeDiscussionNode}
            anchorRect={discussionAnchorRect}
            isDarkMode={isDarkMode}
            theme={mapTheme}
            currentUserId={currentUserId}
            thread={nodeDiscussions.thread}
            isLoading={nodeDiscussions.isLoadingThread}
            isLoadingOlder={nodeDiscussions.isLoadingOlder}
            isSending={nodeDiscussions.isSending}
            error={nodeDiscussions.error}
            onClose={nodeDiscussions.closeDiscussion}
            onLoadOlder={nodeDiscussions.loadOlder}
            onSend={nodeDiscussions.sendMessage}
            onRefresh={nodeDiscussions.refreshActiveDiscussion}
          />
        )}

        {activeInfoNode && (
          <NodeInfoPanel
            node={activeInfoNode}
            anchorRect={infoAnchorRect}
            isDarkMode={isDarkMode}
            theme={mapTheme}
            canEdit={
              canWriteMap
              && (activeInfoNode.id !== root || canManageMapMetadata)
              && !(
                activeInfoNode.userId
                && currentUserId
                && activeInfoNode.userId !== currentUserId
              )
            }
            onClose={() => setActiveInfoNodeId(null)}
            onSave={saveNodeInfo}
          />
        )}

        {attachmentViewer && (
          <AttachmentViewerModal
            attachment={attachmentViewer.attachment}
            content={attachmentViewer.content}
            objectUrl={attachmentViewer.objectUrl}
            isLoading={attachmentViewer.isLoading}
            error={attachmentViewer.error}
            isDarkMode={isDarkMode}
            theme={mapTheme}
            onClose={closeAttachmentViewer}
            onDownload={() => handleDownloadAttachment(attachmentViewer.attachment)}
            canEdit={canEditOpenMarkdownAttachment}
            onSave={handleSaveMarkdownAttachment}
          />
        )}

        {branchMarkdownRequest && (
          <BranchMarkdownDialog
            plan={branchMarkdownRequest.plan}
            isDarkMode={isDarkMode}
            isWorking={branchMarkdownRequest.isWorking}
            onCancel={closeBranchMarkdownDialog}
            onKeep={() => executeBranchMarkdownChoice('keep')}
            onReplace={() => executeBranchMarkdownChoice('replace')}
          />
        )}

        <div
          className="left-menu-scroll"
          data-viewport-occlusion="true"
          aria-hidden={isDistractionFree && !areFullscreenMenusVisible}
          style={{
            marginBottom: '10px',
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            position: 'fixed',
            top: 60,
            left: 10,
            zIndex: 10,
            maxHeight: 'calc(100vh - 80px)',
            overflowY: 'auto',
            opacity: !isDistractionFree || areFullscreenMenusVisible ? 1 : 0,
            transform: !isDistractionFree || areFullscreenMenusVisible
              ? 'translateX(0)'
              : 'translateX(-14px)',
            pointerEvents: !isDistractionFree || areFullscreenMenusVisible ? 'auto' : 'none',
            visibility: !isDistractionFree || areFullscreenMenusVisible ? 'visible' : 'hidden',
            transition: !isDistractionFree || areFullscreenMenusVisible
              ? 'opacity 220ms ease-out, transform 220ms ease-out, visibility 0s linear'
              : 'opacity 180ms ease-in, transform 180ms ease-in, visibility 0s linear 180ms',
          }}
          data-export-ignore="true"
        >
              <Menu handleAddChildNode={handleAddChildNode}
                saveMindMap={saveMindMap}
                loadMindMap={loadMindMap}
                handleRemoveNode={handleRemoveNode}
                handleUndo={handleUndo}
                handleRedo={handleRedo}
                setShowPicker={setShowPicker}
                showPicker={showPicker}
                isDarkMode={isDarkMode}
                canUndo={canUndo}
                canRedo={canRedo}
                showHome={Boolean(focusRootId && focusRootId !== root)}
                onHome={handleHome}
                onExport={handleExportImage}
                onExportData={isLoggedIn ? handleExportData : undefined}
                onSaveThumbnail={isLoggedIn && canManageMapMetadata && mindmapIdForYjs ? handleSaveThumbnail : undefined}
                onShare={handleShare}
                canWrite={canWriteMap}
              />
      </div>

      {isLoggedIn && accessRole === 'viewer' && (
        <div
          className="mind-map-read-only"
          role="status"
          aria-label="View-only map"
          data-export-ignore="true"
          style={{
            position: 'absolute',
            top: 14,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 130,
            padding: '7px 12px',
            borderRadius: 999,
            background: isDarkMode ? 'rgba(31, 41, 55, 0.92)' : 'rgba(255, 255, 255, 0.94)',
            color: isDarkMode ? '#f9fafb' : '#111827',
            border: `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
            fontSize: 13,
            fontWeight: 600,
            pointerEvents: 'none',
          }}
        >
          View only
        </div>
      )}

      <ShareDialog
        isOpen={isShareOpen}
        onClose={() => {
          setIsShareOpen(false);
          setShareNodeId(null);
        }}
        mindmapId={mindmapIdForYjs}
        focusNodeId={shareNodeId}
        focusNodeLabel={shareNodeId
          ? getNodeSearchLabel(nodes.find((node) => node.id === shareNodeId))
          : null}
        canManage={canManageMapMetadata}
        isDarkMode={isDarkMode}
        showNotification={showNotification}
      />

      <UpgradeDialog
        isOpen={Boolean(mapCreationUpgradeRequest)}
        capabilityKey={mapCreationUpgradeRequest?.capabilityKey}
        returnLabel="Create linked map"
        isDarkMode={isDarkMode}
        onClose={() => setMapCreationUpgradeRequest(null)}
        onActivated={async () => {
          const parentId = mapCreationUpgradeRequest?.parentId;
          setMapCreationUpgradeRequest(null);
          if (parentId) await handleContextCreateMindmapLink(parentId);
        }}
      />

      <OfflineRecoveryDialog
        record={yRecoveryNotice}
        onDismiss={clearYRecoveryNotice}
        onReload={async () => {
          const ready = await prepareYRecoveryReload();
          if (!ready) return false;
          window.location.reload();
          return true;
        }}
      />

      {isLoggedIn
        && yIsLocallySynced
        && (!yIsNetworkSynced || yConnectionStatus !== 'connected')
        && (
          <div
            className="mind-map-offline-status"
            role="status"
            aria-label="Offline map"
            data-export-ignore="true"
          >
            {remoteMapData?.offlineCache
              ? 'Recovered from this device · waiting to sync'
              : 'Offline · changes are saved on this device'}
          </div>
        )}

      {(pendingAttachmentUploads.length > 0 || notifications.length > 0) && (
        <div className="mind-map-notifications" data-export-ignore="true">
          <AttachmentUploadStatus uploads={pendingAttachmentUploads} />
          {notifications.map((n) => (
            <div
              key={n.id}
              className={`mind-map-notification mind-map-notification--${n.variant}`}
            >
              {n.message}
            </div>
          ))}
        </div>
      )}

        {/* Zoom controls - moved to bottom-right to avoid overlapping the top menu */}
        <div
          className="zoom-controls"
          data-viewport-occlusion="true"
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            left: 'auto',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            maxWidth: 'calc(100vw - 40px)'
          }}
          data-export-ignore="true"
          onMouseDown={(event) => event.stopPropagation()}
          onMouseUp={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          {isDistractionFree && (
            <button
              onClick={() => setAreFullscreenMenusVisible((visible) => !visible)}
              style={{ ...buttonStyle, width: 30, height: 30 }}
              title={areFullscreenMenusVisible ? 'Hide menus' : 'Show menus'}
              aria-label={areFullscreenMenusVisible ? 'Hide menus' : 'Show menus'}
              aria-pressed={areFullscreenMenusVisible}
            >
              <svg
                aria-hidden="true"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          )}
          <button
            onClick={isDistractionFree ? exitDistractionFree : enterDistractionFree}
            style={{ ...buttonStyle, width: 30, height: 30 }}
            title={isDistractionFree ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-label={isDistractionFree ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {isDistractionFree
                ? <path d="M3 8h5V3M21 8h-5V3M16 21v-5h5M8 21v-5H3" />
                : <path d="M8 3H3v5M16 3h5v5M21 16v5h-5M8 21H3v-5" />}
            </svg>
          </button>
          <button
            onClick={handleFitVisible}
            style={buttonStyle}
            title="Fit visible map (Shift+1)"
            aria-label="Fit visible map"
          >
            ◎
          </button>
          <button
            onClick={handleZoomOut}
            style={{ ...buttonStyle, width: 30, height: 30, lineHeight: 1 }}
            aria-label="Zoom out"
          >
            −
          </button>
          <div
            data-testid="zoom-level"
            style={{
              color: getThemeCanvasForeground(mapTheme, isDarkMode),
              fontSize: 14,
              minWidth: 44,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              margin: 2,
              cursor: 'pointer'
            }}
            onClick={handleResetZoom}
          >
            {parseInt(scale * 100)}%
          </div>
          <button
            onClick={handleZoomIn}
            style={{ ...buttonStyle, width: 30, height: 30, lineHeight: 1 }}
            aria-label="Zoom in"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default NodesContainer;
